import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, UserCheck, MailWarning, ClipboardList, Plus, Power, CheckCircle2, FolderOpen, RefreshCw, KeyRound, Trash2, Clock, UserRound, School, Building2, ChevronRight, ChevronLeft, ArrowLeft, FileCheck, CalendarDays, AlertTriangle, Hourglass, XCircle, Ban, RotateCcw, ClipboardPlus, Download, ShieldCheck, MapPin, Radio, BookOpen, Pencil, Check, X, Send, FileSpreadsheet, FileText, Stethoscope, Network, FileSearch, CalendarClock, Megaphone, PenSquare, Inbox, Puzzle, ListChecks, ArrowRightLeft, ChevronDown, MessageSquare, BellRing, Star, CircleDot, Circle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { AnagraficaAdmin, MultiFilter } from "@/components/office/AnagraficaAdmin";
import { EntryRoleSiteCorrection, buildCorrectionPayload, correctionIncomplete } from "@/components/office/EntryRoleSiteCorrection";
import { BlueBoxSites } from "@/components/office/BlueBoxSites";
import { RelationsAdmin } from "@/components/office/RelationsAdmin";
import { ExternalCheckArchive } from "@/components/office/ExternalCheckArchive";
import { AgendeSettimanaliSection } from "@/components/office/AgendeSettimanali";
import { MailViewSection, MailViewNotifications } from "@/components/office/MailView";
import { PresenzeUtentiExport } from "@/components/office/PresenzeUtentiExport";
import { MultiWorkerImport } from "@/components/office/MultiWorkerImport";
import { MultiUserImport } from "@/components/office/MultiUserImport";
import { MultiSchoolImport } from "@/components/office/MultiSchoolImport";
import { MultiSiteImport } from "@/components/office/MultiSiteImport";
import { IntroCover } from "@/components/IntroCover";
import { PreviewDesktopToggle } from "@/components/PreviewDesktopToggle";
import { loadFavorites, getFavoritesCache, isFav, toggleFavorite, FAVORITES_EVENT, FAV_NAV_EVENT, consumePendingNav } from "@/lib/favorites";
import { isFavoritable } from "@/lib/officeFavorites";
import { isDesktopExperience, PREVIEW_DESKTOP_EVENT } from "@/lib/pwa";
import { OrbitFlow } from "@/components/OrbitFlow";
import { peekIntro, clearIntro } from "@/lib/introFlag";
import { welcomeToast } from "@/lib/welcome";
import { SearchBox } from "@/components/SearchBox";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LibroNotes } from "@/components/LibroNotes";
import { GeoAnomalyNote } from "@/components/GeoAnomalyNote";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { TimePicker } from "@/components/TimePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SERVICES, slugFromKey, ENTITY_KINDS, effEntityKindKey, isCasaAsa } from "@/lib/services";
import { colorFor } from "@/components/OrbitChart";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
          <Icon className="h-4 w-4" /> {label}
        </div>
        <p className="font-heading text-3xl font-bold text-primary mt-2" data-testid={testid}>{value}</p>
      </CardContent>
    </Card>
  );
}

const emptyWorker = { nome: "", cognome: "", ruolo_professionale: "", email: "", profilo_accesso: "LAVORATORE", monte_ore_settimanale_contrattuale: "", appalto_ids: [] };
const CATEGORIE = ["Comunale", "Scuole Superiori", "Esterno", "Privato"];
const SERVICE_LABELS = {
  assistenza_scolastica: "Assistenza scolastica", pre_scuola: "Pre-scuola", post_scuola: "Post-scuola",
  centro_estivo: "Centro estivo", adm: "ADM", spazio_compiti: "Spazio Compiti", blue_box: "Blue Box", nido: "Nido",
  rest: "ReST", casa_di_riposo: "Casa di riposo", assistenza_scuolabus: "Assistenza Scuolabus", assistenza_mensa: "Assistenza Mensa", centro_aggregativo: "Centro aggregativo",
};
const svcLabel = (s) => SERVICE_LABELS[s] || s;

const parseHours = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

function LavoratoriTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [appalti, setAppalti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyWorker);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [emailBusy, setEmailBusy] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [appaltoFilter, setAppaltoFilter] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedWorkers, setDeletedWorkers] = useState([]);
  const [multiOpen, setMultiOpen] = useState(false);

  const load = async () => {
    const [s, w, c, a, d] = await Promise.all([api.get("/stats"), api.get("/workers"), api.get("/office/email-config"), api.get("/my-appalti"), api.get("/workers?deleted=true")]);
    setStats(s.data); setWorkers(w.data); setEmailConfigured(c.data.configured); setAppalti(a.data); setDeletedWorkers(d.data);
    setForm((f) => (a.data.length === 1 && f.appalto_ids.length === 0 ? { ...f, appalto_ids: [a.data[0].id] } : f));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createWorker = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const { data } = await api.post("/workers", { ...form, monte_ore_settimanale_contrattuale: parseHours(form.monte_ore_settimanale_contrattuale) });
      setEmailStatus(data.email_status);
      setForm(emptyWorker); await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const deleteWorker = async () => {
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/workers/${deleting.id}`);
      toast.success(data.message || "Operazione completata");
      setDeleting(null); await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile eliminare il lavoratore");
    } finally { setDeleteBusy(false); }
  };

  const restoreWorker = async (w) => {
    try {
      const { data } = await api.post(`/workers/${w.id}/restore`);
      toast.success(data.message || "Account riattivato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile riattivare l'account");
    }
  };

  const sendEmail = async (w) => {
    const kind = w.attivato ? "reset" : "activation";
    setEmailBusy(w.id);
    try {
      if (kind === "reset") await api.post(`/workers/${w.id}/send-reset`);
      else await api.post(`/workers/${w.id}/resend-activation`);
      toast.success(kind === "reset" ? "E-mail di reimpostazione password inviata." : "E-mail di attivazione inviata.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setEmailBusy(null); }
  };

  const toggleStatus = async (id) => {
    try { await api.patch(`/workers/${id}/status`); await load(); toast.success("Stato aggiornato"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const closeDialog = () => { setOpen(false); setEmailStatus(null); setForm(emptyWorker); setError(""); };

  const statusBox = {
    sent: { cls: "text-success", icon: CheckCircle2, msg: "Account creato. L'invito di attivazione è stato inviato all'indirizzo indicato." },
    failed: { cls: "text-destructive", icon: MailWarning, msg: "Account creato, ma non è stato possibile inviare l'invito. Puoi riprovare con \"Reinvia attivazione\" dall'elenco." },
    not_configured: { cls: "text-destructive", icon: MailWarning, msg: "Account creato, ma il servizio e-mail non è configurato. Contatta l'amministratore, poi usa \"Reinvia attivazione\"." },
  };

  return (
    <div>
      {multiOpen ? (
        <MultiWorkerImport
          appalti={appalti}
          existingEmails={[...workers, ...deletedWorkers].map((w) => w.email)}
          onClose={() => { setMultiOpen(false); load(); }}
        />
      ) : (
      <>
      {!emailConfigured && (
        <div data-testid="email-config-banner" className="mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 flex items-center gap-2">
          <MailWarning className="h-4 w-4 shrink-0" /> Servizio e-mail non configurato: le e-mail di attivazione e reset non verranno inviate finché Resend non sarà configurato.
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-sm text-slate-500">Crea account e gestisci lo stato dei lavoratori</p>
        <div className="flex items-center gap-2 flex-wrap">
        <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
          <DialogTrigger asChild>
            <Button data-testid="open-create-worker-btn" className="rounded-lg font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Nuovo lavoratore
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Nuovo account lavoratore</DialogTitle>
              <DialogDescription>Crea un account e invia l'e-mail di attivazione.</DialogDescription>
            </DialogHeader>
            {emailStatus ? (
              <div data-testid="email-status-box" className="space-y-4">
                {(() => { const S = statusBox[emailStatus] || statusBox.failed; const I = S.icon; return (
                  <div className={`flex items-start gap-2 ${S.cls}`}><I className="h-5 w-5 shrink-0 mt-0.5" /><p className="font-medium text-sm">{S.msg}</p></div>
                ); })()}
                <DialogFooter><Button onClick={closeDialog} className="rounded-lg" data-testid="close-status-box">Chiudi</Button></DialogFooter>
              </div>
            ) : (
              <form onSubmit={createWorker} className="space-y-3">
                {error && <div data-testid="create-worker-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label htmlFor="nome">Nome</Label><Input id="nome" required value={form.nome} data-testid="worker-nome-input" onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label htmlFor="cognome">Cognome</Label><Input id="cognome" required value={form.cognome} data-testid="worker-cognome-input" onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label htmlFor="ruolo">Ruolo professionale</Label><Input id="ruolo" required value={form.ruolo_professionale} data-testid="worker-ruolo-input" placeholder="Es. Educatore" onChange={(e) => setForm({ ...form, ruolo_professionale: e.target.value })} /></div>
                <div className="space-y-1.5"><Label htmlFor="email">Email</Label><Input id="email" type="email" required value={form.email} data-testid="worker-email-input" placeholder="nome@orbit.it" onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Appalti associati</Label>
                  <div className="rounded-lg border border-slate-200 divide-y max-h-40 overflow-y-auto" data-testid="worker-appalti-list">
                    {appalti.length === 0 && <p className="text-sm text-slate-500 p-3">Nessun appalto attivo nel tuo perimetro.</p>}
                    {appalti.map((a) => {
                      const checked = form.appalto_ids.includes(a.id);
                      return (
                        <label key={a.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50">
                          <Checkbox checked={checked} data-testid={`worker-appalto-${a.id}`}
                            onCheckedChange={() => setForm({ ...form, appalto_ids: checked ? form.appalto_ids.filter((x) => x !== a.id) : [...form.appalto_ids, a.id] })} />
                          <span className="text-sm font-medium text-slate-700">{a.nome}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="monte-ore">Monte ore settimanale contrattuale</Label>
                  <Input id="monte-ore" inputMode="decimal" value={form.monte_ore_settimanale_contrattuale} data-testid="worker-monte-ore-input"
                    placeholder="Es. 38,5" onChange={(e) => setForm({ ...form, monte_ore_settimanale_contrattuale: e.target.value })} />
                </div>
                <DialogFooter><Button type="submit" disabled={saving} data-testid="submit-create-worker" className="rounded-lg font-semibold w-full">{saving ? "Creazione..." : "Crea e invia invito"}</Button></DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
        <Button variant="outline" data-testid="open-multi-worker-btn" onClick={() => setMultiOpen(true)}
          className="rounded-lg font-semibold bg-white text-slate-700 border-2 border-amber-500 hover:bg-amber-50 hover:text-slate-900 hover:border-amber-600">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Inserimento multiplo
        </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <StatCard icon={Users} label="Lavoratori" value={stats?.lavoratori_totali ?? "–"} testid="stat-total" />
        <StatCard icon={ClipboardList} label="Registrazioni mensili" value={stats?.registrazioni ?? "–"} testid="stat-entries" />
      </div>

      {loading ? <p className="text-sm text-slate-500">Caricamento…</p> : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <button data-testid="office-toggle-active" onClick={() => setShowDeleted(false)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${!showDeleted ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>Attivi</button>
            <button data-testid="office-toggle-deleted" onClick={() => setShowDeleted(true)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${showDeleted ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>Account eliminati{deletedWorkers.length ? ` (${deletedWorkers.length})` : ""}</button>
          </div>
          {showDeleted ? (
            <div className="space-y-3" data-testid="deleted-workers-list">
              <p className="text-sm text-slate-500">Account eliminati: non possono accedere e non compaiono nell'elenco operativo. I dati storici sono conservati per la rendicontazione.</p>
              {deletedWorkers.length === 0 ? (
                <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="deleted-workers-empty">Nessun account eliminato.</CardContent></Card>
              ) : deletedWorkers.map((w) => (
                <Card key={w.id} className="rounded-xl border-slate-200 shadow-sm opacity-90" data-testid={`deleted-worker-${w.id}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center font-heading font-bold shrink-0">{(w.nome[0] || "") + (w.cognome[0] || "")}</div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 truncate">{w.nome} {w.cognome}</p>
                      <p className="text-sm text-slate-500 truncate">{w.ruolo_professionale} · {w.email}</p>
                    </div>
                    <Badge className="bg-slate-200 text-slate-600 border-0 text-[10px] shrink-0" data-testid={`deleted-worker-badge-${w.id}`}>Eliminato</Badge>
                    <Button variant="outline" size="sm" className="rounded-lg text-success border-success/30 hover:bg-success/5 shrink-0"
                      data-testid={`office-restore-${w.id}`} onClick={() => restoreWorker(w)}>
                      <RotateCcw className="h-4 w-4 mr-1.5" />Riattiva
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
          <>
          <div data-testid="workers-filterbar" className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200/80 bg-slate-50/50 p-2">
            <div className="flex-1 min-w-[180px]">
              <SearchBox value={search} onChange={setSearch} placeholder="Cerca per nome o cognome…" testid="workers-search" />
            </div>
            <MultiFilter label="Appalto" options={appalti.map((a) => ({ value: a.id, label: a.nome }))}
              value={appaltoFilter} onChange={setAppaltoFilter} testid="workers-appalto-filter" />
          </div>
          {(() => {
            const q = search.trim().toLowerCase();
            const sel = appaltoFilter;
            if (sel.length === 0 && !q) {
              return <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="workers-prompt">Seleziona uno o più appalti, oppure digita un nome nel campo di ricerca, per visualizzare i lavoratori.</CardContent></Card>;
            }
            const base = sel.length > 0 ? workers.filter((w) => (w.appalto_ids || []).some((id) => sel.includes(id))) : workers;
            const filtered = q ? base.filter((w) => `${w.nome} ${w.cognome}`.toLowerCase().includes(q) || `${w.cognome} ${w.nome}`.toLowerCase().includes(q)) : base;
            if (filtered.length === 0) {
              return <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="workers-empty">Nessun lavoratore corrisponde ai criteri di ricerca.</CardContent></Card>;
            }
            return (
              <div className="space-y-3" data-testid="workers-list">
                {filtered.map((w, i) => (
            <motion.div key={w.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.04 }}>
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-heading font-bold shrink-0">{(w.nome[0] || "") + (w.cognome[0] || "")}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800 truncate">{w.nome} {w.cognome}</p>
                      <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">{w.profilo_accesso === "UFFICIO" ? "Ufficio" : "Lavoratore"}</Badge>
                      {!w.attivato ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[10px]">Da attivare</Badge>
                        : w.account_attivo ? <Badge className="bg-success/10 text-success hover:bg-success/10 border-0 text-[10px]">Attivo</Badge>
                        : <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10 border-0 text-[10px]">Disattivato</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 truncate">{w.ruolo_professionale} · {w.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    {!w.attivato ? (
                      <Button variant="outline" size="sm" onClick={() => sendEmail(w)} disabled={emailBusy === w.id} data-testid={`resend-activation-${w.id}`}
                        className="rounded-lg text-amber-700 border-amber-300 hover:bg-amber-50">
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${emailBusy === w.id ? "animate-spin" : ""}`} />Reinvia attivazione
                      </Button>
                    ) : w.account_attivo ? (
                      <Button variant="outline" size="sm" onClick={() => sendEmail(w)} disabled={emailBusy === w.id} data-testid={`send-reset-${w.id}`}
                        className="rounded-lg text-primary border-primary/30 hover:bg-primary/5">
                        <KeyRound className={`h-4 w-4 mr-1.5 ${emailBusy === w.id ? "animate-spin" : ""}`} />Reset password
                      </Button>
                    ) : null}
                    <Button variant="outline" size="sm" onClick={() => navigate(`/ufficio/lavoratore/${w.id}`)} data-testid={`open-folder-${w.id}`}
                      className="rounded-lg text-primary border-primary/30 hover:bg-primary/5">
                      <FolderOpen className="h-4 w-4 mr-1.5" />Apri
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(w.id)} data-testid={`toggle-status-${w.id}`}
                      className={`rounded-lg ${w.account_attivo ? "text-destructive border-destructive/30 hover:bg-destructive/5" : "text-success border-success/30 hover:bg-success/5"}`}>
                      <Power className="h-4 w-4 mr-1.5" />{w.account_attivo ? "Disattiva" : "Attiva"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleting(w)} data-testid={`delete-worker-${w.id}`}
                      disabled={w.id === user?.id}
                      className="rounded-lg text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40">
                      <Trash2 className="h-4 w-4 mr-1.5" />Elimina
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
                ))}
              </div>
            );
          })()}
          </>
          )}
        </>
      )}
      </>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="worker-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminare «{deleting ? `${deleting.nome} ${deleting.cognome}` : ""}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Il lavoratore verrà spostato in <b>«Account eliminati»</b>: non potrà più accedere né essere associato a nuove registrazioni e non comparirà nell'elenco operativo. I dati storici (registri, esportazioni, audit) restano conservati per la rendicontazione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="worker-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={deleteWorker} disabled={deleteBusy} data-testid="worker-delete-confirm"
              className="bg-red-600 hover:bg-red-700">{deleteBusy ? "Elaborazione…" : "Elimina"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CheckSubNav({ view, setView, testid }) {
  return (
    <div className="flex gap-2 mb-5" data-testid={`${testid}-subnav`}>
      <button data-testid={`${testid}-tab-nuovo`} onClick={() => setView("nuovo")}
        className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${view === "nuovo" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
        Nuovo check
      </button>
      <button data-testid={`${testid}-tab-archivio`} onClick={() => setView("archivio")}
        className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${view === "archivio" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
        Archivio
      </button>
    </div>
  );
}

function CheckAssistenzaScolasticaSection() {
  const [view, setView] = useState("nuovo");
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [schoolId, setSchoolId] = useState("all");
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [schools, setSchools] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  useEffect(() => {
    api.get("/my-appalti").then((r) => {
      const list = r.data || [];
      setAppalti(list);
      if (list.length === 1) setAppaltoId(list[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setSchoolId("all"); setSelectedSchools([]); setSchools([]); setData(null);
    if (!appaltoId) return;
    api.get(`/office/external-checks/school-assistance/schools?appalto_id=${appaltoId}`)
      .then((r) => setSchools(r.data || [])).catch(() => {});
  }, [appaltoId]);

  const toggleSchool = (id) => {
    setSelectedSchools((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const apply = async () => {
    if (!appaltoId) { toast.error("Seleziona l'appalto"); return; }
    if (!dateFrom || !dateTo) { toast.error("Indica il periodo Dal / Al"); return; }
    setLoading(true); setData(null);
    try {
      let url = `/office/external-checks/school-assistance?appalto_id=${appaltoId}&date_from=${dateFrom}&date_to=${dateTo}`;
      if (selectedSchools.length > 0) url += `&school_ids=${selectedSchools.join(",")}`;
      const res = await api.get(url);
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore nel recupero dati");
    } finally { setLoading(false); }
  };

  const downloadSchool = async (school) => {
    setDownloading(school.school_id);
    try {
      const url = `/office/external-checks/school-assistance/export?appalto_id=${appaltoId}&date_from=${dateFrom}&date_to=${dateTo}&school_id=${school.school_id}`;
      const res = await api.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `AssistenzaScolastica_${school.school_name}.xlsx`;
      const dl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = dl; a.download = fname; a.click();
      window.URL.revokeObjectURL(dl);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore nella generazione Excel");
    } finally { setDownloading(""); }
  };

  const sendCheck = async () => {
    if (!data || !data.schools.length) return;
    setSending(true); setSendResults(null);
    try {
      const res = await api.post("/office/external-checks/school-assistance/send", {
        appalto_id: appaltoId, date_from: dateFrom, date_to: dateTo,
        school_ids: data.schools.map((s) => s.school_id),
      });
      setSendResults(res.data.results || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore durante l'invio");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="check-assistenza-scolastica">
      <CheckSubNav view={view} setView={setView} testid="cas" />
      {view === "archivio" ? <ExternalCheckArchive type="assistenza" /> : (
      <>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 mb-5" data-testid="cas-filters">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Filtri</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 items-start">
          <div className="min-w-0">
            <Label className="text-xs text-slate-500">Appalto</Label>
            <Select value={appaltoId} onValueChange={setAppaltoId}>
              <SelectTrigger className="rounded-lg mt-1.5 h-10 w-full" data-testid="cas-appalto"><SelectValue placeholder="Scegli un appalto" /></SelectTrigger>
              <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <Label className="text-xs text-slate-500">Tipo di scuola</Label>
            {!appaltoId ? (
              <p className="text-sm text-slate-400 mt-1.5">Seleziona prima un appalto.</p>
            ) : schools.length === 0 ? (
              <p className="text-sm text-slate-400 mt-1.5">Nessuna scuola associata all'appalto.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="cas-school-multi">
                <button type="button" onClick={() => setSelectedSchools([])} data-testid="cas-school-all"
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${selectedSchools.length === 0 ? "bg-[#2F5D62] text-white border-[#2F5D62]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                  Tutte le scuole
                </button>
                {schools.map((s) => {
                  const on = selectedSchools.includes(s.id);
                  return (
                    <button key={s.id} type="button" onClick={() => toggleSchool(s.id)} data-testid={`cas-school-chip-${s.id}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-[#2F5D62] text-white border-[#2F5D62]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                      {s.nome_scuola}{s.plesso_istituto ? ` · ${s.plesso_istituto}` : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <DatePicker label="Dal" value={dateFrom} onChange={setDateFrom} testid="cas-date-from" />
          </div>
          <div className="min-w-0">
            <DatePicker label="Al" value={dateTo} onChange={setDateTo} testid="cas-date-to" />
          </div>
        </div>
        <Button onClick={apply} disabled={loading} data-testid="cas-apply" className="mt-5 rounded-lg">
          {loading ? "Ricerca…" : "Applica filtri"}
        </Button>
      </div>

      {data && (
        data.schools.length === 0 ? (
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="cas-empty">
              Nessuna prestazione di Assistenza Scolastica nel periodo selezionato.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5" data-testid="cas-results">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-slate-500">{data.schools.length} scuola/e con prestazioni nel periodo</p>
              <Button onClick={() => { setSendResults(null); setSendOpen(true); }} data-testid="cas-send-open"
                className="rounded-lg bg-[#2F5D62] hover:bg-[#264b4f]">
                <Send className="h-4 w-4 mr-1.5" /> Invia check alle scuole
              </Button>
            </div>
            {data.schools.map((school) => (
              <div key={school.school_id} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`cas-school-${school.school_id}`}>
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#2F5D62]">
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-white truncate" data-testid={`cas-school-name-${school.school_id}`}>
                      {school.school_name}
                      {school.email
                        ? <span className="font-normal text-white/70 text-sm"> · {school.email}</span>
                        : <span className="font-normal italic text-white/50 text-xs"> · Email non configurata</span>}
                    </p>
                    {school.plesso && <p className="text-xs text-white/70">Plesso/Istituto: {school.plesso}</p>}
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => downloadSchool(school)} disabled={downloading === school.school_id}
                    data-testid={`cas-export-${school.school_id}`} className="rounded-lg shrink-0">
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {downloading === school.school_id ? "…" : "Excel"}
                  </Button>
                </div>
                <div className="p-4 space-y-4">
                  {school.users.map((u) => (
                    <div key={u.user_id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`cas-user-${u.user_id}`}>
                      <div className="px-3 py-2 bg-[#E8EFF0] font-semibold text-slate-800 text-sm">{u.user_name}</div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                              <th className="px-3 py-2 font-medium">Educatore</th>
                              <th className="px-3 py-2 font-medium">Data</th>
                              <th className="px-3 py-2 font-medium">Orario</th>
                              <th className="px-3 py-2 font-medium text-right">Ore</th>
                            </tr>
                          </thead>
                          <tbody>
                            {u.entries.map((e, idx) => (
                              <tr key={idx} className="border-b border-slate-100 last:border-0">
                                <td className="px-3 py-2 text-slate-700">{e.educatore}</td>
                                <td className="px-3 py-2 text-slate-700">{e.date ? e.date.split("-").reverse().join("/") : ""}</td>
                                <td className="px-3 py-2 text-slate-700">{e.start_time && e.end_time ? `${e.start_time}–${e.end_time}` : ""}</td>
                                <td className="px-3 py-2 text-slate-700 text-right">{e.hours_label}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-3 py-2 bg-[#F1F5F4] text-sm font-semibold text-slate-800 text-right">
                        Totale {u.user_name}: {u.total_hours_label} h
                      </div>
                    </div>
                  ))}
                  <div className="rounded-xl bg-[#DDEAE7] px-3 py-2.5 text-sm font-bold text-[#1F3A3D] text-right" data-testid={`cas-school-total-${school.school_id}`}>
                    Totale assistenza scuola nel periodo: {school.total_hours_label} h
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Dialog open={sendOpen} onOpenChange={(o) => { if (!o) { setSendOpen(false); setSendResults(null); } }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="cas-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Invia check alle scuole</DialogTitle>
            <DialogDescription>
              {sendResults ? "Esito dell'invio:" : `Periodo dal ${dateFrom ? dateFrom.split("-").reverse().join("/") : ""} al ${dateTo ? dateTo.split("-").reverse().join("/") : ""}. Ogni scuola riceve solo il proprio Excel al proprio indirizzo.`}
            </DialogDescription>
          </DialogHeader>

          {!sendResults ? (
            <div className="max-h-72 overflow-y-auto space-y-1.5 py-1" data-testid="cas-send-list">
              {(data?.schools || []).map((s) => (
                <div key={s.school_id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-800 truncate">{s.school_name}</span>
                  {s.email
                    ? <span className="text-slate-500 text-xs truncate">{s.email}</span>
                    : <span className="text-amber-600 text-xs font-medium shrink-0">Email non configurata</span>}
                </div>
              ))}
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-1.5 py-1" data-testid="cas-send-results">
              {sendResults.map((r) => {
                const map = {
                  sent: { icon: "✓", cls: "text-emerald-700", label: "inviato" },
                  no_email: { icon: "⚠", cls: "text-amber-600", label: "email non configurata" },
                  failed: { icon: "⚠", cls: "text-red-600", label: "invio fallito" },
                  no_data: { icon: "⚠", cls: "text-slate-500", label: "nessuna prestazione" },
                }[r.status] || { icon: "•", cls: "text-slate-500", label: r.status };
                return (
                  <div key={r.school_id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`cas-send-result-${r.school_id}`}>
                    <span className="font-medium text-slate-800 truncate">{r.school_name}</span>
                    <span className={`text-xs font-semibold shrink-0 ${map.cls}`}>{map.icon} {map.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            {!sendResults ? (
              <>
                <Button variant="outline" onClick={() => setSendOpen(false)} data-testid="cas-send-cancel" className="rounded-lg">Annulla</Button>
                <Button onClick={sendCheck} disabled={sending} data-testid="cas-send-confirm" className="rounded-lg bg-[#2F5D62] hover:bg-[#264b4f]">
                  <Send className="h-4 w-4 mr-1.5" /> {sending ? "Invio…" : "Invia"}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setSendOpen(false); setSendResults(null); }} data-testid="cas-send-close" className="rounded-lg">Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}

function CheckAdmSection() {
  const [view, setView] = useState("nuovo");
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  useEffect(() => {
    api.get("/office/external-checks/adm/appalti").then((r) => {
      const list = r.data || []; setAppalti(list);
      if (list.length === 1) setAppaltoId(list[0].id);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    setSelected([]); setUsers([]); setData(null);
    if (!appaltoId) return;
    api.get(`/office/external-checks/adm/users?appalto_id=${appaltoId}`).then((r) => setUsers(r.data || [])).catch(() => {});
  }, [appaltoId]);

  const toggleUser = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const apply = async () => {
    if (!appaltoId) { toast.error("Seleziona l'appalto"); return; }
    if (!dateFrom || !dateTo) { toast.error("Indica il periodo Dal / Al"); return; }
    setLoading(true); setData(null);
    try {
      let url = `/office/external-checks/adm?appalto_id=${appaltoId}&date_from=${dateFrom}&date_to=${dateTo}`;
      if (selected.length > 0) url += `&user_ids=${selected.join(",")}`;
      setData((await api.get(url)).data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore nel recupero dati"); }
    finally { setLoading(false); }
  };
  const downloadUser = async (u) => {
    setDownloading(u.user_id);
    try {
      const res = await api.get(`/office/external-checks/adm/export?appalto_id=${appaltoId}&date_from=${dateFrom}&date_to=${dateTo}&user_id=${u.user_id}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || ""; const m = cd.match(/filename="?([^"]+)"?/);
      const dl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = dl; a.download = m ? m[1] : `ADM_${u.user_name}.xlsx`; a.click();
      window.URL.revokeObjectURL(dl);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore Excel"); }
    finally { setDownloading(""); }
  };
  const sendCheck = async () => {
    if (!data || !data.users.length) return;
    setSending(true); setSendResults(null);
    try {
      const res = await api.post("/office/external-checks/adm/send", {
        appalto_id: appaltoId, date_from: dateFrom, date_to: dateTo, user_ids: data.users.map((u) => u.user_id),
      });
      setSendResults(res.data.results || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore invio"); }
    finally { setSending(false); }
  };

  const fmtD = (s) => s ? s.split("-").reverse().join("/") : "";
  return (
    <div data-testid="check-adm">
      <CheckSubNav view={view} setView={setView} testid="adm" />
      {view === "archivio" ? <ExternalCheckArchive type="adm" /> : (
      <>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 mb-5" data-testid="adm-filters">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Filtri</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 items-start">
          <div className="min-w-0">
            <Label className="text-xs text-slate-500">Appalto</Label>
            <Select value={appaltoId} onValueChange={setAppaltoId}>
              <SelectTrigger className="rounded-lg mt-1.5 h-10 w-full" data-testid="adm-appalto"><SelectValue placeholder="Scegli un appalto ADM" /></SelectTrigger>
              <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <Label className="text-xs text-slate-500">Utenti</Label>
            {!appaltoId ? <p className="text-sm text-slate-400 mt-1.5">Seleziona prima un appalto.</p>
              : users.length === 0 ? <p className="text-sm text-slate-400 mt-1.5">Nessun utente ADM sull'appalto.</p>
              : (
                <div className="flex flex-wrap gap-2 mt-1.5" data-testid="adm-user-multi">
                  <button type="button" onClick={() => setSelected([])} data-testid="adm-user-all"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${selected.length === 0 ? "bg-[#3A4A7A] text-white border-[#3A4A7A]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>Tutti gli utenti</button>
                  {users.map((u) => {
                    const on = selected.includes(u.id);
                    return <button key={u.id} type="button" onClick={() => toggleUser(u.id)} data-testid={`adm-user-chip-${u.id}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-[#3A4A7A] text-white border-[#3A4A7A]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>{u.nome}</button>;
                  })}
                </div>
              )}
          </div>
          <div className="min-w-0"><DatePicker label="Dal" value={dateFrom} onChange={setDateFrom} testid="adm-date-from" /></div>
          <div className="min-w-0"><DatePicker label="Al" value={dateTo} onChange={setDateTo} testid="adm-date-to" /></div>
        </div>
        <Button onClick={apply} disabled={loading} data-testid="adm-apply" className="mt-5 rounded-lg">{loading ? "Ricerca…" : "Applica filtri"}</Button>
      </div>

      {data && (data.users.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="adm-empty">Nessuna prestazione ADM nel periodo selezionato.</CardContent></Card>
      ) : (
        <div className="space-y-5" data-testid="adm-results">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-slate-500">{data.users.length} utente/i con prestazioni ADM nel periodo</p>
            <Button onClick={() => { setSendResults(null); setSendOpen(true); }} data-testid="adm-send-open" className="rounded-lg bg-[#3A4A7A] hover:bg-[#2f3d66]"><Send className="h-4 w-4 mr-1.5" /> Invia check ADM</Button>
          </div>
          {data.users.map((u) => (
            <div key={u.user_id} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`adm-user-${u.user_id}`}>
              <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#3A4A7A]">
                <p className="font-heading font-bold text-white truncate" data-testid={`adm-user-name-${u.user_id}`}>
                  {u.user_name}
                  {u.email ? <span className="font-normal text-white/70 text-sm"> · {u.email}</span> : <span className="font-normal italic text-white/50 text-xs"> · Email non configurata</span>}
                </p>
                <Button size="sm" variant="secondary" onClick={() => downloadUser(u)} disabled={downloading === u.user_id} data-testid={`adm-export-${u.user_id}`} className="rounded-lg shrink-0"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> {downloading === u.user_id ? "…" : "Excel"}</Button>
              </div>
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="px-3 py-2 font-medium">Educatore</th><th className="px-3 py-2 font-medium">Data</th><th className="px-3 py-2 font-medium">Orario</th><th className="px-3 py-2 font-medium text-right">Ore</th>
                    </tr></thead>
                    <tbody>{u.entries.map((e, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="px-3 py-2 text-slate-700">{e.educatore}</td>
                        <td className="px-3 py-2 text-slate-700">{fmtD(e.date)}</td>
                        <td className="px-3 py-2 text-slate-700">{e.start_time && e.end_time ? `${e.start_time}–${e.end_time}` : ""}</td>
                        <td className="px-3 py-2 text-slate-700 text-right">{e.hours_label}</td>
                      </tr>))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 rounded-xl bg-[#E3E8F4] px-3 py-2 text-sm font-bold text-[#20305C] text-right" data-testid={`adm-user-total-${u.user_id}`}>Totale {u.user_name}: {u.total_hours_label} h</div>
              </div>
            </div>
          ))}
        </div>
      ))}

      <Dialog open={sendOpen} onOpenChange={(o) => { if (!o) { setSendOpen(false); setSendResults(null); } }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="adm-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Invia check ADM</DialogTitle>
            <DialogDescription>{sendResults ? "Esito dell'invio:" : `Periodo dal ${fmtD(dateFrom)} al ${fmtD(dateTo)}. Ogni utente riceve solo il proprio Excel al proprio indirizzo.`}</DialogDescription>
          </DialogHeader>
          {!sendResults ? (
            <div className="max-h-72 overflow-y-auto space-y-1.5 py-1" data-testid="adm-send-list">
              {(data?.users || []).map((u) => (
                <div key={u.user_id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-800 truncate">{u.user_name}</span>
                  {u.email ? <span className="text-slate-500 text-xs truncate">{u.email}</span> : <span className="text-amber-600 text-xs font-medium shrink-0">Email non configurata</span>}
                </div>
              ))}
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-1.5 py-1" data-testid="adm-send-results">
              {sendResults.map((r) => {
                const m = { sent: { i: "✓", c: "text-emerald-700", l: "inviato" }, no_email: { i: "⚠", c: "text-amber-600", l: "email non configurata" }, failed: { i: "⚠", c: "text-red-600", l: "invio fallito" }, no_data: { i: "⚠", c: "text-slate-500", l: "nessuna prestazione" } }[r.status] || { i: "•", c: "text-slate-500", l: r.status };
                return <div key={r.user_id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`adm-send-result-${r.user_id}`}><span className="font-medium text-slate-800 truncate">{r.user_name}</span><span className={`text-xs font-semibold shrink-0 ${m.c}`}>{m.i} {m.l}</span></div>;
              })}
            </div>
          )}
          <DialogFooter>
            {!sendResults ? (<>
              <Button variant="outline" onClick={() => setSendOpen(false)} data-testid="adm-send-cancel" className="rounded-lg">Annulla</Button>
              <Button onClick={sendCheck} disabled={sending} data-testid="adm-send-confirm" className="rounded-lg bg-[#3A4A7A] hover:bg-[#2f3d66]"><Send className="h-4 w-4 mr-1.5" /> {sending ? "Invio…" : "Invia"}</Button>
            </>) : (
              <Button onClick={() => { setSendOpen(false); setSendResults(null); }} data-testid="adm-send-close" className="rounded-lg">Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}

function CheckEsterniGeneraliSection() {
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [servicesList, setServicesList] = useState([]);
  const [selServices, setSelServices] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    api.get("/my-appalti").then((r) => {
      const list = r.data || []; setAppalti(list);
      if (list.length === 1) setAppaltoId(list[0].id);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    setSelServices([]); setData(null);
    const a = appalti.find((x) => x.id === appaltoId);
    const seen = new Set(); const uniq = [];
    (a?.servizi || []).filter((s) => s.stato === "attivo").forEach((s) => {
      if (!seen.has(s.slug)) { seen.add(s.slug); uniq.push({ key: s.slug, label: svcLabel(s.slug) }); }
    });
    setServicesList(appaltoId ? uniq : []);
  }, [appaltoId, appalti]);

  const svcParam = () => selServices.join(",");
  const qs = () => `appalto_id=${appaltoId}&date_from=${dateFrom}&date_to=${dateTo}&services=${svcParam()}`;
  const apply = async () => {
    if (!appaltoId) { toast.error("Seleziona l'appalto"); return; }
    if (selServices.length === 0) { toast.error("Seleziona almeno un servizio"); return; }
    if (!dateFrom || !dateTo) { toast.error("Indica Data inizio e Data fine"); return; }
    if (dateFrom > dateTo) { toast.error("La Data inizio non può essere successiva alla Data fine"); return; }
    setLoading(true); setData(null);
    try {
      const { data: res } = await api.get(`/office/external-checks/general?${qs()}`);
      setData(res);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore nel recupero dati"); }
    finally { setLoading(false); }
  };
  const download = async (kind) => {
    const isPdf = kind === "pdf";
    setDownloading(kind);
    try {
      const path = isPdf ? "general/export.pdf" : "general/export";
      const res = await api.get(`/office/external-checks/${path}?${qs()}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || ""; const m = cd.match(/filename="?([^"]+)"?/);
      const dl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = dl;
      a.download = m ? m[1] : (isPdf ? "CheckEsterniGenerali.pdf" : "CheckEsterniGenerali.xlsx"); a.click();
      window.URL.revokeObjectURL(dl);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || (isPdf ? "Errore PDF" : "Errore Excel")); }
    finally { setDownloading(""); }
  };

  return (
    <div data-testid="check-esterni-generali">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 mb-5" data-testid="ceg-filters">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Filtri</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 items-start">
          <div className="min-w-0">
            <Label className="text-xs text-slate-500">Appalto</Label>
            <Select value={appaltoId} onValueChange={setAppaltoId}>
              <SelectTrigger className="rounded-lg mt-1.5 h-10 w-full" data-testid="ceg-appalto"><SelectValue placeholder="Scegli un appalto" /></SelectTrigger>
              <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-0"><DatePicker label="Data inizio" value={dateFrom} onChange={setDateFrom} testid="ceg-date-from" /></div>
          <div className="min-w-0"><DatePicker label="Data fine" value={dateTo} onChange={setDateTo} testid="ceg-date-to" /></div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-500">Servizi ({selServices.length} selezionati)</Label>
            {servicesList.length > 0 && (
              <button type="button" data-testid="ceg-services-all"
                onClick={() => setSelServices(selServices.length === servicesList.length ? [] : servicesList.map((s) => s.key))}
                className="text-xs font-semibold text-[#22364D] hover:underline">
                {selServices.length === servicesList.length ? "Deseleziona tutti" : "Seleziona tutti"}
              </button>
            )}
          </div>
          {!appaltoId ? <p className="text-sm text-slate-400 mt-1.5">Seleziona prima un appalto.</p>
            : servicesList.length === 0 ? <p className="text-xs text-slate-400 mt-1.5">Nessun servizio configurato per questo appalto.</p>
            : (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="ceg-services">
                {servicesList.map((s) => {
                  const on = selServices.includes(s.key);
                  return <button key={s.key} type="button" data-testid={`ceg-service-${s.key}`}
                    onClick={() => setSelServices(on ? selServices.filter((x) => x !== s.key) : [...selServices, s.key])}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-[#22364D] text-white border-[#22364D]" : "bg-white text-slate-600 border-slate-300 hover:border-[#22364D]/50"}`}>{s.label}</button>;
                })}
              </div>
            )}
        </div>
        <Button onClick={apply} disabled={loading || !appaltoId || selServices.length === 0} data-testid="ceg-apply" className="mt-5 rounded-lg">{loading ? "Ricerca…" : "Applica filtri"}</Button>
      </div>

      {data && (data.rows.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="ceg-empty">Nessuna prestazione nel periodo selezionato.</CardContent></Card>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="ceg-results">
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#22364D]">
            <div className="min-w-0">
              <p className="font-heading font-bold text-white truncate" data-testid="ceg-appalto-name">{data.appalto_nome}</p>
              <p className="text-white/70 text-xs truncate">{data.servizi} · {data.periodo} · {data.count} prestazioni · Tot. {data.totals?.totale}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="secondary" onClick={() => download("xlsx")} disabled={!!downloading} data-testid="ceg-export" className="rounded-lg"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> {downloading === "xlsx" ? "…" : "Scarica Excel"}</Button>
              <Button size="sm" variant="secondary" onClick={() => download("pdf")} disabled={!!downloading} data-testid="ceg-export-pdf" className="rounded-lg"><FileText className="h-4 w-4 mr-1.5" /> {downloading === "pdf" ? "…" : "Scarica PDF"}</Button>
            </div>
          </div>
          {data.totals?.multi && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-600" data-testid="ceg-totals">
              <span className="font-semibold text-slate-500">Totale ore per servizio:</span>
              {data.totals.per_servizio.map((it) => (
                <span key={it.servizio}><span className="text-slate-500">{it.servizio}:</span> <b className="text-slate-800">{it.ore}</b></span>
              ))}
            </div>
          )}
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
                  <th className="px-3 py-2 font-medium">Nome</th><th className="px-3 py-2 font-medium">Cognome</th>
                  <th className="px-3 py-2 font-medium">Data</th><th className="px-3 py-2 font-medium">Servizio</th>
                  <th className="px-3 py-2 font-medium text-center" colSpan={2}>Ingresso</th>
                  <th className="px-3 py-2 font-medium text-center" colSpan={2}>Uscita</th>
                  <th className="px-3 py-2 font-medium text-right">Ore totali</th>
                </tr>
                <tr className="text-left text-[11px] text-slate-400 border-b border-slate-200">
                  <th></th><th></th><th></th><th></th>
                  <th className="px-3 py-1 font-normal">Dichiarato</th><th className="px-3 py-1 font-normal">Server</th>
                  <th className="px-3 py-1 font-normal">Dichiarata</th><th className="px-3 py-1 font-normal">Server</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>{data.rows.map((e, i) => {
                const newDay = i > 0 && e.data !== data.rows[i - 1].data;
                return (
                <tr key={i} data-testid={`ceg-row-${i}`}
                  className={`last:border-0 ${newDay ? "border-t-2 border-t-slate-300" : "border-b border-slate-100"}`}>
                  <td className="px-3 py-2 text-slate-700">{e.nome}</td>
                  <td className="px-3 py-2 text-slate-700">{e.cognome}</td>
                  <td className="px-3 py-2 text-slate-700">{e.data}</td>
                  <td className="px-3 py-2 text-slate-700">{e.servizio}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{e.ingresso_dichiarato}</td>
                  <td className="px-3 py-2 text-slate-500">{e.ingresso_server}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{e.uscita_dichiarata}</td>
                  <td className="px-3 py-2 text-slate-500">{e.uscita_server}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">{e.ore_totali}</td>
                </tr>);
              })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}


const OFFICE_SECTIONS = [
  { key: "lavoratori", label: "Lavoratori", icon: Users },
  { key: "timbrature", label: "Timbrature", icon: Clock },
  { key: "giustificativi", label: "Richieste giustificativi", icon: FileCheck },
  { key: "calendario", label: "Agenda assenze", icon: CalendarDays },
  { key: "operatori", label: "Operatori in servizio", icon: Radio },
  { key: "consuntivo", label: "Consuntivo Libri Presenze", icon: BookOpen },
  { key: "rendicontazione", label: "Rendicontazione", icon: FileSpreadsheet },
  { key: "utenti", label: "Utenti e gruppi", icon: UserRound },
  { key: "scuole", label: "Scuole per assistenza scolastica", icon: School },
  { key: "sedi-operative", label: "Sedi operative", icon: Building2 },
  { key: "check-assistenza-scolastica", label: "Check Assistenza Scolastica", icon: FileSearch },
  { key: "check-adm", label: "Check ADM", icon: FileSearch },
  { key: "check-esterni-generali", label: "Presenze Operatori", icon: Users },
  { key: "relazioni", label: "Relazioni", icon: UserCheck },
  { key: "agende-settimanali", label: "Agende Settimanali Lavoratori", icon: CalendarClock },
  { key: "presenze-utenti", label: "Presenze utenti", icon: ClipboardList },
  { key: "comunicazione-nuova", label: "Nuova comunicazione", icon: PenSquare },
  { key: "comunicazioni-inviate", label: "Comunicazioni inviate", icon: Inbox },
  { key: "mail-view", label: "Mail View", icon: MailWarning },
  { key: "matchmaking", label: "Assegnazioni", icon: Puzzle },
  { key: "elenco-assegnazioni", label: "Sostituzioni", icon: ArrowRightLeft },
];
// Macro-aree della Home Ufficio (sola riorganizzazione/navigazione frontend).
const OFFICE_GROUPS = [
  { key: "inserimento", label: "Inserimento dati", icon: ClipboardPlus, desc: "Anagrafiche e dati operativi",
    sections: ["lavoratori", "scuole", "utenti", "sedi-operative", "matchmaking", "elenco-assegnazioni"] },
  { key: "esportazione", label: "Esportazione dati", icon: Download, desc: "Report ed esportazioni",
    sections: ["consuntivo", "rendicontazione", "presenze-utenti", "check-esterni-generali"] },
  { key: "controlli", label: "Controlli e autorizzazioni", icon: ShieldCheck, desc: "Verifiche e approvazioni",
    sections: ["giustificativi", "timbrature", "operatori"] },
  { key: "agende", label: "Agende", icon: CalendarClock, desc: "Assenze e agende settimanali",
    sections: ["calendario", "agende-settimanali"] },
  { key: "check-esterni", label: "Check esterni", icon: Network, desc: "Verifiche per soggetti terzi",
    sections: ["check-assistenza-scolastica", "check-adm"] },
  { key: "comunicazioni-generali", label: "Comunicazioni generali", icon: Megaphone, desc: "Invia e consulta le comunicazioni",
    sections: ["comunicazione-nuova", "comunicazioni-inviate", "mail-view"] },
];
const ACCENTS = ["bg-[#E0F2FE]", "bg-[#F1F5F9]", "bg-[#E2E8F0]", "bg-[#DBEAFE]"];
// Coerenza cromatica icone Home Ufficio:
// - Area "Comunicazioni e Verifiche" → icone antracite con disegno bianco (home + sottomenù + card).
// - Area "Agende" → mantiene il colore lilla della home anche nei sottomenù/header.
const OFFICE_ANTRACITE_BOX = "bg-[#3A3A4C] border border-[#2C2C39]";
const COMM_AREA_GROUPS = new Set(["controlli", "check-esterni", "comunicazioni-generali", "comunicazioni"]);
const COMM_AREA_SECTIONS = new Set(["giustificativi", "timbrature", "operatori", "check-assistenza-scolastica", "check-adm", "comunicazione-nuova", "comunicazioni-inviate", "relazioni", "mail-view"]);
const AGENDA_AREA_SECTIONS = new Set(["calendario", "agende-settimanali"]);

function UtentiSection() {
  const [multi, setMulti] = useState(false);
  const isNido = (f) => (f.service_types || []).includes("nido");
  const isSala = (d) => (d.service_types || []).includes("nido") && !d.cognome;
  if (multi) return <MultiUserImport onClose={() => setMulti(false)} />;
  return (
    <AnagraficaAdmin
      endpoint="/admin/school-users" singular="utente" testid="school-users"
      requireFilterToShow
      multiFilters={[
        {
          label: "Appalto",
          testid: "school-users-appalto-filter",
          loadOptions: async () => {
            const { data } = await api.get("/my-appalti");
            return (data || []).map((a) => ({ value: a.id, label: a.nome }));
          },
          itemMatches: (u, sel) => sel.includes(u.appalto_id),
        },
        {
          label: "Servizio associato",
          testid: "school-users-service-filter",
          loadOptions: async () => {
            const { data } = await api.get("/my-appalti");
            const map = new Map();
            (data || []).forEach((a) => (a.servizi || []).forEach((s) => {
              if (s.stato === "attivo" && (s.entity_kind === "school_user" || s.slug === "centro_estivo") && !map.has(s.slug))
                map.set(s.slug, { value: s.slug, label: svcLabel(s.slug) });
            }));
            return [...map.values()];
          },
          itemMatches: (u, sel) => (u.service_types || []).some((s) => sel.includes(s)),
        },
      ]}
      extraAction={
        <Button variant="outline" data-testid="open-multi-user-btn" onClick={() => setMulti(true)}
          className="rounded-lg font-semibold shrink-0 bg-white text-slate-700 border-2 border-amber-500 hover:bg-amber-50 hover:text-slate-900 hover:border-amber-600">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Inserimento multiplo
        </Button>
      }
      titleFn={(d) => (d.cognome ? `${d.nome} ${d.cognome}` : d.nome)}
      subtitleFn={(d) => {
        if (isSala(d)) return d.comune || "Sala Nido";
        if (d.ce_config) {
          const cc = d.ce_config;
          if (cc.tipologia === "gruppo") return `Gruppo · ${cc.monte_ore_giornaliero != null ? fmtNum(cc.monte_ore_giornaliero) + " ore/giorno" : "—"}`;
          return `Utente · ${cc.monte_ore_settimanale != null ? fmtNum(cc.monte_ore_settimanale) + " ore/settimana" : "—"}`;
        }
        const mo = d.monte_ore_per_service?.assistenza_scolastica ?? d.monte_ore_settimanale;
        return `${d.scuola || "—"} · ${d.comune}${d.plesso ? " · " + d.plesso : ""} · ${d.categoria_rendicontazione || "—"} · ${mo != null ? fmtNum(mo) + "h/sett" : "—"}`;
      }}
      emptyForm={{ nome: "", cognome: "", categoria_rendicontazione: "", school_id: "", mail_privata_adm: "", comune: "", monte_ore_per_service: {}, ce_tipologia: "", ce_site_id: "", ce_monte_ore_settimanale: "", ce_monte_ore_giornaliero: "", appalto_id: "", service_types: [] }}
      fields={[
        { name: "appalto_id", label: "Appalto", type: "appalto", resets: ["service_types", "school_id", "categoria_rendicontazione"] },
        { name: "service_types", label: "Servizi associati", type: "multiservice",
          filterOptions: (s) => s.entity_kind === "school_user" || s.slug === "centro_estivo" },
        { name: "nome", label: "Nome" },
        { name: "cognome", label: "Cognome", showIf: (f) => !isNido(f), clearOnHide: true,
          required: (f) => !((f.service_types || []).includes("centro_estivo") && f.ce_tipologia === "gruppo") },
        { name: "categoria_rendicontazione", label: "Categoria di rendicontazione", type: "select", options: CATEGORIE,
          showIf: (f) => (f.service_types || []).some((s) => ["assistenza_scolastica"].includes(s)) },
        { name: "school_id", label: "Scuola collegata", type: "asyncSelect", optionsEndpoint: "/admin/schools", filterByAppalto: true,
          showIf: (f) => (f.service_types || []).some((s) => ["assistenza_scolastica"].includes(s)),
          optionLabel: (s) => `${s.nome_scuola}${s.plesso_istituto ? " · " + s.plesso_istituto : ""}`,
          infoFn: (s) => `Plesso: ${s.plesso_istituto || "—"} · Segreteria: ${s.email_segreteria || "—"}` },
        { name: "comune", label: "Comune", showIf: (f) => !isNido(f), clearOnHide: true },
        { name: "mail_privata_adm", label: "Mail privata ADM", type: "email",
          showIf: (f) => (f.service_types || []).includes("adm"), clearOnHide: true,
          required: (f) => (f.service_types || []).includes("adm"),
          helper: "Destinatario del Check ADM inviato all'utente" },
        { name: "monte_ore_per_service", label: "Monte ore per servizio", type: "monteorePerService",
          schema: { assistenza_scolastica: { serviceLabel: "Assistenza scolastica", ph: "es. 8" }, adm: { serviceLabel: "ADM", ph: "es. 12" }, rest: { serviceLabel: "ReST", ph: "es. 6" } } },
        { name: "ce_tipologia", label: "Tipologia (Centro Estivo)", type: "segmented",
          options: [{ value: "utente", label: "Utente" }, { value: "gruppo", label: "Gruppo" }],
          showIf: (f) => (f.service_types || []).includes("centro_estivo"), clearOnHide: true },
        { name: "ce_site_id", label: "Sede (Centro Estivo)", type: "ceSede", optionsEndpoint: "/admin/operative-sites",
          showIf: (f) => (f.service_types || []).includes("centro_estivo"), clearOnHide: true },
        { name: "ce_monte_ore", label: "Monte ore (Centro Estivo)", type: "ceMonteOre",
          showIf: (f) => (f.service_types || []).includes("centro_estivo") && (f.ce_tipologia === "utente" || f.ce_tipologia === "gruppo") },
      ]}
    />
  );
}

function ScuoleSection() {
  const [multi, setMulti] = useState(false);
  if (multi) return <MultiSchoolImport onClose={() => setMulti(false)} />;
  return (
    <AnagraficaAdmin
      endpoint="/admin/schools" singular="scuola" testid="schools"
      multiFilters={[
        {
          label: "Appalto",
          testid: "schools-appalto-filter",
          loadOptions: async () => {
            const { data } = await api.get("/my-appalti");
            return (data || []).map((a) => ({ value: a.id, label: a.nome }));
          },
          itemMatches: (s, sel) => (s.appalto_ids || []).some((id) => sel.includes(id)),
        },
      ]}
      extraAction={
        <Button variant="outline" data-testid="open-multi-school-btn" onClick={() => setMulti(true)}
          className="rounded-lg font-semibold shrink-0 bg-white text-slate-700 border-2 border-amber-500 hover:bg-amber-50 hover:text-slate-900 hover:border-amber-600">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Inserimento multiplo
        </Button>
      }
      titleFn={(d) => d.nome_scuola}
      subtitleFn={(d) => `${d.plesso_istituto || "—"}${d.email_segreteria ? " · " + d.email_segreteria : ""}`}
      badgesFn={(d) => (d.appalti_visibili || []).map((a) => (
        <Badge key={a.id} variant="secondary" className="text-[10px]" data-testid={`schools-appalto-badge-${a.id}`}>{a.nome}</Badge>
      ))}
      emptyForm={{ nome_scuola: "", plesso_istituto: "", email_segreteria: "", appalto_ids: [] }}
      fields={[
        { name: "appalto_ids", label: "Appalti associati", type: "multiappalto" },
        { name: "nome_scuola", label: "Nome scuola" },
        { name: "plesso_istituto", label: "Plesso / Istituto", required: false },
        { name: "email_segreteria", label: "Email segreteria", required: false },
      ]}
    />
  );
}

function SediOperativeSection() {
  const [multi, setMulti] = useState(false);
  if (multi) return <MultiSiteImport onClose={() => setMulti(false)} />;
  return (
    <>
    <AnagraficaAdmin
      endpoint="/admin/operative-sites" singular="sede operativa" testid="operative-sites"
      multiFilters={[
        {
          label: "Appalto",
          testid: "operative-sites-appalto-filter",
          loadOptions: async () => {
            const { data } = await api.get("/my-appalti");
            return (data || []).map((a) => ({ value: a.id, label: a.nome }));
          },
          itemMatches: (s, sel) => sel.includes(s.appalto_id),
        },
      ]}
      extraAction={
        <Button variant="outline" data-testid="open-multi-site-btn" onClick={() => setMulti(true)}
          className="rounded-lg font-semibold shrink-0 bg-white text-slate-700 border-2 border-amber-500 hover:bg-amber-50 hover:text-slate-900 hover:border-amber-600">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Inserimento multiplo
        </Button>
      }
      titleFn={(d) => d.denominazione}
      subtitleFn={(d) => `${[d.indirizzo, d.comune].filter(Boolean).join(" · ") || "—"} · ${d.appalto_nome || "—"}`}
      badgesFn={(d) => (d.service_types || []).map((s) => (
        <Badge key={s} className="bg-[#E0F2FE] text-primary hover:bg-[#E0F2FE] border-0 text-[10px]" data-testid={`operative-sites-service-badge-${s}`}>{svcLabel(s)}</Badge>
      ))}
      emptyForm={{ denominazione: "", indirizzo: "", comune: "", note: "", appalto_id: "", service_types: [], rendic_params: {} }}
      fields={[
        { name: "appalto_id", label: "Appalto", type: "appalto", resets: ["service_types"] },
        { name: "service_types", label: "Servizi associati", type: "multiservice",
          filterOptions: (s) => s.entity_kind === "operative_site" || s.slug === "casa_di_riposo" },
        { name: "denominazione", label: "Denominazione della sede" },
        { name: "indirizzo", label: "Indirizzo", required: false },
        { name: "comune", label: "Comune", required: false },
        { name: "note", label: "Note", required: false },
        { name: "rendic_params", type: "rendicparams", labelFn: svcLabel,
          helper: "Opzionale · utilizzato per la futura rendicontazione PRE/Post e Spazio Compiti",
          schema: {
            pre_scuola: [
              { key: "educatore", label: "Monte ore quotidiano Educatore", ph: "es. 2,5" },
              { key: "sorveglianza", label: "Monte ore quotidiano Sorveglianza", ph: "es. 1" },
            ],
            post_scuola: [
              { key: "educatore", label: "Monte ore quotidiano Educatore", ph: "es. 2,5" },
              { key: "sorveglianza", label: "Monte ore quotidiano Sorveglianza", ph: "es. 1" },
            ],
            spazio_compiti: [
              { key: "monte_ore_settimanale", label: "Monte ore settimanale", ph: "es. 15" },
            ],
            assistenza_scuolabus: [
              { key: "monte_ore_settimanale", label: "Monte ore settimanale", ph: "es. 15" },
            ],
            assistenza_mensa: [
              { key: "monte_ore_settimanale", label: "Monte ore settimanale", ph: "es. 15" },
            ],
            centro_aggregativo: [
              { key: "monte_ore_settimanale", label: "Monte ore settimanale", ph: "es. 15" },
            ],
          } },
      ]}
    />
    <BlueBoxSites />
    </>
  );
}

const TODO_TONE = {
  none: "bg-white border-slate-200 hover:border-slate-300",
  warn: "bg-amber-50 border-amber-200 hover:border-amber-300",
  urgent: "bg-red-50 border-red-200 hover:border-red-300",
};
const TODO_ICON = { none: "text-slate-400", warn: "text-amber-600", urgent: "text-red-600" };
const SUB_TODO_CHIP = {
  suggerita: { chip: "bg-[#F0E1E7] text-[#4A3340] border-[#E1CFD8]", label: "Suggerita" },
  disponibile: { chip: "bg-sky-100 text-sky-700 border-sky-200", label: "Disponibile" },
  urgente: { chip: "bg-orange-100 text-orange-700 border-orange-300", label: "Urgente" },
  coperta: { chip: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Coperta" },
};
const todoFmtDate = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }); } catch { return iso; } };
const todoLeavePeriod = (it) => {
  if (it.start_time && it.end_time) return `${todoFmtDate(it.start_date)} · ${it.start_time}–${it.end_time}`;
  if (it.end_date && it.end_date !== it.start_date) return `${todoFmtDate(it.start_date)} → ${todoFmtDate(it.end_date)}`;
  return todoFmtDate(it.start_date);
};

function TodoGestire({ send, leave, sub, anomalies, open, setOpen, onNavSend, onNavTimbrature, onOpenLeave, onOpenSub, onSeenCovered }) {
  const [covered, setCovered] = useState([]);
  const [fading, setFading] = useState({});
  const pausedRef = useRef(false);

  useEffect(() => { setCovered(sub.covered || []); setFading({}); }, [sub.covered]);

  // Fade-out delle coperte quando l'Ufficio apre il riepilogo Sostituzioni (circa 1 ogni 3s).
  useEffect(() => {
    if (open !== "sostituzioni") return;
    const snap = covered.slice();
    if (snap.length === 0) return;
    onSeenCovered && onSeenCovered(snap.map((c) => c.id));
    let i = 0;
    const int = setInterval(() => {
      if (pausedRef.current) return; // non rimuovere mentre l'utente interagisce con una card
      if (i >= snap.length) { clearInterval(int); return; }
      const c = snap[i]; i += 1;
      setFading((f) => ({ ...f, [c.id]: true }));
      setTimeout(() => setCovered((cur) => cur.filter((x) => x.id !== c.id)), 600);
    }, 3000);
    return () => clearInterval(int);
    /* eslint-disable-next-line */
  }, [open]);

  const Row = ({ testid, icon: Icon, label, desc, severity, count, mode, isOpen, onClick }) => {
    const t = severity || "none";
    return (
      <button data-testid={testid} onClick={onClick}
        className={`w-full text-left rounded-xl border ${TODO_TONE[t]} active:scale-[0.99] hover:shadow-sm shadow-sm transition-all duration-200 px-4 py-3.5 flex items-center gap-3`}>
        <div className="h-10 w-10 rounded-lg bg-white/70 border border-black/5 flex items-center justify-center shrink-0">
          <Icon className={`h-5 w-5 ${TODO_ICON[t]}`} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold text-slate-900 text-sm truncate">{label}</p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{desc}</p>
        </div>
        {count > 0 && (
          <span data-testid={`${testid}-badge`}
            className="min-w-[24px] h-6 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-sm shrink-0">
            {count}
          </span>
        )}
        {mode === "expand"
          ? <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>
    );
  };

  const toggle = (k) => setOpen((cur) => (cur === k ? null : k));

  return (
    <div data-testid="office-todo" className="mt-10">
      <h2 className="orbit-area-title font-heading text-lg font-semibold text-slate-800 mb-1 flex items-center gap-2">
        <span className="inline-block h-4 w-1.5 rounded-full bg-[#6D5FB8]" />Da gestire
      </h2>
      <p className="text-sm text-slate-500 mb-3">Attività dell'ufficio in sospeso, ordinate per priorità</p>
      <div className="space-y-2.5">
        {/* 1. Invio dati (Fase 1, invariato) */}
        <Row testid="todo-send-monthly" icon={Send} label="Invio dati agli uffici"
          desc={send.count > 0 ? `${send.count} appalt${send.count === 1 ? "o" : "i"} da inviare per il mese precedente` : "Tutti i dati del mese precedente sono stati inviati"}
          severity={send.severity} count={send.count} mode="nav" onClick={onNavSend} />

        {/* 2. Richieste giustificativi (Fase 2) */}
        <Row testid="todo-giustificativi" icon={FileCheck} label="Richieste giustificativi"
          desc={leave.count > 0 ? `${leave.count} richiest${leave.count === 1 ? "a" : "e"} da verificare` : "Nessuna richiesta da verificare"}
          severity={leave.severity} count={leave.count} mode="expand" isOpen={open === "giustificativi"}
          onClick={() => toggle("giustificativi")} />
        {open === "giustificativi" && (
          <div data-testid="todo-giustificativi-panel" className="pl-2 pr-1 pb-1 space-y-2">
            {leave.items.length === 0 ? (
              <p className="text-xs text-slate-400 py-2 px-2">Nessuna richiesta in sospeso.</p>
            ) : leave.items.map((it) => (
              <button key={it.id} data-testid={`todo-leave-card-${it.id}`} onClick={() => onOpenLeave(it)}
                className={`w-full text-left rounded-lg border px-3 py-2.5 flex items-center gap-3 hover:shadow-sm transition ${it.urgent ? "border-red-200 bg-red-50/70" : "border-slate-200 bg-white"}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-slate-900 truncate">{it.worker_name}</p>
                  <p className="text-xs text-slate-500 truncate">{it.causale_label} · {todoLeavePeriod(it)}</p>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md border bg-amber-100 text-amber-700 border-amber-200 shrink-0">{it.status_label}</span>
                <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
              </button>
            ))}
          </div>
        )}

        {/* 3. Sostituzioni (Fase 2) */}
        <Row testid="todo-sostituzioni" icon={ArrowRightLeft} label="Sostituzioni"
          desc={sub.count > 0 ? `${sub.count} da gestire o coprire` : "Nessuna sostituzione da gestire"}
          severity={sub.severity} count={sub.count} mode="expand" isOpen={open === "sostituzioni"}
          onClick={() => toggle("sostituzioni")} />
        {open === "sostituzioni" && (
          <div data-testid="todo-sostituzioni-panel" className="pl-2 pr-1 pb-1 space-y-2">
            {sub.items.length === 0 && covered.length === 0 ? (
              <p className="text-xs text-slate-400 py-2 px-2">Nessuna sostituzione da gestire.</p>
            ) : (<>
              {sub.items.map((it) => {
                const Icon = SERVICES[slugFromKey(it.service)]?.icon || ArrowRightLeft;
                const tone = SUB_TODO_CHIP[it.ui_status] || SUB_TODO_CHIP.disponibile;
                return (
                  <button key={it.id || it.key} data-testid={`todo-sub-card-${it.id || it.key}`} onClick={() => onOpenSub(it)}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 flex items-center gap-3 hover:shadow-sm transition ${it.ui_status === "urgente" ? "border-orange-200 bg-orange-50/60" : "border-slate-200 bg-white"}`}>
                    <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-slate-500" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-900 truncate">{SERVICES[slugFromKey(it.service)]?.label || it.service}</p>
                      <p className="text-xs text-slate-500 truncate">{todoFmtDate(it.date)} · {it.start_time}–{it.end_time}{it.entity_kind !== "appalto" && it.entity_label ? ` · ${it.entity_label}` : ""}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border shrink-0 ${tone.chip}`}>{tone.label}</span>
                    <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                  </button>
                );
              })}
              {covered.map((c) => {
                const Icon = SERVICES[slugFromKey(c.service)]?.icon || ArrowRightLeft;
                return (
                  <button key={c.id} data-testid={`todo-sub-covered-${c.id}`} onClick={() => onOpenSub(c)}
                    onMouseEnter={() => { pausedRef.current = true; }} onMouseLeave={() => { pausedRef.current = false; }}
                    className={`w-full text-left rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2.5 flex items-center gap-3 transition-all duration-500 ${fading[c.id] ? "opacity-0 scale-95" : "opacity-100"}`}>
                    <div className="h-9 w-9 rounded-lg bg-white border border-emerald-200 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-emerald-600" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-900 truncate">{SERVICES[slugFromKey(c.service)]?.label || c.service}</p>
                      <p className="text-xs text-slate-500 truncate">{todoFmtDate(c.date)} · {c.start_time}–{c.end_time}{c.substitute_worker_label ? ` · ${c.substitute_worker_label}` : ""}</p>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md border bg-emerald-100 text-emerald-700 border-emerald-200 shrink-0 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Coperta</span>
                  </button>
                );
              })}
            </>)}
          </div>
        )}

        {/* 4. Timbrature (Fase 1, invariato) */}
        <Row testid="todo-timbrature" icon={Clock} label="Timbrature"
          desc={anomalies > 0 ? `${anomalies} anomali${anomalies === 1 ? "a" : "e"} da verificare` : "Nessuna anomalia da verificare"}
          severity={anomalies > 0 ? "warn" : "none"} count={anomalies} mode="nav" onClick={onNavTimbrature} />
      </div>
    </div>
  );
}

export default function OfficeHome() {
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [intro, setIntro] = useState(() => peekIntro());
  useEffect(() => { clearIntro(); }, []);
  const [section, setSection] = useState(null);
  // Preferiti Orbit Desktop: caricamento per-account + navigazione deep-link dalla sidebar.
  useEffect(() => {
    loadFavorites().then(setFavs);
    const onChange = () => setFavs(getFavoritesCache());
    const applyNav = (g, s) => { setGroup(g); setSection(s); try { window.scrollTo(0, 0); } catch (_) {} };
    const p = consumePendingNav();
    if (p) applyNav(p.group, p.section);
    const onNav = (e) => { if (e?.detail) applyNav(e.detail.group, e.detail.section); };
    window.addEventListener(FAVORITES_EVENT, onChange);
    window.addEventListener(FAV_NAV_EVENT, onNav);
    return () => {
      window.removeEventListener(FAVORITES_EVENT, onChange);
      window.removeEventListener(FAV_NAV_EVENT, onNav);
    };
  }, []);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [pendingAnomalies, setPendingAnomalies] = useState(0);
  const [stampAlerts, setStampAlerts] = useState(0);
  const [agendaUpdated, setAgendaUpdated] = useState(0);
  const [archiveOld, setArchiveOld] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [relationsAction, setRelationsAction] = useState(0);
  const [mailViewBadge, setMailViewBadge] = useState(0);
  const [mvRefresh, setMvRefresh] = useState(0);
  const refreshMailBadge = () => api.get("/office/mail-view/badge").then((r) => setMailViewBadge(r.data?.count || 0)).catch(() => {});
  const [subTotal, setSubTotal] = useState(0);
  const [todoSend, setTodoSend] = useState({ count: 0, severity: "none" });
  const [todoLeave, setTodoLeave] = useState({ count: 0, severity: "none", items: [] });
  const [todoSub, setTodoSub] = useState({ count: 0, severity: "none", items: [], covered: [] });
  const [todoOpen, setTodoOpen] = useState(null); // accordion: "giustificativi" | "sostituzioni" | null
  const [deepLink, setDeepLink] = useState(null); // { section, tab?, id?, key? }
  const [favs, setFavs] = useState(() => getFavoritesCache());
  const [desktop, setDesktop] = useState(() => isDesktopExperience());
  const [orbitOn, setOrbitOn] = useState(true); // animazione orbitale visibile di default (toggle momentaneo)
  useEffect(() => {
    const r = () => setDesktop(isDesktopExperience());
    window.addEventListener(PREVIEW_DESKTOP_EVENT, r);
    window.addEventListener("appinstalled", r);
    let mq; try { mq = window.matchMedia("(display-mode: standalone)"); mq.addEventListener("change", r); } catch (_) {}
    return () => {
      window.removeEventListener(PREVIEW_DESKTOP_EVENT, r);
      window.removeEventListener("appinstalled", r);
      try { mq && mq.removeEventListener("change", r); } catch (_) {}
    };
  }, []);
  const [monthlyComplete, setMonthlyComplete] = useState(false);
  const _nowRome = romeYM();
  const [expAppalti, setExpAppalti] = useState([]);
  const hasPresenzeServices = expAppalti.some((a) => (a.servizi || []).some((s) => s.stato === "attivo" && ["pre_scuola", "post_scuola", "centro_estivo", "blue_box"].includes(s.slug)));
  const [exportCtx, setExportCtx] = useState({ appaltoId: "", month: _nowRome.month, year: _nowRome.year });
  const [exportStatus, setExportStatus] = useState({ cons: false, rend: false });
  const [sendOpen, setSendOpen] = useState(false);
  const [sendPreview, setSendPreview] = useState(null);
  const [sendPreviewBusy, setSendPreviewBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const current = OFFICE_SECTIONS.find((s) => s.key === section);
  const currentGroup = OFFICE_GROUPS.find((g) => g.key === group);
  const SectionIcon = current?.icon;
  const GroupIcon = currentGroup?.icon;

  const loadMonthlyComplete = () => {
    api.get("/office/consuntivo/monthly-complete").then((r) => setMonthlyComplete(!!r.data?.complete)).catch(() => {});
  };
  const loadExportStatus = () => {
    if (!exportCtx.appaltoId) { setExportStatus({ cons: false, rend: false }); return; }
    const q = `appalto_id=${exportCtx.appaltoId}&year=${exportCtx.year}&month=${exportCtx.month}`;
    Promise.all([
      api.get(`/office/consuntivo/check-status?${q}`).then((r) => !!r.data?.complete).catch(() => false),
      api.get(`/office/rendicontazione/services-status?${q}`).then((r) => {
        const s = r.data?.services || [];
        return s.length > 0 && s.every((x) => x.controlled);
      }).catch(() => false),
    ]).then(([cons, rend]) => setExportStatus({ cons, rend }));
  };
  const openSendModal = async () => {
    setSendResult(null); setSendPreview(null); setSendPreviewBusy(true); setSendOpen(true);
    try {
      const q = `appalto_id=${exportCtx.appaltoId}&year=${exportCtx.year}&month=${exportCtx.month}`;
      const { data } = await api.get(`/office/export/send-monthly/preview?${q}`);
      setSendPreview(data);
    } catch (err) {
      toast.error(formatApiError(err)); setSendOpen(false);
    } finally { setSendPreviewBusy(false); }
  };
  const confirmSend = async () => {
    setSendBusy(true);
    try {
      const { data } = await api.post("/office/export/send-monthly", {
        appalto_id: exportCtx.appaltoId, year: exportCtx.year, month: exportCtx.month });
      setSendResult(data);
      if (data.status === "sent") toast.success("Dati mensili inviati correttamente.");
      else if (data.status === "partial") toast.warning("Invio parziale: alcune email non sono partite.");
      else if (data.status === "not_configured") toast.error("Servizio e-mail non configurato.");
      else toast.error("Invio non riuscito.");
      setSendOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSendBusy(false); }
  };
  useEffect(() => {
    setSendResult(null);
    if (group === "esportazione") {
      loadMonthlyComplete();
      loadExportStatus();
    }
    if (group === "esportazione" && expAppalti.length === 0) {
      api.get("/my-appalti").then((r) => setExpAppalti(r.data || [])).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [group, section, exportCtx.appaltoId, exportCtx.month, exportCtx.year]);

  const loadPending = () => {
    api.get("/office/leave/pending-count").then((r) => setPendingLeave(r.data?.count || 0)).catch(() => {});
    api.get("/office/anomalies/pending-count").then((r) => setPendingAnomalies(r.data?.count || 0)).catch(() => {});
    api.get("/office/stamp-alerts/count").then((r) => setStampAlerts(r.data?.count || 0)).catch(() => {});
    api.get("/office/active-services/count").then((r) => setActiveCount(r.data?.count || 0)).catch(() => {});
    api.get("/office/archive-alert").then((r) => setArchiveOld(!!r.data?.has_old)).catch(() => {});
    api.get("/office/agenda/updated-count").then((r) => setAgendaUpdated(r.data?.count || 0)).catch(() => {});
    api.get("/office/relations/action-count").then((r) => setRelationsAction(r.data?.count || 0)).catch(() => {});
    api.get("/office/mail-view/badge").then((r) => setMailViewBadge(r.data?.count || 0)).catch(() => {});
    api.get("/office/substitutions/badges").then((r) => setSubTotal(r.data?.total || 0)).catch(() => {});
    api.get("/office/todo/send-monthly-status").then((r) => setTodoSend({ count: r.data?.count || 0, severity: r.data?.severity || "none" })).catch(() => {});
    api.get("/office/todo/leave-pending").then((r) => setTodoLeave({ count: r.data?.count || 0, severity: r.data?.severity || "none", items: r.data?.items || [] })).catch(() => {});
    api.get("/office/todo/substitutions").then((r) => setTodoSub({ count: r.data?.count || 0, severity: r.data?.severity || "none", items: r.data?.items || [], covered: r.data?.covered || [] })).catch(() => {});
  };
  const refreshAgendaCount = () => api.get("/office/agenda/updated-count").then((r) => setAgendaUpdated(r.data?.count || 0)).catch(() => {});
  useEffect(() => { loadPending(); }, [section]);
  // Aggiornamento continuo leggero del contatore "Operatori in servizio" (badge verde informativo)
  useEffect(() => {
    const id = setInterval(() => {
      api.get("/office/active-services/count").then((r) => setActiveCount(r.data?.count || 0)).catch(() => {});
    }, 15000);
    const onVis = () => { if (!document.hidden) loadPending(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
    /* eslint-disable-next-line */
  }, []);

  const sectionMeta = (key) => OFFICE_SECTIONS.find((s) => s.key === key);
  // tone "amber" = elementi che richiedono azione (entrano nella somma della macro-card);
  // tone "green" = indicatore puramente informativo (NON entra nella somma).
  const sectionBadge = (key) => {
    if (key === "giustificativi") return pendingLeave > 0 ? { count: pendingLeave, tone: "amber" } : null;
    if (key === "timbrature") { const n = pendingAnomalies + stampAlerts; return n > 0 ? { count: n, tone: "amber" } : null; }
    if (key === "operatori") return activeCount > 0 ? { count: activeCount, tone: "green" } : null;
    if (key === "agende-settimanali") return agendaUpdated > 0 ? { count: agendaUpdated, tone: "amber" } : null;
    if (key === "elenco-assegnazioni") return subTotal > 0 ? { count: subTotal, tone: "amber" } : null;
    if (key === "mail-view") return mailViewBadge > 0 ? { count: mailViewBadge, tone: "lilla" } : null;
    return null;
  };
  const groupBadge = (g) => {
    const total = g.sections.reduce((sum, key) => {
      const b = sectionBadge(key);
      return sum + (b && b.tone === "amber" ? b.count : 0);
    }, 0);
    return total > 0 ? total : null;
  };
  // Totale badge numerici realmente visibili nella Home (GroupCard renderizzate + Relazioni). Riusa i contatori esistenti.
  const HOME_BADGE_GROUPS = ["inserimento", "esportazione", "controlli", "check-esterni", "comunicazioni-generali"];
  const homeBadgeTotal = HOME_BADGE_GROUPS.reduce((s, k) => {
    const g = OFFICE_GROUPS.find((x) => x.key === k);
    return s + (g ? (groupBadge(g) || 0) : 0);
  }, 0) + (relationsAction || 0);
  // Azioni urgenti reali: to-do con severità "urgent" che corrispondono a badge della Home (giustificativi, sostituzioni).
  const urgentTotal = (todoLeave?.severity === "urgent" ? (todoLeave.count || 0) : 0)
    + (todoSub?.severity === "urgent" ? (todoSub.count || 0) : 0);

  return (
    <div className="office-theme min-h-screen bg-[#ECEBF2] pb-10">
      {intro && <IntroCover variant="office" onDone={() => { setIntro(false); welcomeToast(user?.nome, "office"); }} />}
      <AppHeader />
      {!group && <MailViewNotifications onChanged={() => { refreshMailBadge(); setMvRefresh((v) => v + 1); }} />}
      <main className={`${desktop ? "max-w-none" : (["consuntivo", "rendicontazione", "presenze-utenti", "check-esterni-generali"].includes(section) ? "max-w-[1600px] xl:w-[94%]" : "max-w-5xl")} mx-auto px-4 md:px-6 py-6`}>
        {!group ? (
          /* --- Livello 1: Home con le 3 macro-card --- */
          <div className={desktop ? "" : "max-w-3xl mx-auto"}>
            <PreviewDesktopToggle />
            <div className={desktop ? "relative mb-10 h-[96px] flex items-center" : "mb-6"}>
              {desktop && (
                <div className={`absolute inset-0 z-0 transition-opacity duration-500 ${orbitOn ? "opacity-100" : "opacity-0"}`} data-testid="orbit-flow-layer">
                  <OrbitFlow count={homeBadgeTotal} urgent={urgentTotal} />
                </div>
              )}
              {desktop && (
                <button type="button" data-testid="orbit-toggle-btn"
                  onClick={() => setOrbitOn((v) => !v)}
                  aria-label={orbitOn ? "Nascondi animazione" : "Mostra animazione"}
                  title={orbitOn ? "Nascondi animazione" : "Mostra animazione"}
                  className="absolute top-0 right-0 z-20 h-6 w-6 flex items-center justify-center rounded-full text-slate-400 opacity-20 hover:opacity-70 transition-opacity">
                  {orbitOn ? <CircleDot className="h-3.5 w-3.5" strokeWidth={2} /> : <Circle className="h-3.5 w-3.5" strokeWidth={2} />}
                </button>
              )}
              <div className="relative z-10">
                <p className="text-sm text-slate-500" data-testid="office-greeting">Ciao,</p>
                <h1 className={`font-heading font-bold tracking-tight text-slate-900 ${[user?.nome, user?.cognome].filter(Boolean).join(" ").length > 18 ? "text-xl" : "text-2xl"}`} data-testid="office-greeting-name">{[user?.nome, user?.cognome].filter(Boolean).join(" ")}</h1>
              </div>
            </div>
            {archiveOld && (
              <div data-testid="archive-old-note"
                className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900 text-sm">Dati in archivio</p>
                  <p className="text-sm text-amber-800">Sono presenti dati archiviati da più di 6 mesi. Verifica gli archivi ed elimina i dati non più necessari.</p>
                </div>
              </div>
            )}
            {(() => {
              const byKey = (k) => OFFICE_GROUPS.find((x) => x.key === k);
              const GroupCard = (g, variant) => {
                if (!g) return null;
                const Icon = g.icon; const badge = groupBadge(g);
                const lillaBadge = g.key === "comunicazioni-generali" ? mailViewBadge : 0;
                const isComm = variant === "comm";
                const isLilla = variant === "lilla" || isComm;
                const cardCls = isLilla
                  ? "bg-[#C4B7E6] border border-[#A99BD6] hover:border-[#8478C4]"
                  : "bg-white border border-slate-200 hover:border-primary/30";
                const iconBoxCls = isComm
                  ? OFFICE_ANTRACITE_BOX
                  : isLilla
                  ? "bg-white border border-[#BCB6D2]"
                  : "bg-gradient-to-br from-[#E1DEEE] to-[#CFC9E2] border border-[#BCB6D2]";
                return (
                  <button key={g.key} data-testid={`office-group-${g.key}`}
                    onClick={() => { setSection(null); setGroup(g.key); }}
                    className={`group relative text-left rounded-2xl ${cardCls} shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 ${desktop ? "w-full p-4 flex flex-row items-center gap-4" : "p-4 flex flex-col items-start gap-3"}`}>
                    {badge != null && (
                      <span data-testid={`office-group-badge-${g.key}`}
                        className={`absolute ${desktop ? "top-2 right-2" : "top-4 right-4"} min-w-[24px] h-6 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-sm`}>
                        {badge}
                      </span>
                    )}
                    {badge == null && lillaBadge > 0 && (
                      <span data-testid={`office-group-lilla-badge-${g.key}`}
                        className={`absolute ${desktop ? "top-2 right-2" : "top-4 right-4"} min-w-[24px] h-6 px-1.5 rounded-full bg-[#6D5FB8] text-white text-xs font-bold flex items-center justify-center shadow-sm`}>
                        {lillaBadge}
                      </span>
                    )}
                    <div className={`${desktop ? "h-11 w-11" : "h-11 w-11"} rounded-xl ${iconBoxCls} shadow-inner flex items-center justify-center shrink-0`}>
                      <Icon className={`h-5 w-5 ${isComm ? "text-white" : "text-primary"}`} strokeWidth={2} />
                    </div>
                    <div className={desktop ? "min-w-0 flex-1" : ""}>
                      <p className={`font-heading font-bold text-slate-900 ${desktop ? "truncate" : ""}`}>{g.label}</p>
                      <p className={`text-xs mt-0.5 ${isLilla ? "text-slate-600" : "text-slate-500"} ${desktop ? "truncate" : ""}`}>{g.desc}</p>
                      {!desktop && (
                        <span className="mt-1 inline-flex items-center text-xs font-semibold text-primary">
                          Apri <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      )}
                    </div>
                    {desktop && <ChevronRight className={`h-4 w-4 ml-auto shrink-0 group-hover:translate-x-0.5 transition-transform ${isComm ? "text-slate-600" : "text-slate-400"}`} />}
                  </button>
                );
              };
              const DirectCard = (c) => {
                const Icon = c.icon;
                return (
                  <button key={c.key} data-testid={`office-direct-${c.key}`}
                    onClick={() => { setGroup("agende"); setSection(c.key); }}
                    className={`group relative text-left rounded-2xl bg-[#E4DEF2] border border-[#C3B9E0] shadow-sm hover:shadow-md hover:border-[#8478C4] active:scale-[0.98] transition-all duration-200 ${desktop ? "w-full p-4 flex flex-row items-center gap-4" : "p-4 flex flex-col items-start gap-3"}`}>
                    <div className={`${desktop ? "h-11 w-11" : "h-11 w-11"} rounded-xl bg-[#C4B7E6] border border-[#A99BD6] shadow-inner flex items-center justify-center shrink-0`}>
                      <Icon className="h-5 w-5 text-primary" strokeWidth={2} />
                    </div>
                    <div className={desktop ? "min-w-0 flex-1" : ""}>
                      <p className={`font-heading font-bold text-slate-900 ${desktop ? "truncate" : ""}`}>{c.label}</p>
                      <p className={`text-xs text-slate-500 mt-0.5 ${desktop ? "truncate" : ""}`}>{c.desc}</p>
                      {!desktop && (
                        <span className="mt-1 inline-flex items-center text-xs font-semibold text-[#5B4A7D]">
                          Apri <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      )}
                    </div>
                    {desktop && <ChevronRight className="h-4 w-4 ml-auto shrink-0 text-[#5B4A7D] group-hover:translate-x-0.5 transition-transform" />}
                  </button>
                );
              };
              const barTitle = (label, first) => (
                <h2 className={`${first ? "" : "orbit-area-title "}font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2`}>
                  <span className="inline-block h-4 w-1.5 rounded-full bg-[#6D5FB8]" />{label}
                </h2>
              );
              const gridCls = desktop ? "grid grid-cols-2 gap-4" : "grid grid-cols-2 gap-4";
              const relazioniCard = (
                <button key="relazioni" data-testid="office-direct-relazioni"
                  onClick={() => { setGroup("comunicazioni"); setSection("relazioni"); }}
                  className={`group relative text-left rounded-2xl bg-[#C4B7E6] border border-[#A99BD6] hover:border-[#8478C4] shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 ${desktop ? "w-full p-4 flex flex-row items-center gap-4" : "p-4 flex flex-col items-start gap-3"}`}>
                  {relationsAction > 0 && (
                    <span data-testid="office-relazioni-badge"
                      className={`absolute ${desktop ? "top-2 right-2" : "top-3 right-3"} min-w-[24px] h-6 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-sm`}>
                      {relationsAction}
                    </span>
                  )}
                  <div className={`${desktop ? "h-11 w-11" : "h-11 w-11"} rounded-xl bg-[#3A3A4C] border border-[#2C2C39] shadow-inner flex items-center justify-center shrink-0`}>
                    <UserCheck className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <div className={desktop ? "min-w-0 flex-1" : ""}>
                    <p className={`font-heading font-bold text-slate-900 ${desktop ? "truncate" : ""}`}>Relazioni</p>
                    <p className={`text-xs mt-0.5 text-slate-600 ${desktop ? "truncate" : ""}`}>Utenti ed operatori titolari</p>
                    {!desktop && (
                      <span className="mt-1 inline-flex items-center text-xs font-semibold text-primary">
                        Apri <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    )}
                  </div>
                  {desktop && <ChevronRight className="h-4 w-4 ml-auto shrink-0 text-slate-600 group-hover:translate-x-0.5 transition-transform" />}
                </button>
              );
              const dati = [GroupCard(byKey("inserimento")), GroupCard(byKey("esportazione"))];
              const agende = [
                DirectCard({ key: "calendario", label: "Agenda assenze", icon: CalendarDays, desc: "Assenze e permessi" }),
                DirectCard({ key: "agende-settimanali", label: "Agende Settimanali Lavoratori", icon: CalendarClock, desc: "Agende della settimana" }),
              ];
              const comunicazioni = [
                GroupCard(byKey("controlli"), "comm"),
                GroupCard(byKey("check-esterni"), "comm"),
                relazioniCard,
                GroupCard(byKey("comunicazioni-generali"), "comm"),
              ];
              return (
                <div data-testid="office-hub">
                  {barTitle("Dati", true)}
                  <div className={gridCls}>{dati}</div>
                  {barTitle("Agende", false)}
                  <div className={gridCls}>{agende}</div>
                  {barTitle("Comunicazioni e Verifiche", false)}
                  <div className={gridCls}>{comunicazioni}</div>
                </div>
              );
            })()}
            <TodoGestire
              send={todoSend} leave={todoLeave} sub={todoSub} anomalies={pendingAnomalies}
              open={todoOpen} setOpen={setTodoOpen}
              onNavSend={() => { setSection(null); setGroup("esportazione"); }}
              onNavTimbrature={() => { setGroup("controlli"); setSection("timbrature"); }}
              onOpenLeave={(it) => { setDeepLink({ section: "giustificativi", id: it.id }); setGroup("controlli"); setSection("giustificativi"); }}
              onOpenSub={(it) => {
                const tab = it.link === "suggerite" ? "suggerite" : "archivio";
                setDeepLink({ section: "elenco-assegnazioni", tab, id: it.id, key: it.key });
                setGroup("inserimento"); setSection("elenco-assegnazioni");
              }}
              onSeenCovered={(ids) => { api.post("/office/todo/substitutions/mark-covered-seen", { ids }).catch(() => {}); }}
            />
          </div>
        ) : !section ? (
          /* --- Livello 2: Sottomenu della macro-area --- */
          <>
            <button data-testid="office-back-home" onClick={() => setGroup(null)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
              <ArrowLeft className="h-4 w-4" /> Indietro
            </button>
            <div className="flex items-center gap-3 mb-1">
              {GroupIcon && (
                <div className={`h-11 w-11 rounded-xl shadow-inner flex items-center justify-center shrink-0 ${COMM_AREA_GROUPS.has(group) ? OFFICE_ANTRACITE_BOX : "bg-gradient-to-br from-[#E1DEEE] to-[#CFC9E2] border border-[#BCB6D2]"}`} data-testid="office-group-icon">
                  <GroupIcon className={`h-5 w-5 ${COMM_AREA_GROUPS.has(group) ? "text-white" : "text-primary"}`} strokeWidth={2} />
                </div>
              )}
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900" data-testid="office-group-title">{currentGroup?.label}</h1>
            </div>
            <p className="text-sm text-slate-500 mb-6">{currentGroup?.desc}</p>
            {group === "esportazione" && (
              <div className="mb-6 max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-sm p-4" data-testid="export-context">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Contesto Esportazione dati</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">Appalto</Label>
                    <Select value={exportCtx.appaltoId} onValueChange={(v) => setExportCtx((c) => ({ ...c, appaltoId: v }))}>
                      <SelectTrigger className="rounded-lg" data-testid="export-ctx-appalto"><SelectValue placeholder="Scegli un appalto" /></SelectTrigger>
                      <SelectContent>{expAppalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="w-full sm:w-44">
                    <Label className="text-xs text-slate-500">Mese</Label>
                    <Select value={String(exportCtx.month)} onValueChange={(v) => setExportCtx((c) => ({ ...c, month: +v }))}>
                      <SelectTrigger className="rounded-lg" data-testid="export-ctx-month"><SelectValue /></SelectTrigger>
                      <SelectContent>{MONTH_NAMES.map((mn, i) => <SelectItem key={i} value={String(i + 1)}>{mn}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="w-full sm:w-28">
                    <Label className="text-xs text-slate-500">Anno</Label>
                    <Select value={String(exportCtx.year)} onValueChange={(v) => setExportCtx((c) => ({ ...c, year: +v }))}>
                      <SelectTrigger className="rounded-lg" data-testid="export-ctx-year"><SelectValue /></SelectTrigger>
                      <SelectContent>{[_nowRome.year - 1, _nowRome.year, _nowRome.year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {!exportCtx.appaltoId && <p className="text-xs text-amber-600 mt-2" data-testid="export-ctx-hint">Seleziona prima Appalto e periodo per aprire Consuntivo e Rendicontazione.</p>}
              </div>
            )}
            {(() => {
              const SectionCard = (key, i, variant) => {
                const s = sectionMeta(key);
                const Icon = s.icon;
                const badge = sectionBadge(key);
                const complete = group === "esportazione" && ((key === "consuntivo" && exportStatus.cons) || (key === "rendicontazione" && exportStatus.rend));
                const isComm = variant === "comm";
                const isLilla = variant === "lilla" || isComm;
                const favable = isFavoritable(key);
                const starred = favable && favs.some((f) => f.group === group && f.section === key);
                const cardCls = isLilla
                  ? "bg-[#CDC4E8] border border-[#A99BD6] hover:border-[#8478C4]"
                  : "bg-white border border-slate-200 hover:border-primary/30";
                const iconBoxCls = isComm
                  ? OFFICE_ANTRACITE_BOX
                  : isLilla
                  ? "bg-white border border-[#BCB6D2]"
                  : "bg-gradient-to-br from-[#E1DEEE] to-[#CFC9E2] border border-[#BCB6D2]";
                const dimmed = group === "esportazione" && (key === "consuntivo" || key === "rendicontazione") && !exportCtx.appaltoId;
                return (
                  <div key={key} className="relative h-full">
                  <motion.button data-testid={`office-nav-${key}`}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    onClick={() => {
                      if (dimmed) { toast.error("Seleziona prima Appalto e periodo"); return; }
                      setSection(key);
                    }}
                    className={`group relative w-full h-full text-left rounded-2xl ${cardCls} shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 ${desktop ? "p-4 flex flex-row items-center gap-4" : "p-4 flex flex-col items-start gap-3"} ${dimmed ? "opacity-60" : ""}`}>
                    {badge != null && !desktop && (
                      <span data-testid={`office-nav-badge-${key}`}
                        className={`absolute top-4 ${favable ? "right-12" : "right-4"} min-w-[24px] h-6 px-1.5 rounded-full text-white text-xs font-bold flex items-center justify-center shadow-sm ${badge.tone === "green" ? "bg-emerald-500" : badge.tone === "lilla" ? "bg-[#6D5FB8]" : "bg-amber-500"}`}>
                        {badge.count}
                      </span>
                    )}
                    <div className={`h-11 w-11 rounded-xl ${iconBoxCls} shadow-inner flex items-center justify-center relative shrink-0`}>
                      <Icon className={`h-5 w-5 ${isComm ? "text-white" : "text-primary"}`} strokeWidth={2} />
                      {complete && (
                        <span data-testid={`office-nav-complete-${key}`} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-emerald-500 border border-white flex items-center justify-center shadow-sm">
                          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                        </span>
                      )}
                    </div>
                    <div className={desktop ? "min-w-0 flex-1" : ""}>
                      <p className={`font-heading font-bold text-slate-900 ${desktop ? "truncate" : ""}`}>{s.label}</p>
                      {!desktop && (
                        <span className="mt-1 inline-flex items-center text-xs font-semibold text-primary">
                          Apri <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      )}
                    </div>
                    {desktop && badge != null && (
                      <span data-testid={`office-nav-badge-${key}`}
                        className={`ml-auto ${favable ? "mr-6" : ""} min-w-[24px] h-6 px-1.5 rounded-full text-white text-xs font-bold flex items-center justify-center shadow-sm ${badge.tone === "green" ? "bg-emerald-500" : badge.tone === "lilla" ? "bg-[#6D5FB8]" : "bg-amber-500"}`}>
                        {badge.count}
                      </span>
                    )}
                    {desktop && <ChevronRight className={`h-4 w-4 shrink-0 ${badge != null ? "" : `ml-auto ${favable ? "mr-6" : ""}`} group-hover:translate-x-0.5 transition-transform ${isComm ? "text-slate-600" : "text-slate-400"}`} />}
                  </motion.button>
                  {favable && (
                    <button type="button" data-testid={`office-fav-star-${key}`}
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(group, key); }}
                      aria-label={starred ? "Rimuovi dai Preferiti" : "Aggiungi ai Preferiti"}
                      className="absolute top-2.5 right-2.5 z-10 h-7 w-7 rounded-full flex items-center justify-center hover:bg-black/5 active:scale-90 transition-all">
                      <Star className={`h-4 w-4 transition-colors ${starred ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} strokeWidth={2} />
                    </button>
                  )}
                  </div>
                );
              };
              const boxTitle = (label) => (
                <h2 className="font-heading text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <span className="inline-block h-4 w-1.5 rounded-full bg-[#6D5FB8]" />{label}
                </h2>
              );
              if (currentGroup?.sections.length === 0) {
                return (
                  <Card className="rounded-xl border-slate-200 shadow-sm">
                    <CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="office-submenu-empty">
                      Nessuna funzione disponibile al momento.
                    </CardContent>
                  </Card>
                );
              }
              if (group === "inserimento") {
                const persone = ["lavoratori", "utenti"];
                const strutture = ["scuole", "sedi-operative"];
                return (
                  <div className={`${desktop ? "" : "max-w-3xl"} space-y-6`} data-testid="office-submenu">
                    <div data-testid="office-submenu-persone">
                      {boxTitle("Persone")}
                      <div className="grid grid-cols-2 gap-4 auto-rows-fr">
                        {persone.map((key, i) => SectionCard(key, i))}
                      </div>
                    </div>
                    <div data-testid="office-submenu-strutture">
                      {boxTitle("Strutture")}
                      <div className="grid grid-cols-2 gap-4 auto-rows-fr">
                        {strutture.map((key, i) => SectionCard(key, i))}
                      </div>
                    </div>
                    <div data-testid="office-submenu-assegnazioni">
                      {boxTitle("Matchmaking")}
                      <div className="grid grid-cols-2 gap-4 auto-rows-fr">
                        {["matchmaking", "elenco-assegnazioni"].map((key, i) => SectionCard(key, i))}
                      </div>
                    </div>
                  </div>
                );
              }
              if (group === "esportazione") {
                return (
                  <div className={desktop ? "" : "max-w-3xl"} data-testid="office-submenu">
                    <div className="grid grid-cols-2 gap-4 auto-rows-fr">
                      {["consuntivo", "rendicontazione"].map((key, i) => SectionCard(key, i))}
                    </div>
                    {exportCtx.appaltoId && exportStatus.cons && exportStatus.rend && (
                      <div className="mt-1" data-testid="invia-dati-mensili-wrap">
                        {/* connettore discreto: due rami dalle card Consuntivo/Rendicontazione verso il pulsante */}
                        <div className="relative h-7 w-full pointer-events-none" aria-hidden="true">
                          <span className="absolute left-1/4 top-0 h-3.5 w-px bg-emerald-300 -translate-x-1/2" />
                          <span className="absolute left-3/4 top-0 h-3.5 w-px bg-emerald-300 -translate-x-1/2" />
                          <span className="absolute left-1/4 right-1/4 top-[14px] h-px bg-emerald-300" />
                          <span className="absolute left-1/2 top-[14px] h-3.5 w-px bg-emerald-300 -translate-x-1/2" />
                        </div>
                        <div className="flex flex-col items-center">
                          <Button data-testid="invia-dati-mensili-btn"
                            onClick={openSendModal}
                            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-6 text-base font-semibold shadow-sm">
                            <Send className="h-5 w-5 mr-2" /> Invia i dati mensili
                          </Button>
                          {!sendResult ? (
                            <p className="text-xs text-slate-400 mt-2">Consuntivo e Rendicontazione risultano entrambi completi.</p>
                          ) : (
                            <div className="mt-3 w-full max-w-md" data-testid="send-monthly-result">
                              {sendResult.status === "sent" && (
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2">
                                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                                  Inviato a {sendResult.recipients_count} destinatari · Consuntivo + {sendResult.rendicontazioni_count} rendicontazioni.
                                </div>
                              )}
                              {sendResult.status === "partial" && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
                                  <AlertTriangle className="h-4 w-4 shrink-0" />
                                  Invio parziale: Consuntivo «{sendResult.consuntivo?.status}» · Rendicontazione «{sendResult.rendicontazione?.status}».
                                </div>
                              )}
                              {(sendResult.status === "failed" || sendResult.status === "not_configured") && (
                                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 flex items-center gap-2">
                                  <XCircle className="h-4 w-4 shrink-0" />
                                  {sendResult.status === "not_configured" ? "Servizio e-mail non configurato." : "Invio non riuscito. Riprova."}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {(hasPresenzeServices || true) && (
                      <div className="mt-8 pt-6 border-t border-slate-200">
                        <div className="grid grid-cols-2 gap-4 auto-rows-fr">
                          {hasPresenzeServices && SectionCard("presenze-utenti", 0)}
                          {SectionCard("check-esterni-generali", 1)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div className={`grid gap-4 auto-rows-fr ${desktop ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-3"}`} data-testid="office-submenu">
                  {currentGroup.sections.filter((key) => key !== "presenze-utenti" || hasPresenzeServices).map((key, i) => SectionCard(key, i, COMM_AREA_GROUPS.has(group) ? "comm" : undefined))}
                </div>
              );
            })()}
          </>
        ) : (
          /* --- Livello 3: Schermata della funzione --- */
          <>
            <button data-testid="office-back-btn" onClick={() => { if (group === "agende" || group === "comunicazioni") setGroup(null); setSection(null); }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
              <ArrowLeft className="h-4 w-4" /> Indietro
            </button>
            <div className="flex items-center gap-3 mb-5">
              {SectionIcon && (
                <div className={`h-11 w-11 rounded-xl shadow-inner flex items-center justify-center shrink-0 ${COMM_AREA_SECTIONS.has(section) ? OFFICE_ANTRACITE_BOX : AGENDA_AREA_SECTIONS.has(section) ? "bg-[#C4B7E6] border border-[#A99BD6]" : "bg-gradient-to-br from-[#E1DEEE] to-[#CFC9E2] border border-[#BCB6D2]"}`} data-testid="office-section-icon">
                  <SectionIcon className={`h-5 w-5 ${COMM_AREA_SECTIONS.has(section) ? "text-white" : "text-primary"}`} strokeWidth={2} />
                </div>
              )}
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900" data-testid="office-section-title">{current?.label}</h1>
            </div>
            {section === "lavoratori" && <LavoratoriTab />}
            {section === "timbrature" && <TimbratureTab onValidated={loadPending} />}
            {section === "giustificativi" && <GiustificativiSection onReviewed={loadPending} highlightId={deepLink?.section === "giustificativi" ? deepLink.id : null} onHighlightDone={() => setDeepLink(null)} />}
            {section === "calendario" && <CalendarSection onOpenWorkerAgenda={({ workerId, date }) => { setDeepLink({ section: "agende-settimanali", workerId, date }); setGroup("agende"); setSection("agende-settimanali"); try { window.scrollTo(0, 0); } catch (_) {} }} />}
            {section === "agende-settimanali" && <AgendeSettimanaliSection onSeen={refreshAgendaCount} deepLink={deepLink?.section === "agende-settimanali" ? deepLink : null} onDeepLinkDone={() => setDeepLink(null)} />}
            {section === "operatori" && <OperatoriSection onCount={setActiveCount} />}
            {section === "consuntivo" && <ConsuntivoSection onChecksChanged={() => { loadMonthlyComplete(); loadExportStatus(); }} ctx={exportCtx} />}
            {section === "rendicontazione" && <RendicontazioneSection ctx={exportCtx} />}
            {section === "presenze-utenti" && <PresenzeUtentiExport appalti={expAppalti} appaltoId={exportCtx.appaltoId} month={exportCtx.month} year={exportCtx.year} />}
            {section === "utenti" && <UtentiSection />}
            {section === "scuole" && <ScuoleSection />}
            {section === "sedi-operative" && <SediOperativeSection />}
            {section === "check-assistenza-scolastica" && <CheckAssistenzaScolasticaSection />}
            {section === "check-adm" && <CheckAdmSection />}
            {section === "check-esterni-generali" && <CheckEsterniGeneraliSection />}
            {section === "relazioni" && <RelationsAdmin />}
            {section === "comunicazione-nuova" && <ComunicazioneNuovaSection />}
            {section === "comunicazioni-inviate" && <ComunicazioniInviateSection />}
            {section === "mail-view" && <MailViewSection refreshKey={mvRefresh} onChanged={refreshMailBadge} />}
            {section === "matchmaking" && <AssegnazioniSection />}
            {section === "elenco-assegnazioni" && <ElencoAssegnazioniSection deepLink={deepLink?.section === "elenco-assegnazioni" ? deepLink : null} onDeepLinkDone={() => setDeepLink(null)} />}
          </>
        )}
      </main>

      <Dialog open={sendOpen} onOpenChange={(o) => { if (!sendBusy) setSendOpen(o); }}>
        <DialogContent className="sm:max-w-lg" data-testid="send-monthly-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-600" /> Invio dati mensili
            </DialogTitle>
            <DialogDescription>
              Verifica i destinatari e gli allegati prima di inviare. Per modificare i recapiti usa le Configurazioni dell'Amministratore.
            </DialogDescription>
          </DialogHeader>
          {sendPreviewBusy ? (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="send-monthly-loading">Caricamento riepilogo…</div>
          ) : sendPreview ? (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                <span className="font-semibold text-slate-800">{sendPreview.appalto_nome}</span>
                <span className="text-slate-500"> · {MONTH_NAMES[sendPreview.month - 1]} {sendPreview.year}</span>
              </div>

              {!sendPreview.email_configured && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 flex items-center gap-2">
                  <XCircle className="h-4 w-4 shrink-0" /> Servizio e-mail non configurato: l'invio non è possibile.
                </div>
              )}

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Destinatari (in copia nascosta)</p>
                {sendPreview.recipients?.length ? (
                  <ul className="space-y-1" data-testid="send-monthly-recipients">
                    {sendPreview.recipients.map((r) => (
                      <li key={r} className="text-sm text-slate-700 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {r}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-amber-600">Nessun destinatario configurato. Imposta le Email di riferimento (Amministratore o Appalto).</p>
                )}
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">1ª email · Consuntivo Libri Presenze</p>
                <div className="text-sm text-slate-700 flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600 shrink-0" /> {sendPreview.consuntivo?.filename}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                  2ª email · Rendicontazioni ({sendPreview.rendicontazioni?.length || 0})
                </p>
                {sendPreview.rendicontazioni?.length ? (
                  <ul className="space-y-1" data-testid="send-monthly-rendicontazioni">
                    {sendPreview.rendicontazioni.map((r) => (
                      <li key={r.service} className="text-sm text-slate-700 flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-emerald-600 shrink-0" /> {r.filename}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-400">Nessuna rendicontazione con prestazioni nel mese: verrà inviato solo il Consuntivo.</p>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sendBusy} data-testid="send-monthly-cancel">Annulla</Button>
            <Button onClick={confirmSend}
              disabled={sendBusy || sendPreviewBusy || !sendPreview?.email_configured || !sendPreview?.recipients?.length}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-monthly-confirm">
              {sendBusy ? "Invio in corso…" : "Conferma e invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const LEAVE_STATUS = {
  pending: { label: "In attesa", icon: Hourglass, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approvata", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rifiutata", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled: { label: "Annullata", icon: Ban, cls: "bg-slate-100 text-slate-600 border-slate-200" },
  ricezione: { label: "In fase di ricezione", icon: Hourglass, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  inviata: { label: "Inviata correttamente", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  non_conforme: { label: "Invio non conforme", icon: AlertTriangle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
const DOC_CAUSALI_FE = ["diritto_studio", "permesso_lutto", "permesso_elettorale", "congedo"];
const MALATTIA_STATUS_OPTIONS = [  { value: "ricezione", label: "In fase di ricezione" },
  { value: "inviata", label: "Inviata correttamente" },
  { value: "non_conforme", label: "Invio non conforme" },
];
const fmtLeaveDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
const fmtReviewDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric" }) : "";
const fmtLeaveDuration = (min) => `${Math.floor((min || 0) / 60)}h ${((min || 0) % 60).toString().padStart(2, "0")}m`;

function LeaveCard({ r, i, children, flash }) {
  const st = LEAVE_STATUS[r.status] || LEAVE_STATUS.pending;
  const StIcon = st.icon;
  const over8 = (r.over_8h_days || []).length;
  const isPermesso = r.causale === "permesso";
  const isMalattia = r.causale === "malattia";
  const is104 = r.causale === "permesso_104";
  const isSind = r.causale === "permesso_sindacale";
  const isAssemblea = r.causale === "assemblea_sindacale";
  const isPnr = r.causale === "permesso_non_retribuito";
  const isDS = DOC_CAUSALI_FE.includes(r.causale);
  let topBadge = { label: st.label, cls: st.cls, Icon: StIcon };
  if (isDS && r.status === "approved") {
    if (r.doc_status === "verificata") topBadge = { label: "Approvata", cls: LEAVE_STATUS.approved.cls, Icon: CheckCircle2 };
    else if (r.doc_status === "caricata") topBadge = { label: "Documentazione in verifica", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Hourglass };
    else if (r.doc_status === "non_conforme") topBadge = { label: "Documentazione non conforme", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertTriangle };
    else topBadge = { label: "In attesa di documentazione", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Hourglass };
  }
  const hourly = (r.start_time && r.end_time) || (!r.full_day && !isMalattia);
  const dateStr = (isPermesso || ((is104 || isSind || isAssemblea || isPnr) && r.start_time))
    ? `${fmtLeaveDate(r.start_date)}${r.start_time ? ` · ${r.start_time}–${r.end_time}` : ""}`
    : `${fmtLeaveDate(r.start_date)} → ${fmtLeaveDate(r.end_date)}`;
  return (
    <Card id={`office-leave-card-${r.id}`} data-testid={`office-leave-card-${i}`} className={`rounded-xl border-slate-200 shadow-sm transition-all duration-500 ${flash ? "ring-2 ring-amber-400 border-amber-300" : ""}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-heading font-bold text-slate-900">{r.worker_name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{r.appalto_nome} · <span className="font-semibold">{r.causale_label}</span></p>
          </div>
          <Badge className={`border ${topBadge.cls} gap-1`} data-testid={`office-leave-status-${i}`}><topBadge.Icon className="h-3.5 w-3.5" /> {topBadge.label}</Badge>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800"><CalendarDays className="h-4 w-4 text-primary" /> {dateStr}</span>
          {isMalattia ? (
            <span className="inline-flex items-center gap-1.5" data-testid={`office-leave-puc-${i}`}><FileText className="h-4 w-4" /> PUC {r.puc_number}</span>
          ) : r.full_day ? (
            <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700" data-testid={`office-leave-fullday-${i}`}><CalendarDays className="h-4 w-4" /> Giornata intera</span>
          ) : (
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {fmtLeaveDuration(r.computed_total_minutes)}</span>
          )}
        </div>
        {r.note && <p className="mt-2 text-sm text-slate-600">{r.note}</p>}
        {r.status === "pending" && !r.preavviso_ok && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid={`office-leave-preavviso-${i}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Preavviso inferiore a 7 giorni
          </p>
        )}
        {r.status === "approved" && over8 > 0 && (
          <p className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid={`office-leave-over8-${i}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Superate 8 ore complessive in {over8} {over8 === 1 ? "giornata" : "giornate"}: {r.over_8h_days.map((d) => fmtLeaveDate(d)).join(", ")}
          </p>
        )}
        {r.review_note && (
          <p className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid={`office-leave-review-note-${i}`}>
            <b>Motivazione Ufficio:</b> {r.review_note}
          </p>
        )}
        {r.reviewed_by && r.status !== "pending" && r.status !== "cancelled" && (
          <p className="mt-2 text-[11px] text-slate-400" data-testid={`office-leave-reviewed-by-${i}`}>
            Valutata da {r.reviewed_by}{r.reviewed_at ? ` · ${fmtReviewDate(r.reviewed_at)}` : ""}
          </p>
        )}
        {r.status === "cancelled" && r.cancelled_by && (
          <p className="mt-2 text-[11px] text-slate-400">Annullata da {r.cancelled_by}{r.cancelled_at ? ` · ${fmtReviewDate(r.cancelled_at)}` : ""}</p>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function GiustificativiSection({ onReviewed, highlightId, onHighlightDone }) {
  const [tab, setTab] = useState("valutare");
  const [flashId, setFlashId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null); // {req, action}
  const [l104Warn, setL104Warn] = useState(null); // {req, check}
  const [assembleaWarn, setAssembleaWarn] = useState(null); // {req, check}
  const [verifyDoc, setVerifyDoc] = useState(false);
  const [docReject, setDocReject] = useState(null); // {req}
  const [docRejectNote, setDocRejectNote] = useState("");

  const openDoc = async (r) => {
    try { const res = await api.get(`/office/leave/requests/${r.id}/document`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); window.open(u, "_blank"); }
    catch { toast.error("Impossibile aprire il documento"); }
  };
  const doDocStatus = async (r, status, note) => {
    setBusy(r.id);
    try {
      await api.post(`/office/leave/requests/${r.id}/document-status`, { status, note: note || null });
      toast.success(status === "verificata" ? "Documentazione verificata" : "Documentazione segnata non conforme");
      setDocReject(null); setDocRejectNote(""); await load(); onReviewed && onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const [reviewNote, setReviewNote] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ start_date: "", end_date: "", total_hours: "", note: "" });
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/office/leave/requests");
      setItems(data || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!highlightId || loading) return;
    setTab("valutare");
    setFlashId(highlightId);
    const s = setTimeout(() => {
      const el = document.getElementById(`office-leave-card-${highlightId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    const t = setTimeout(() => { setFlashId(null); onHighlightDone && onHighlightDone(); }, 2600);
    return () => { clearTimeout(s); clearTimeout(t); };
    /* eslint-disable-next-line */
  }, [highlightId, loading]);

  const pending = items.filter((r) => r.status === "pending" || r.status === "ricezione" || (DOC_CAUSALI_FE.includes(r.causale) && r.doc_status === "caricata"));
  const archive = items.filter((r) => !(r.status === "pending" || r.status === "ricezione" || (DOC_CAUSALI_FE.includes(r.causale) && r.doc_status === "caricata")));
  const list = tab === "valutare" ? pending : archive;

  const doMalattiaStatus = async (r, status) => {
    if (status === r.status) return;
    setBusy(r.id);
    try {
      await api.post(`/office/leave/requests/${r.id}/malattia-status`, { status });
      toast.success("Stato malattia aggiornato");
      await load(); onReviewed && onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const doApproveConfirmed = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/office/leave/requests/${r.id}/review`, { action: "approve" });
      toast.success("Richiesta approvata");
      setL104Warn(null); setAssembleaWarn(null);
      await load(); onReviewed && onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const doApprove = async (r) => {
    // Permessi con controlli automatici (es. Legge 104): se c'è una segnalazione da verificare,
    // mostra un avviso PRIMA della conferma definitiva. La segnalazione NON blocca l'approvazione.
    if (r.causale === "permesso_104") {
      setBusy(r.id);
      try {
        const { data } = await api.get(`/office/leave/requests/${r.id}/legge104-check`);
        if (data?.applicable && data?.exceeded) { setL104Warn({ req: r, check: data }); return; }
      } catch (e) { /* in caso di errore del check, procede senza bloccare */ }
      finally { setBusy(null); }
    }
    if (r.causale === "assemblea_sindacale") {
      setBusy(r.id);
      try {
        const { data } = await api.get(`/office/leave/requests/${r.id}/assemblea-check`);
        if (data?.applicable && data?.exceeded) { setAssembleaWarn({ req: r, check: data }); return; }
      } catch (e) { /* in caso di errore del check, procede senza bloccare */ }
      finally { setBusy(null); }
    }
    await doApproveConfirmed(r);
  };

  const doReview = async () => {
    const { req, action } = reviewTarget;
    if (action === "reject" && !reviewNote.trim()) { toast.error("La motivazione del rifiuto è obbligatoria"); return; }
    setBusy(req.id);
    try {
      await api.post(`/office/leave/requests/${req.id}/review`, { action, review_note: reviewNote.trim() || null, verify_document: !!verifyDoc });
      toast.success(action === "approve" ? "Richiesta approvata" : "Richiesta rifiutata");
      setReviewTarget(null); setReviewNote(""); setVerifyDoc(false); await load(); onReviewed && onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const openEdit = (r) => {
    setEditForm({ start_date: r.start_date, end_date: r.end_date, start_time: r.start_time ?? "", end_time: r.end_time ?? "", total_hours: r.total_hours ?? "", note: r.note ?? "" });
    setEditTarget(r);
  };
  const doEdit = async () => {
    const r = editTarget;
    setBusy(r.id);
    try {
      const payload = { note: editForm.note.trim() || null, start_date: editForm.start_date };
      if (r.causale === "permesso") {
        payload.start_time = editForm.start_time;
        payload.end_time = editForm.end_time;
      } else {
        payload.end_date = editForm.end_date;
        if (editForm.total_hours !== "" && editForm.total_hours != null) payload.total_hours = Number(String(editForm.total_hours).replace(",", "."));
      }
      await api.patch(`/office/leave/requests/${r.id}`, payload);
      toast.success("Richiesta aggiornata");
      setEditTarget(null); await load(); onReviewed && onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const doCancel = async () => {
    const r = cancelTarget;
    setBusy(r.id);
    try {
      await api.post(`/office/leave/requests/${r.id}/cancel`, {});
      toast.success("Richiesta annullata");
      setCancelTarget(null); await load(); onReviewed && onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const [archiveDelTarget, setArchiveDelTarget] = useState(null);
  const doArchiveDelete = async () => {
    const r = archiveDelTarget;
    setBusy(r.id);
    try {
      await api.delete(`/office/leave/requests/${r.id}/archive`);
      toast.success("Voce eliminata dall'archivio");
      setArchiveDelTarget(null); await load(); onReviewed && onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-5" data-testid="leave-tabs">
        <button data-testid="leave-tab-valutare" onClick={() => setTab("valutare")}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${tab === "valutare" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
          Da valutare{pending.length ? ` (${pending.length})` : ""}
        </button>
        <button data-testid="leave-tab-archivio" onClick={() => setTab("archivio")}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${tab === "archivio" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
          Archivio{archive.length ? ` (${archive.length})` : ""}
        </button>
      </div>

      <div className="space-y-3" data-testid="office-leave-list">
        {loading ? (
          [0, 1, 2].map((i) => <div key={i} className="rounded-xl bg-white border border-slate-200 h-28 animate-pulse" />)
        ) : list.length === 0 ? (
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="office-leave-empty">
              {tab === "valutare" ? "Nessuna richiesta da valutare." : "Nessuna richiesta in archivio."}
            </CardContent>
          </Card>
        ) : (
          list.map((r, i) => (
            <LeaveCard key={r.id} r={r} i={i} flash={flashId === r.id}>
              {r.causale === "malattia" ? (
                <div className="mt-3 flex flex-col gap-1.5" data-testid={`office-malattia-status-wrap-${i}`}>
                  <Label className="text-xs text-slate-500">Stato invio</Label>
                  <Select value={r.status} onValueChange={(v) => doMalattiaStatus(r, v)} disabled={busy === r.id}>
                    <SelectTrigger data-testid={`office-malattia-status-${i}`} className="h-10 w-full sm:w-72"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MALATTIA_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`office-malattia-status-opt-${o.value}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  {r.status === "pending" && (
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" disabled={busy === r.id} onClick={() => { if (DOC_CAUSALI_FE.includes(r.causale)) { setReviewNote(""); setVerifyDoc(false); setReviewTarget({ req: r, action: "approve" }); } else { doApprove(r); } }}
                        data-testid={`office-leave-approve-${i}`} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approva
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => { setReviewNote(""); setReviewTarget({ req: r, action: "reject" }); }}
                        data-testid={`office-leave-reject-${i}`} className="border-rose-200 text-rose-600 hover:bg-rose-50">
                        <XCircle className="h-4 w-4 mr-1.5" /> Rifiuta
                      </Button>
                    </div>
                  )}
                  {DOC_CAUSALI_FE.includes(r.causale) && (r.status === "pending" || r.status === "approved") && (
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`office-ds-doc-${i}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Documentazione</span>
                        <Badge className="border bg-slate-50 text-slate-700 border-slate-200" data-testid={`office-ds-doc-status-${i}`}>{r.doc_status_label}</Badge>
                      </div>
                      {r.has_document ? (
                        <button onClick={() => openDoc(r)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid={`office-ds-doc-view-${i}`}>
                          <FileText className="h-4 w-4" /> {r.doc_filename || "Visualizza documento"}
                        </button>
                      ) : r.doc_transmitted ? (
                        <p className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 inline-flex items-start gap-1.5" data-testid={`office-ds-doc-transmitted-${i}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Documentazione trasmessa — Il documento è stato trasmesso allo Spazio Giustificativi e non è più conservato su Orbit.
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-slate-400">Nessun documento caricato dal lavoratore.</p>
                      )}
                      {r.status === "approved" && r.has_document && r.doc_status !== "verificata" && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <Button size="sm" disabled={busy === r.id} onClick={() => doDocStatus(r, "verificata")} data-testid={`office-ds-verify-${i}`} className="bg-emerald-600 hover:bg-emerald-700">
                            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Documentazione verificata
                          </Button>
                          <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => { setDocRejectNote(""); setDocReject({ req: r }); }} data-testid={`office-ds-nonconf-${i}`} className="border-rose-200 text-rose-600 hover:bg-rose-50">
                            <XCircle className="h-4 w-4 mr-1.5" /> Non conforme
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {r.status === "approved" && (
                    <div className="mt-3 flex gap-2">
                      {!(r.causale === "permesso_104" || r.causale === "permesso_sindacale" || r.causale === "assemblea_sindacale" || r.causale === "permesso_non_retribuito" || DOC_CAUSALI_FE.includes(r.causale) || r.full_day) && (
                        <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => openEdit(r)} data-testid={`office-leave-edit-${i}`}>Modifica</Button>
                      )}
                      <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => setCancelTarget(r)} data-testid={`office-leave-cancel-${i}`}
                        className="border-rose-200 text-rose-600 hover:bg-rose-50"><Ban className="h-4 w-4 mr-1.5" /> Annulla</Button>
                    </div>
                  )}
                  {tab === "archivio" && (
                    r.archive_deletable ? (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => setArchiveDelTarget(r)} data-testid={`office-leave-archive-delete-${i}`}
                          className="text-slate-400 hover:text-destructive hover:bg-destructive/5 h-8 px-2"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina dall'archivio</Button>
                      </div>
                    ) : (
                      <p className="mt-2 text-[11px] text-slate-300" data-testid={`office-leave-archive-locked-${i}`}>Eliminabile dall'archivio dopo 60 giorni</p>
                    )
                  )}
                </>
              )}
            </LeaveCard>
          ))
        )}
      </div>

      {/* Dialog valutazione */}
      <Dialog open={!!reviewTarget} onOpenChange={(o) => !o && setReviewTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">{reviewTarget?.action === "approve" ? "Approva richiesta" : "Rifiuta richiesta"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{reviewTarget?.req?.worker_name} · {reviewTarget?.req?.causale_label}</p>
            {reviewTarget?.action === "approve" && DOC_CAUSALI_FE.includes(reviewTarget?.req?.causale) && reviewTarget?.req?.has_document && (
              <label className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 cursor-pointer" data-testid="ds-verify-checkbox-wrap">
                <input type="checkbox" className="mt-0.5" checked={verifyDoc} onChange={(e) => setVerifyDoc(e.target.checked)} data-testid="ds-verify-checkbox" />
                <span className="text-sm text-slate-700">Il documento allegato è <b>conforme</b>: verifica contestuale e invio allo Spazio Giustificativi.</span>
              </label>
            )}
            <div className="space-y-1.5">
              <Label>{reviewTarget?.action === "approve" ? "Nota Ufficio (facoltativa)" : "Motivazione del rifiuto (obbligatoria)"}</Label>
              <Textarea data-testid="review-note-input" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} rows={3}
                placeholder={reviewTarget?.action === "reject" ? "Es. Richiesta negata per mancato rispetto del preavviso previsto." : ""} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewTarget(null)}>Annulla</Button>
            <Button onClick={doReview} disabled={busy === reviewTarget?.req?.id} data-testid="review-confirm-btn"
              className={reviewTarget?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}>
              {reviewTarget?.action === "approve" ? "Approva" : "Rifiuta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avviso verifica soglia Legge 104 (NON bloccante) */}
      <AlertDialog open={!!l104Warn} onOpenChange={(o) => !o && setL104Warn(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="l104-warn-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" /> Verifica richiesta
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Questa richiesta porta {l104Warn?.req?.worker_name} oltre la soglia mensile prevista per il Permesso Legge 104. Verifica i dati prima di procedere.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {l104Warn?.check && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-1.5 text-sm" data-testid="l104-warn-details">
              <div className="flex justify-between"><span className="text-slate-600">Giorni interi già fruiti nel mese</span><span className="font-semibold text-slate-800">{l104Warn.check.giorni_interi_fruiti}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Ore già fruite</span><span className="font-semibold text-slate-800">{l104Warn.check.ore_fruite_h} h</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Totale/equivalente mensile (incl. questa)</span><span className="font-semibold text-amber-800">{l104Warn.check.totale_equivalente_h} h</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Soglia di riferimento</span><span className="font-semibold text-slate-800">{l104Warn.check.soglia_h} h</span></div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="l104-warn-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="l104-warn-approve" onClick={() => l104Warn && doApproveConfirmed(l104Warn.req)}
              className="bg-emerald-600 hover:bg-emerald-700">Approva comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!assembleaWarn} onOpenChange={(o) => !o && setAssembleaWarn(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="assemblea-warn-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" /> Verifica Assemblea sindacale
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Con questa richiesta il lavoratore supera le 12 ore annue di Assemblea sindacale registrate in Orbit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {assembleaWarn?.check && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-1.5 text-sm" data-testid="assemblea-warn-details">
              <div className="flex justify-between"><span className="text-slate-600">Ore già utilizzate nell'anno {assembleaWarn.check.year}</span><span className="font-semibold text-slate-800">{assembleaWarn.check.ore_gia_utilizzate_h} h</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Ore di questa richiesta</span><span className="font-semibold text-slate-800">{assembleaWarn.check.ore_richiesta_h} h</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Totale risultante</span><span className="font-semibold text-amber-800">{assembleaWarn.check.totale_risultante_h} h</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Soglia annua di riferimento</span><span className="font-semibold text-slate-800">{assembleaWarn.check.soglia_h} h</span></div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assemblea-warn-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="assemblea-warn-approve" onClick={() => assembleaWarn && doApproveConfirmed(assembleaWarn.req)}
              className="bg-emerald-600 hover:bg-emerald-700">Approva comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog documentazione non conforme (Diritto allo studio) */}
      <Dialog open={!!docReject} onOpenChange={(o) => !o && setDocReject(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Documentazione non conforme</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{docReject?.req?.worker_name} · {docReject?.req?.causale_label}</p>
            <div className="space-y-1.5">
              <Label>Motivo (facoltativo, visibile al lavoratore)</Label>
              <Textarea data-testid="ds-nonconf-note" value={docRejectNote} onChange={(e) => setDocRejectNote(e.target.value)} rows={3}
                placeholder="Es. Documento illeggibile / non pertinente. Ricaricare l'attestazione corretta." />
            </div>
            <p className="text-xs text-slate-500">Il permesso approvato NON viene modificato. Il lavoratore potrà caricare un nuovo documento.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDocReject(null)}>Annulla</Button>
            <Button onClick={() => docReject && doDocStatus(docReject.req, "non_conforme", docRejectNote.trim() || null)} disabled={busy === docReject?.req?.id}
              data-testid="ds-nonconf-confirm" className="bg-rose-600 hover:bg-rose-700">Segna non conforme</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog modifica (approvata) */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Modifica richiesta approvata</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">{editTarget?.worker_name} · {editTarget?.appalto_nome} · {editTarget?.causale_label} (lavoratore e appalto non modificabili)</p>
            {editTarget?.causale === "permesso" ? (
              <>
                <DatePicker label="Data" value={editForm.start_date} onChange={(v) => setEditForm((f) => ({ ...f, start_date: v }))} testid="edit-date" />
                <div className="grid grid-cols-2 gap-3">
                  <TimePicker label="Dalle ore" value={editForm.start_time} onChange={(v) => setEditForm((f) => ({ ...f, start_time: v }))} testid="edit-start-time" />
                  <TimePicker label="Alle ore" value={editForm.end_time} onChange={(v) => setEditForm((f) => ({ ...f, end_time: v }))} testid="edit-end-time" />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <DatePicker label="Data inizio" value={editForm.start_date} onChange={(v) => setEditForm((f) => ({ ...f, start_date: v }))} testid="edit-start-date" />
                  <DatePicker label="Data fine" value={editForm.end_date} onChange={(v) => setEditForm((f) => ({ ...f, end_date: v }))} testid="edit-end-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Ore (opzionale)</Label>
                  <Input data-testid="edit-hours-input" value={editForm.total_hours} onChange={(e) => setEditForm((f) => ({ ...f, total_hours: e.target.value }))} className="h-12" />
                </div>
              </>
            )}
            <div className="space-y-1.5"><Label>Note</Label><Textarea data-testid="edit-note-input" value={editForm.note} onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)}>Annulla</Button>
            <Button onClick={doEdit} disabled={busy === editTarget?.id} data-testid="edit-confirm-btn">Salva modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma annullamento */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare la richiesta?</AlertDialogTitle>
            <AlertDialogDescription>
              La richiesta verrà contrassegnata come "Annullata": gli effetti sul Libro Presenze e sui conteggi saranno rimossi, ma lo storico resta consultabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Indietro</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} data-testid="cancel-confirm-btn" className="bg-rose-600 hover:bg-rose-700">Annulla richiesta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!archiveDelTarget} onOpenChange={(o) => !o && setArchiveDelTarget(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="leave-archive-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa voce dall'Archivio?</AlertDialogTitle>
            <AlertDialogDescription>
              Viene rimossa solo dalla consultazione dell'Archivio. La richiesta operativa, le ore nel Libro Presenze e l'esito dell'approvazione restano invariati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="leave-archive-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doArchiveDelete} data-testid="leave-archive-delete-confirm" className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Anello minimale "orbita" per la vista Ufficio: si riempie proporzionalmente alla percentuale,
// percentuale intera al centro, colore dalla logica cromatica esistente (colorFor di OrbitChart).
function OfficeTrendRing({ pct, label, testid }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const accent = colorFor(p);
  const size = 52, stroke = 5, r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const filled = (p / 100) * C;
  return (
    <div className="flex flex-col items-center gap-1" data-testid={testid}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={accent} strokeWidth={stroke}
            strokeDasharray={`${filled} ${C}`} strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.5s ease, stroke 0.3s ease" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-heading text-[13px] font-bold leading-none" style={{ color: accent }} data-testid={`${testid}-value`}>{Math.round(p)}%</span>
        </div>
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
    </div>
  );
}

const _TREND_MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function MonthYearPicker({ label, m, y, onMonth, onYear, years, testid }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <span className="text-xs font-semibold text-slate-500 shrink-0">{label}</span>
      <Select value={String(m)} onValueChange={(v) => onMonth(Number(v))}>
        <SelectTrigger className="h-9 w-[124px] rounded-lg border-slate-200 text-sm" data-testid={`${testid}-month`}><SelectValue /></SelectTrigger>
        <SelectContent>{_TREND_MESI.map((mm, i) => <SelectItem key={i} value={String(i + 1)}>{mm}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(y)} onValueChange={(v) => onYear(Number(v))}>
        <SelectTrigger className="h-9 w-[92px] rounded-lg border-slate-200 text-sm" data-testid={`${testid}-year`}><SelectValue /></SelectTrigger>
        <SelectContent>{years.map((yy) => <SelectItem key={yy} value={String(yy)}>{yy}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function AndamentoWorkerDetail({ worker, appaltoName, onBack, onSeen }) {
  const now = new Date();
  const cy = now.getFullYear(), cm = now.getMonth() + 1;
  const [openAlerts, setOpenAlerts] = useState(worker.open_alerts || []);
  const markSeen = async (id) => {
    try {
      await api.post(`/office/stamp-alerts/${id}/seen`);
      setOpenAlerts((prev) => prev.filter((a) => a.id !== id));
      onSeen && onSeen();
    } catch (e) { toast.error("Errore nella presa visione"); }
  };
  const [fromY, setFromY] = useState(cy);
  const [fromM, setFromM] = useState(cm);
  const [toY, setToY] = useState(cy);
  const [toM, setToM] = useState(cm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const years = Array.from({ length: 6 }, (_, i) => cy - 4 + i);

  const qs = `from_year=${fromY}&from_month=${fromM}&to_year=${toY}&to_month=${toM}`;
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/office/stamp-trend/${worker.worker_id}/errors?${qs}`);
      setData(data);
    } catch (e) { setData({ errors: [], count: 0 }); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromY, fromM, toY, toM]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/office/stamp-trend/${worker.worker_id}/errors.xlsx?${qs}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const mm = cd.match(/filename="?([^"]+)"?/);
      const fname = mm ? mm[1] : `Timbrature_errate_${worker.nome}_${worker.cognome}.xlsx`;
      const dl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = dl; a.download = fname; a.click();
      window.URL.revokeObjectURL(dl);
    } catch (e) { toast.error("Errore nella generazione dell'Excel"); }
    setDownloading(false);
  };

  const ctx = [...(worker.appalto_ids || []).map(appaltoName).filter(Boolean),
               ...(worker.services || []).map(svcLabel)].join(" · ");
  const errors = data?.errors || [];

  return (
    <div data-testid="andamento-detail">
      <button onClick={onBack} data-testid="andamento-back"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-primary transition-colors mb-4">
        <ArrowLeft className="h-4 w-4" /> Torna all'elenco
      </button>

      <div className="mb-4">
        <h3 className="font-heading text-xl font-bold text-slate-900" data-testid="andamento-detail-name">{worker.nome} {worker.cognome}</h3>
        {ctx && <p className="text-xs text-slate-400 mt-0.5">{ctx}</p>}
      </div>

      <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50/50" data-testid="andamento-alerts">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-amber-800">Avvisi mensili timbrature</p>
          <span className="text-xs font-medium text-slate-500" data-testid="andamento-detail-yearcount">Segnalazioni {cy}: {worker.alerts_year || 0}</span>
        </div>
        {openAlerts.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="andamento-alerts-none">Nessun avviso da visualizzare.</p>
        ) : (
          <div className="space-y-1.5">
            {openAlerts.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid={`andamento-alert-${a.id}`}>
                <Checkbox onCheckedChange={() => markSeen(a.id)} data-testid={`andamento-alert-seen-${a.id}`} />
                <span className="font-semibold text-slate-700">{_TREND_MESI[a.month - 1]} {a.year}</span>
                <span className="text-xs text-slate-400">· {Math.round(a.percent)}% valide</span>
                <span className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Visualizzato</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4 p-2.5 rounded-xl border border-slate-200/80 bg-slate-50/50" data-testid="andamento-period">
        <MonthYearPicker label="Da" m={fromM} y={fromY} onMonth={setFromM} onYear={setFromY} years={years} testid="andamento-from" />
        <MonthYearPicker label="A" m={toM} y={toY} onMonth={setToM} onYear={setToY} years={years} testid="andamento-to" />
      </div>

      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm font-semibold text-slate-700" data-testid="andamento-error-count">Errori nel periodo: {data ? (data.count ?? errors.length) : "…"}</p>
        <Button variant="outline" size="sm" onClick={download} disabled={downloading || loading || errors.length === 0}
          data-testid="andamento-download-excel" className="rounded-lg text-slate-700 border-slate-300 hover:bg-slate-50">
          <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {downloading ? "…" : "Excel"}
        </Button>
      </div>

      {loading || data === null ? (
        <p className="text-sm text-slate-500">Caricamento…</p>
      ) : errors.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="andamento-errors-empty">Nessuna timbratura errata nel periodo selezionato.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="andamento-errors-list">
          {errors.map((e, i) => (
            <Card key={i} className="rounded-xl border-slate-200 shadow-sm" data-testid={`andamento-error-${i}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-slate-800 text-white border-0 text-[10px]">{e.motivo}</Badge>
                  <p className="text-sm font-semibold text-slate-800">{e.work_date}</p>
                  <span className="text-xs text-slate-400">· {e.appalto || "—"} · {e.servizio || "—"}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Orario lavoratore: <b>{e.orario_lavoratore || "—"}</b> · Orario server: <b>{e.orario_server || "—"}</b>
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


function AndamentoTimbratureTab({ onAlertsChange }) {
  const [appaltiFull, setAppaltiFull] = useState([]);
  const [appaltoFilter, setAppaltoFilter] = useState([]);
  const [serviceFilter, setServiceFilter] = useState([]);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/my-appalti").then((r) => setAppaltiFull(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appaltoFilter.length) params.set("appalti", appaltoFilter.join(","));
      if (serviceFilter.length) params.set("services", serviceFilter.join(","));
      const { data } = await api.get(`/office/stamp-trend?${params.toString()}`);
      setRows(data.workers || []);
    } catch (e) { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appaltoFilter, serviceFilter]);

  const refreshAlerts = async () => {
    await load();
    try { const r = await api.get("/office/stamp-alerts/count"); onAlertsChange && onAlertsChange(r.data?.count || 0); } catch (e) {}
  };

  const appaltoName = (id) => (appaltiFull.find((a) => a.id === id) || {}).nome || "";
  const appaltoOpts = appaltiFull.map((a) => ({ value: a.id, label: a.nome }));
  // Servizi coerenti con gli appalti selezionati (o tutti quelli autorizzati se nessuno selezionato)
  const serviceOpts = (() => {
    const src = appaltoFilter.length ? appaltiFull.filter((a) => appaltoFilter.includes(a.id)) : appaltiFull;
    const map = new Map();
    src.forEach((a) => (a.servizi || []).forEach((s) => {
      if (s.stato === "attivo" && !map.has(s.slug)) map.set(s.slug, { value: s.slug, label: svcLabel(s.slug) });
    }));
    return [...map.values()];
  })();
  // Se cambiano gli appalti, elimina dai servizi selezionati quelli non più pertinenti
  useEffect(() => {
    const valid = new Set(serviceOpts.map((o) => o.value));
    setServiceFilter((prev) => prev.filter((s) => valid.has(s)));
    // eslint-disable-next-line
  }, [appaltoFilter, appaltiFull]);

  if (detail) {
    return <AndamentoWorkerDetail worker={detail} appaltoName={appaltoName}
      onSeen={refreshAlerts} onBack={() => { setDetail(null); refreshAlerts(); }} />;
  }

  return (
    <div data-testid="admin-andamento-timbrature">
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="andamento-filterbar">
        <MultiFilter label="Appalto" options={appaltoOpts} value={appaltoFilter} onChange={setAppaltoFilter} testid="andamento-appalto-filter" />
        <MultiFilter label="Servizio" options={serviceOpts} value={serviceFilter} onChange={setServiceFilter} testid="andamento-service-filter" />
      </div>

      {rows === null || loading ? (
        <p className="text-sm text-slate-500">Caricamento…</p>
      ) : rows.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="andamento-empty">Nessun lavoratore corrisponde ai filtri selezionati.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="andamento-list">
          {rows.map((w) => {
            const ctx = [...(w.appalto_ids || []).map(appaltoName).filter(Boolean),
                         ...(w.services || []).map(svcLabel)].join(" · ");
            return (
              <Card key={w.worker_id} onClick={() => setDetail(w)} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetail(w); } }}
                className="rounded-xl border-slate-200 shadow-sm cursor-pointer transition-all hover:border-primary/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid={`andamento-worker-${w.worker_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate text-sm" data-testid={`andamento-name-${w.worker_id}`}>{w.nome} {w.cognome}</p>
                    {ctx && <p className="text-[11px] text-slate-400 truncate mt-0.5">{ctx}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] font-medium text-slate-500" data-testid={`andamento-yearcount-${w.worker_id}`}>Segnalazioni {new Date().getFullYear()}: {w.alerts_year || 0}</span>
                      {(w.open_alerts || []).length > 0 && (
                        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`andamento-openbadge-${w.worker_id}`}>{w.open_alerts.length}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <OfficeTrendRing pct={w.monthly?.percent} label="Mensile" testid={`andamento-monthly-${w.worker_id}`} />
                    <OfficeTrendRing pct={w.annual?.percent} label="Annuale" testid={`andamento-annual-${w.worker_id}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TimbratureTab({ onValidated }) {
  const [sub, setSub] = useState("convalide");
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    api.get("/office/stamp-alerts/count").then((r) => {
      const n = r.data?.count || 0;
      setAlertCount(n);
      if (n > 0) setSub("andamento");
    }).catch(() => {});
  }, []);
  return (
    <div data-testid="timbrature-section">
      <div className="flex gap-2 mb-5" data-testid="timbrature-subtabs">
        <button data-testid="timbrature-subtab-convalide" onClick={() => setSub("convalide")}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${sub === "convalide" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
          Convalide
        </button>
        <button data-testid="timbrature-subtab-andamento" onClick={() => setSub("andamento")}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all inline-flex items-center gap-2 ${sub === "andamento" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
          Andamento timbrature
          {alertCount > 0 && (
            <span data-testid="timbrature-subtab-andamento-badge"
              className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">{alertCount}</span>
          )}
        </button>
      </div>
      {sub === "convalide" ? <ConvalideTab onValidated={onValidated} /> : <AndamentoTimbratureTab onAlertsChange={setAlertCount} />}
    </div>
  );
}


function ConvalideTab({ onValidated }) {
  const [tab, setTab] = useState("valutare");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [toDel, setToDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [appaltiOpts, setAppaltiOpts] = useState([]);
  const [appaltoFilter, setAppaltoFilter] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const keyOf = (r) => `${r.entry_id}-${r.phase}${r.anomaly_id ? "-" + r.anomaly_id : ""}`;
  const toggleOne = (k) => setSelected((prev) => { const s = new Set(prev); s.has(k) ? s.delete(k) : s.add(k); return s; });

  const archiveDelete = async () => {
    if (!toDel) return;
    setDelBusy(true);
    try {
      await api.post(`/office/anomalies/${toDel.entry_id}/archive-delete`, { phase: toDel.phase, anomaly_id: toDel.anomaly_id || null });
      toast.success("Voce eliminata dall'archivio");
      setToDel(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita"); }
    finally { setDelBusy(false); }
  };

  const load = async () => {
    const { data } = await api.get(`/office/anomalies`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get("/my-appalti").then((r) => setAppaltiOpts((r.data || []).map((a) => ({ value: a.id, label: a.nome })))).catch(() => {});
  }, []);

  // Validazione massiva: riusa lo stesso endpoint/flusso della validazione singola, iterando sulle righe selezionate e visibili.
  const validateSelected = async (validated) => {
    const rows = pending.filter((r) => selected.has(keyOf(r)));
    if (!rows.length) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const r of rows) {
      try {
        await api.post(`/office/anomalies/${r.entry_id}/validate`, { phase: r.phase, validated, anomaly_id: r.anomaly_id || null });
        ok++;
      } catch (e) { fail++; }
    }
    setBulkBusy(false);
    setSelected(new Set());
    await load();
    onValidated && onValidated();
    if (fail) toast.error(`${ok} aggiornate · ${fail} non riuscite`);
    else toast.success(validated ? `${ok} anomali${ok === 1 ? "a giustificata" : "e giustificate"}` : `${ok} anomali${ok === 1 ? "a non giustificata" : "e non giustificate"}`);
  };

  const validate = async (row, validated) => {
    const key = `${row.entry_id}-${row.phase}${row.anomaly_id ? "-" + row.anomaly_id : ""}`;
    setBusy(key);
    try {
      await api.post(`/office/anomalies/${row.entry_id}/validate`, { phase: row.phase, validated, anomaly_id: row.anomaly_id || null });
      toast.success(validated ? "Anomalia giustificata" : "Anomalia non giustificata");
      await load();
      onValidated && onValidated();
    } catch (e) { toast.error("Operazione non riuscita"); }
    finally { setBusy(""); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const phaseLabel = (p) => p === "open" ? "Entrata" : p === "close" ? "Uscita" : "Modifica";
  const fmtSlot = (v) => v ? `${v.work_date}${v.end_date && v.end_date !== v.work_date ? "→" + v.end_date : ""} ${v.start_time || ""}–${v.end_time || ""}`.trim() : "—";

  const matchesAppalto = (r) => appaltoFilter.length === 0 || appaltoFilter.includes(r.appalto_id);
  const pending = (data?.anomalies || []).filter(matchesAppalto);
  const archived = (data?.archived || []).filter(matchesAppalto);
  const list = tab === "valutare" ? pending : archived;
  const visKeys = tab === "valutare" ? pending.map(keyOf) : [];
  const allSelected = visKeys.length > 0 && visKeys.every((k) => selected.has(k));
  const toggleAll = () => setSelected((prev) => {
    const s = new Set(prev);
    if (allSelected) visKeys.forEach((k) => s.delete(k)); else visKeys.forEach((k) => s.add(k));
    return s;
  });

  return (
    <div data-testid="admin-timbrature">
      <div className="flex gap-2 mb-5" data-testid="anomaly-tabs">
        <button data-testid="anomaly-tab-valutare" onClick={() => setTab("valutare")}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${tab === "valutare" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
          Da valutare{pending.length ? ` (${pending.length})` : ""}
        </button>
        <button data-testid="anomaly-tab-archivio" onClick={() => setTab("archivio")}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${tab === "archivio" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
          Archivio{archived.length ? ` (${archived.length})` : ""}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MultiFilter label="Appalto" options={appaltiOpts} value={appaltoFilter}
          onChange={(v) => { setAppaltoFilter(v); }} testid="timbrature-appalto-filter" />
      </div>

      {tab === "valutare" && pending.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-2.5 rounded-lg border border-slate-200 bg-slate-50/60" data-testid="timbrature-bulk-bar">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="timbrature-select-all" />
            Seleziona tutti ({pending.length})
          </label>
          {selected.size > 0 && <span className="text-xs text-slate-500" data-testid="timbrature-selected-count">{selected.size} selezionate</span>}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" disabled={selected.size === 0 || bulkBusy} onClick={() => validateSelected(true)}
              data-testid="timbrature-bulk-validate" className="rounded-lg text-success border-success/30 hover:bg-success/5">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Valida (giustifica)
            </Button>
            <Button variant="outline" size="sm" disabled={selected.size === 0 || bulkBusy} onClick={() => validateSelected(false)}
              data-testid="timbrature-bulk-invalidate" className="rounded-lg text-destructive border-destructive/30 hover:bg-destructive/5">
              Non valida
            </Button>
          </div>
        </div>
      )}

      {!data ? <p className="text-sm text-slate-500">Caricamento…</p> : list.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="anomaly-empty">
          {tab === "valutare" ? "Nessuna anomalia da valutare." : "Nessuna anomalia in archivio."}
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="anomaly-list">
          {list.map((r) => {
            const key = `${r.entry_id}-${r.phase}${r.anomaly_id ? "-" + r.anomaly_id : ""}`;
            const justified = r.validated === true;
            return (
            <Card key={key} className="rounded-xl border-slate-200 shadow-sm" data-testid={`anomaly-${key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {tab === "valutare" && (
                    <Checkbox checked={selected.has(key)} onCheckedChange={() => toggleOne(key)}
                      data-testid={`timbrature-select-${key}`} className="mr-0.5" />
                  )}
                  <Badge className="bg-slate-800 text-white border-0 text-[10px]">{phaseLabel(r.phase)}</Badge>
                  <p className="font-semibold text-slate-800">{r.worker.nome} {r.worker.cognome}</p>
                  {tab === "valutare" ? (
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Da valutare</Badge>
                  ) : justified ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Giustificata</Badge>
                  ) : (
                    <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">Non giustificata</Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600">{r.entity.titolo} · {r.work_date}</p>
                {r.phase === "edit" ? (
                  <p className="text-xs text-slate-500 mt-1">Modificata il {fmt(r.server_at)} · <b>Da:</b> {fmtSlot(r.original)} · <b>A:</b> {fmtSlot(r.new)}</p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">Dichiarato: {fmt(r.declared_at)} · Server: {fmt(r.server_at)}</p>
                )}
                <GeoAnomalyNote phase={r.phase} geo={r.geo} testid={`anomaly-geo-${key}`} className="mt-1.5" />
                <p className="text-sm text-slate-700 mt-1">Motivazione: <b>{r.reason || "—"}</b>{r.note ? ` · ${r.note}` : ""}</p>
                {tab === "archivio" && r.validated_by_name && (
                  <p className="mt-1.5 text-[11px] text-slate-400" data-testid={`anomaly-validated-by-${key}`}>
                    {justified ? "Convalidata" : "Non convalidata"} da {r.validated_by_name}{r.validated_at ? ` · ${fmtReviewDate(r.validated_at)}` : ""}
                  </p>
                )}
                {tab === "archivio" && (
                  r.archive_deletable ? (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <Button variant="ghost" size="sm" onClick={() => setToDel(r)} data-testid={`anomaly-archive-delete-${key}`}
                        className="text-slate-400 hover:text-destructive hover:bg-destructive/5 h-8 px-2">
                        <Trash2 className="h-4 w-4 mr-1.5" /> Elimina dall'archivio
                      </Button>
                    </div>
                  ) : (
                    <p className="mt-2 text-[11px] text-slate-300" data-testid={`anomaly-archive-locked-${key}`}>Eliminabile dall'archivio dopo 60 giorni</p>
                  )
                )}
                {tab === "valutare" && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                    <Button variant="outline" size="sm" disabled={busy === key} onClick={() => validate(r, true)}
                      data-testid={`validate-${key}`} className="rounded-lg text-success border-success/30 hover:bg-success/5">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Valida (giustifica)
                    </Button>
                    <Button variant="outline" size="sm" disabled={busy === key} onClick={() => validate(r, false)}
                      data-testid={`invalidate-${key}`} className="rounded-lg text-destructive border-destructive/30 hover:bg-destructive/5">
                      Non valida
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ); })}
        </div>
      )}

      <AlertDialog open={!!toDel} onOpenChange={(o) => !o && setToDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="anomaly-archive-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa voce dall'Archivio?</AlertDialogTitle>
            <AlertDialogDescription>
              Viene rimossa solo dalla consultazione dell'Archivio Timbrature. La timbratura e tutti i dati operativi restano invariati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="anomaly-archive-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={archiveDelete} disabled={delBusy} data-testid="anomaly-archive-delete-confirm"
              className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ===================== Calendario (agenda mensile sfogliabile) =====================
const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function romeYM() {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const y = +parts.find((p) => p.type === "year").value;
  const m = +parts.find((p) => p.type === "month").value;
  const d = +parts.find((p) => p.type === "day").value;
  return { year: y, month: m, day: d };
}

function CalendarSection({ onOpenWorkerAgenda }) {
  const today = romeYM();
  const [cur, setCur] = useState({ year: today.year, month: today.month });
  const [notes, setNotes] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = async (y, m) => {
    setNotes(null);
    try {
      const { data } = await api.get(`/office/calendar/absences?year=${y}&month=${m}`);
      setNotes(data.notes || []);
    } catch { setNotes([]); }
  };
  useEffect(() => { load(cur.year, cur.month); /* eslint-disable-next-line */ }, [cur]);

  const prev = () => setCur((c) => c.month === 1 ? { year: c.year - 1, month: 12 } : { year: c.year, month: c.month - 1 });
  const next = () => setCur((c) => c.month === 12 ? { year: c.year + 1, month: 1 } : { year: c.year, month: c.month + 1 });
  const goToday = () => setCur({ year: today.year, month: today.month });

  // Costruzione griglia (lunedì-domenica)
  const firstDow = (() => { const d = new Date(cur.year, cur.month - 1, 1).getDay(); return d === 0 ? 6 : d - 1; })();
  const daysInMonth = new Date(cur.year, cur.month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const notesByDay = {};
  (notes || []).forEach((n) => {
    const day = +n.date.split("-")[2];
    (notesByDay[day] = notesByDay[day] || []).push(n);
  });
  const isToday = (d) => d === today.day && cur.year === today.year && cur.month === today.month;
  const CELL_MAX = 2; // note visibili nella cella mensile; il resto nel dettaglio giorno
  const dayNotes = selectedDay ? (notesByDay[selectedDay] || []) : [];

  return (
    <div data-testid="office-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prev} data-testid="calendar-prev" className="rounded-lg h-9 w-9"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={next} data-testid="calendar-next" className="rounded-lg h-9 w-9"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="calendar-today" className="rounded-lg">Oggi</Button>
        </div>
        <p className="font-heading text-lg font-bold text-slate-900" data-testid="calendar-title">{MONTH_NAMES[cur.month - 1]} {cur.year}</p>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] font-semibold text-slate-400 uppercase tracking-wide py-1">{w}</div>)}
      </div>
      {notes === null ? (
        <p className="text-sm text-slate-500 py-6 text-center">Caricamento…</p>
      ) : (
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {cells.map((d, i) => {
            const dn = d ? (notesByDay[d] || []) : [];
            const extra = dn.length - CELL_MAX;
            return (
              <button key={i} type="button" disabled={!d}
                data-testid={d ? `calendar-day-${d}` : undefined}
                onClick={() => d && setSelectedDay(d)}
                className={`text-left min-h-[92px] rounded-lg border p-1.5 flex flex-col gap-1 transition-all ${d ? "bg-white border-slate-200 hover:border-primary/40 hover:shadow-sm cursor-pointer" : "bg-transparent border-transparent cursor-default"} ${isToday(d) ? "ring-2 ring-primary/50 border-primary/30" : ""}`}>
                {d && <span className={`text-[11px] font-bold ${isToday(d) ? "text-primary" : "text-slate-500"}`}>{d}</span>}
                {d && dn.slice(0, CELL_MAX).map((n, j) => (
                  <div key={j} data-testid={`calendar-note-${d}-${j}`}
                    className={`rounded-md px-1.5 py-1 text-[10px] leading-tight border ${n.causale === "malattia" ? "bg-rose-50 border-rose-200 text-rose-800" : n.causale === "permesso" ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                    <span className="font-semibold block truncate">{n.worker_name}</span>
                    <span className="block truncate">{n.causale_label}{n.start_time ? ` · ${n.start_time}–${n.end_time}` : ""}</span>
                  </div>
                ))}
                {d && extra > 0 && (
                  <span data-testid={`calendar-more-${d}`} className="text-[10px] font-semibold text-primary mt-0.5">+{extra} altre</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-4 mt-4 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-100 border border-emerald-300" /> Ferie</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-sky-100 border border-sky-300" /> Permesso retribuito</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-rose-100 border border-rose-300" /> Malattia</span>
      </div>

      <Dialog open={selectedDay != null} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="sm:max-w-md" data-testid="calendar-day-detail">
          <DialogHeader>
            <DialogTitle className="font-heading" data-testid="calendar-day-detail-title">
              {selectedDay} {MONTH_NAMES[cur.month - 1].toLowerCase()} {cur.year}
            </DialogTitle>
            <DialogDescription>
              {dayNotes.length === 0 ? "Nessuna assenza autorizzata" : `${dayNotes.length} ${dayNotes.length === 1 ? "assenza autorizzata" : "assenze autorizzate"}`}
            </DialogDescription>
          </DialogHeader>
          {dayNotes.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center" data-testid="calendar-day-empty">Nessuna assenza programmata per questo giorno.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="calendar-day-list">
              {dayNotes.map((n, j) => (
                <button key={j} type="button" data-testid={`calendar-day-note-${j}`}
                  onClick={() => {
                    if (!n.worker_id) return;
                    setSelectedDay(null);
                    onOpenWorkerAgenda && onOpenWorkerAgenda({ workerId: n.worker_id, date: n.date });
                  }}
                  className="w-full text-left rounded-xl border border-slate-200 p-3 flex items-start gap-3 hover:border-[#6D5FB8]/50 hover:bg-[#F4F3FA] hover:shadow-sm transition-all">
                  <span className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${n.causale === "malattia" ? "bg-rose-500" : n.causale === "permesso" ? "bg-sky-400" : "bg-emerald-400"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-bold text-slate-900">{n.worker_name}</p>
                    <p className="text-sm text-slate-600 mt-0.5">
                      {n.causale_label}{n.start_time ? ` · ${n.start_time}–${n.end_time}` : ""}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== Operatori in servizio (realtime leggero via polling) =====================
function OperatoriSection({ onCount }) {
  const [ops, setOps] = useState(null);
  const [query, setQuery] = useState("");
  const [appalto, setAppalto] = useState("all");
  const [svc, setSvc] = useState("all");
  const [level3, setLevel3] = useState("all");
  const [appalti, setAppalti] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/office/active-services");
      setOps(data.operators || []);
      onCount && onCount(data.count || 0);
    } catch { setOps([]); }
  };
  useEffect(() => {
    api.get("/my-appalti").then((r) => setAppalti(r.data || [])).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
    /* eslint-disable-next-line */
  }, []);

  const onAppalto = (v) => { setAppalto(v); setSvc("all"); setLevel3("all"); };
  const onSvc = (v) => { setSvc(v); setLevel3("all"); };

  const q = query.trim().toLowerCase();
  const opsArr = ops || [];
  // Appalto → Servizio → Sede/Scuola: opzioni derivate dai soli operatori realmente in servizio (nessuna lista hardcoded).
  const apName = (id) => (appalti.find((a) => a.id === id)?.nome) || id;
  const appaltiPresent = Array.from(new Set(opsArr.map((o) => o.appalto_id).filter(Boolean)));
  const byAppalto = opsArr.filter((o) => appalto === "all" || o.appalto_id === appalto);
  const servicesPresent = Array.from(new Set(byAppalto.map((o) => o.service_type)));
  const bySvc = byAppalto.filter((o) => svc === "all" || o.service_type === svc);
  const level3Map = new Map();
  if (svc !== "all") bySvc.forEach((o) => { if (o.level3) level3Map.set(o.level3.id, o.level3); });
  const level3Opts = Array.from(level3Map.values());
  const showLevel3 = svc !== "all" && level3Opts.length >= 2;
  const level3Label = level3Opts[0]?.kind === "scuola" ? "Scuola" : "Sede operativa";
  const effLevel3 = showLevel3 ? level3 : "all";
  const filtered = bySvc.filter((o) =>
    (effLevel3 === "all" || (o.level3 && o.level3.id === effLevel3)) &&
    (!q || o.worker_name.toLowerCase().includes(q))
  );

  return (
    <div data-testid="office-operatori">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 text-sm font-semibold" data-testid="operatori-count">
            <Radio className="h-4 w-4" /> {(ops || []).length} in servizio
          </span>
          <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" title="Aggiornamento automatico" />
        </div>
      </div>
      {/* Filtri: Appalto → Servizio → Sede operativa/Scuola (stessa cornice/colori delle altre aree filtro) */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4 space-y-3 mb-4" data-testid="operatori-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Appalto</Label>
            <Select value={appalto} onValueChange={onAppalto}>
              <SelectTrigger className="rounded-lg" data-testid="operatori-appalto-filter"><SelectValue placeholder="Tutti gli appalti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli appalti</SelectItem>
                {appaltiPresent.map((id) => <SelectItem key={id} value={id}>{apName(id)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Servizio</Label>
            <Select value={svc} onValueChange={onSvc}>
              <SelectTrigger className="rounded-lg" data-testid="operatori-service-filter"><SelectValue placeholder="Tutti i servizi" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i servizi</SelectItem>
                {servicesPresent.map((s) => <SelectItem key={s} value={s}>{svcLabel(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {showLevel3 && (
            <div data-testid="operatori-place-wrap">
              <Label className="text-xs text-slate-500">{level3Label}</Label>
              <Select value={level3} onValueChange={setLevel3}>
                <SelectTrigger className="rounded-lg" data-testid="operatori-place-filter"><SelectValue placeholder={`Tutte le ${level3Label === "Scuola" ? "scuole" : "sedi"}`} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{level3Label === "Scuola" ? "Tutte le scuole" : "Tutte le sedi"}</SelectItem>
                  {level3Opts.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <SearchBox value={query} onChange={setQuery} placeholder="Cerca per nome o cognome…" testid="operatori-search" />
      </div>
      {ops === null ? (
        <p className="text-sm text-slate-500">Caricamento…</p>
      ) : filtered.length === 0 ? (
        <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="operatori-empty">
          {(ops || []).length === 0 ? "Nessun operatore attualmente in servizio." : "Nessun operatore corrisponde ai filtri."}
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="operatori-list">
          {filtered.map((o) => {
            const luogo = o.entity ? [o.entity.titolo, o.entity.sottotitolo].filter(Boolean).join(" · ") : null;
            return (
              <Card key={o.entry_id} className="rounded-xl border-slate-200 shadow-sm" data-testid={`operatore-${o.entry_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                      <UserCheck className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-slate-900">{o.worker_name}</p>
                      <p className="text-sm text-slate-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 font-semibold text-slate-800"><Clock className="h-3.5 w-3.5 text-primary" /> {o.start_time || "—"}</span>
                        <span className="text-slate-300">·</span>
                        <span>{svcLabel(o.service_type)}{o.role_or_task ? ` (${o.role_or_task})` : ""}</span>
                        {luogo && (<><span className="text-slate-300">·</span><span className="inline-flex items-center gap-1 text-slate-500"><MapPin className="h-3.5 w-3.5" /> {luogo}</span></>)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===================== Consuntivo Libri Presenze (raccoglitore digitale interno) =====================
const fmtDec = (min) => (min ? fmtNum(min / 60) : "");
const ROLES_BY_SERVICE = {
  assistenza_scolastica: null, adm: null, nido: ["Educatore", "Ausiliario"], rest: null, blue_box: null,
  casa_di_riposo: ["ASA / OSS", "Infermiere", "Educatore"],
  pre_scuola: ["Educatore", "Sorvegliante"], post_scuola: ["Educatore", "Sorvegliante"],
  centro_estivo: ["Educatore", "Ausiliario", "Referente"], spazio_compiti: ["Educatore", "Sorvegliante"],
};

const CAUSALE_LABELS_IT = {
  ferie: "Ferie", permesso: "Permesso retribuito", malattia: "Malattia",
  permesso_104: "Permesso Legge 104", diritto_studio: "Diritto allo studio", permesso_lutto: "Permesso per lutto",
  permesso_elettorale: "Permesso per funzioni elettorali", permesso_sindacale: "Permesso sindacale RSU/RSA",
  assemblea_sindacale: "Assemblea sindacale", permesso_non_retribuito: "Permesso non retribuito", congedo: "Congedo",
};
const CAUSALE_SYMBOL_IT = {
  ferie: "F", malattia: "M", permesso_104: "PS", diritto_studio: "DS", permesso_lutto: "PL",
  permesso_elettorale: "FE", permesso_sindacale: "RSU", assemblea_sindacale: "AS", permesso_non_retribuito: "PNR", congedo: "CG",
};
const ASSENZA_MENU = [
  { key: "ferie", label: "Ferie" },
  { key: "malattia", label: "Malattia" },
  { key: "permesso", label: "Permesso retribuito" },
  { key: "__speciali", label: "Permessi speciali", children: [
    { key: "permesso_104", label: "Permesso Legge 104" }, { key: "diritto_studio", label: "Diritto allo studio" },
    { key: "permesso_lutto", label: "Permesso per lutto" }, { key: "permesso_elettorale", label: "Permesso per funzioni elettorali" },
    { key: "permesso_sindacale", label: "Permesso sindacale RSU/RSA" }, { key: "assemblea_sindacale", label: "Assemblea sindacale" },
    { key: "congedo", label: "Congedo" },
  ] },
  { key: "permesso_non_retribuito", label: "Permesso non retribuito (PNR)" },
];

function LibroGrid({ data, onRettifica, onSelectMulti, onInsert, onIndRettifica, onIndSelectMulti, onIndInsert, extraRows = [], onAssenzaInsert, onAddRow }) {
  const { dataset, worker, entries, indirect_entries } = data;
  const ndays = new Date(dataset.year, dataset.month, 0).getDate();
  const days = Array.from({ length: ndays }, (_, i) => i + 1);
  const matrix = dataset.matrix || [];
  let prevSvc = null, zi = 0;
  const ind = dataset.indirect_matrix || [];
  const ferie = dataset.ferie_cells || dataset.ferie_days || {};
  const permesso = dataset.permesso_days || {};
  const legge104 = dataset.legge104_cells || {};
  const ds = dataset.ds_cells || {};
  const lutto = dataset.lutto_cells || {};
  const elett = dataset.elett_cells || {};
  const hasFerie = Object.keys(ferie).length > 0 || (dataset.ferie_total || 0) > 0;
  const hasPerm = Object.keys(permesso).length > 0 || (dataset.permesso_total || 0) > 0;
  const has104 = Object.keys(legge104).length > 0;
  const hasDS = Object.keys(ds).length > 0;
  const hasLutto = Object.keys(lutto).length > 0;
  const hasElett = Object.keys(elett).length > 0;
  const hasSind = Object.keys(dataset.sind_cells || {}).length > 0;
  const hasAssemblea = Object.keys(dataset.assemblea_cells || {}).length > 0;
  const hasMalattia = (dataset.malattia_rows || []).length > 0;
  const hasCongedo = Object.keys(dataset.congedo_cells || {}).length > 0;
  const hasSpeciali = has104 || hasDS || hasLutto || hasElett || hasSind || hasAssemblea || hasCongedo;
  // Tot. Ore = ore lavorate (dirette) + ore indirette. Le assenze e il PNR non concorrono al totale.
  const indirectTotalMin = (ind || []).reduce((s, g) => s + (g.total_minutes || 0), 0);
  const totOreMin = (dataset.total_minutes || 0) + indirectTotalMin;
  const summaryCats = [];
  if (hasFerie) summaryCats.push("Ferie");
  if (hasMalattia) summaryCats.push("Malattia");
  if (hasPerm) summaryCats.push("Permessi retribuiti");
  if (hasSpeciali) summaryCats.push("Permessi speciali");
  const dayNum = (wd) => parseInt(wd.split("-")[2], 10);
  const matchEntries = (m, d) => (entries || []).filter((e) =>
    e.service_type === m.service_type && e.entity_id === m.entity_id && dayNum(e.work_date) === d
    && (m.role ? e.role_or_task === m.role : true));
  const matchInd = (g, d) => (indirect_entries || []).filter((e) => e.indirect_type === g.indirect_type && (e.note || "") === (g.note || "") && dayNum(e.date) === d);
  const onIndCell = (g, d, hasVal) => {
    if (hasVal) {
      const ms = matchInd(g, d);
      if (ms.length === 1) onIndRettifica(ms[0]);
      else if (ms.length > 1) onIndSelectMulti(ms);
    } else {
      const wd = `${dataset.year}-${String(dataset.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      onIndInsert({ date: wd, indirect_type: g.indirect_type });
    }
  };
  const onCell = (m, d, hasVal) => {
    if (hasVal) {
      const ms = matchEntries(m, d);
      if (ms.length === 1) onRettifica(ms[0]);
      else if (ms.length > 1) onSelectMulti(ms);
      // se 0 match (caso limite turno oltre mezzanotte) non si apre nulla
    } else {
      const wd = `${dataset.year}-${String(dataset.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      onInsert({ work_date: wd, service_type: m.service_type, service: m.service, entity_id: m.entity_id,
        entity_kind: m.entity_kind || null, entity_label: m.entity, role: m.role || null });
    }
  };
  const isFestivo = (d) => {
    const wd = new Date(dataset.year, dataset.month - 1, d).getDay();
    if (wd === 0) return true;
    const ds = `${dataset.year}-${String(dataset.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return (dataset.holidays || dataset.festivi || []).includes(ds);
  };
  const dayNumCells = (thBg) => (
    <>
      {days.map((d) => (
        <td key={d} className={`px-1 py-1.5 text-center font-bold ${thBg} ${isFestivo(d) ? "text-red-400" : ""}`}>{d}</td>
      ))}
      <td className={`px-2 py-1.5 text-center font-bold ${thBg}`}>Totale</td>
    </>
  );
  const wdOf = (d) => `${dataset.year}-${String(dataset.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  // Righe placeholder aggiunte manualmente dall'Ufficio ("+ Aggiungi riga"): unite ai blocchi esistenti.
  const extraPrest = (extraRows || []).filter((r) => r.kind === "prestazione");
  const extraInd = (extraRows || []).filter((r) => r.kind === "indirect");
  const extraAssCausali = new Set((extraRows || []).filter((r) => r.kind === "assenza").map((r) => r.causale));
  const mergedInd = [...ind, ...extraInd];
  const LIBRO_ABBR = { centro_estivo: "Campus", casa_di_riposo: "RSA" };
  const svcLabelFor = (st, role) => {
    const cfg = SERVICES[slugFromKey(st)];
    const base = LIBRO_ABBR[st] || (cfg ? cfg.label : st);
    return role ? `${base} - ${role}` : base;
  };
  const extraPrestNorm = extraPrest.map((r) => ({ ...r, service: svcLabelFor(r.service_type, r.role) }));
  const mergedMatrix = [...matrix, ...extraPrestNorm].slice().sort((a, b) =>
    `${a.service}\u0000${a.entity}`.localeCompare(`${b.service}\u0000${b.entity}`, "it"));
  // Definizioni assenze giustificate (riuso di causali/colori esistenti + sindacale/assemblea/PNR).
  const assenzaDefs = [
    { causale: "ferie", label: "Ferie", rowCls: "bg-[#F3F2FA]", labelCls: "text-slate-700", strongCls: "text-[#315766]", cells: ferie, total: dataset.ferie_total },
    { causale: "permesso", label: "Permessi retribuiti", rowCls: "bg-[#F3F2FA]", labelCls: "text-slate-700", strongCls: "text-slate-700", cells: permesso, total: dataset.permesso_total },
    { causale: "permesso_104", label: "Permesso Legge 104", rowCls: "bg-[#EAF2E8]", labelCls: "text-emerald-800", strongCls: "text-emerald-700", cells: legge104, total: dataset.legge104_total },
    { causale: "diritto_studio", label: "Diritto allo studio", rowCls: "bg-[#E8EEF6]", labelCls: "text-[#2F5C7D]", strongCls: "text-[#2F5C7D]", cells: ds, total: dataset.ds_total },
    { causale: "permesso_lutto", label: "Permesso per lutto", rowCls: "bg-[#F3EEE8]", labelCls: "text-[#8a6a4f]", strongCls: "text-[#8a6a4f]", cells: lutto, total: dataset.lutto_total },
    { causale: "permesso_elettorale", label: "Permesso per funzioni elettorali", rowCls: "bg-[#EFEAF6]", labelCls: "text-[#5B4A7D]", strongCls: "text-[#5B4A7D]", cells: elett, total: dataset.elett_total },
    { causale: "permesso_sindacale", label: "Permesso sindacale RSU/RSA", rowCls: "bg-[#EFEAF6]", labelCls: "text-[#5B4A7D]", strongCls: "text-[#5B4A7D]", cells: dataset.sind_cells || {}, total: dataset.sind_total },
    { causale: "assemblea_sindacale", label: "Assemblea sindacale", rowCls: "bg-[#EFEAF6]", labelCls: "text-[#5B4A7D]", strongCls: "text-[#5B4A7D]", cells: dataset.assemblea_cells || {}, total: dataset.assemblea_total },
    { causale: "congedo", label: "Congedo", rowCls: "bg-[#EAF3F3]", labelCls: "text-[#2E6B6B]", strongCls: "text-[#2E6B6B]", cells: dataset.congedo_cells || {}, total: dataset.congedo_total },
    { causale: "permesso_non_retribuito", label: "Permesso non retribuito", rowCls: "bg-[#F1F1F4]", labelCls: "text-slate-600", strongCls: "text-slate-700", cells: dataset.pnr_cells || {}, total: dataset.pnr_total },
  ];
  const assenzaCell = (def, d) => {
    const raw = def.cells ? def.cells[d] : undefined;
    const has = raw !== undefined && raw !== null && raw !== "";
    const content = has ? (typeof raw === "string" ? raw : fmtDec(raw)) : "";
    const clickable = !has && !!onAssenzaInsert;
    return (
      <td key={d} data-testid={`libro-ass-cell-${def.causale}-${d}`}
        onClick={clickable ? () => onAssenzaInsert(def.causale, wdOf(d)) : undefined}
        className={`px-1 py-1 text-center transition-colors ${typeof raw === "string" ? `font-bold ${def.strongCls}` : ""} ${clickable ? "cursor-pointer hover:bg-amber-50 text-slate-300" : ""}`}>
        {content || (clickable ? "·" : "")}
      </td>
    );
  };
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="libro-grid">
      <div className="bg-[#315766] text-white text-center py-2.5 font-heading font-bold text-sm">
        ORBIT — Libro Presenze · {dataset.appalto_nome}
      </div>
      <div className="grid grid-cols-4 bg-[#F7FAFB] border-b border-slate-200 text-[11px]">
        {[["Lavoratore", worker.nome + " " + worker.cognome], ["Mese", MONTH_NAMES[dataset.month - 1] + " " + dataset.year],
          ["Err. timbratura", dataset.stamp_errors ?? 0]].map(([l, v], i) => (
          <div key={i} className="px-3 py-2 border-r border-slate-100 last:border-0">
            <p className="text-slate-400">{l}</p>
            <p className="font-bold text-slate-800 text-sm" data-testid={`libro-summary-${i}`}>{v}</p>
          </div>
        ))}
        <div className="px-3 py-2">
          <p className="text-slate-400">Tot. Ore</p>
          <p className="font-bold text-slate-800 text-sm" data-testid="libro-summary-3">
            {fmtNum(totOreMin / 60) || "0"}
            {summaryCats.length > 0 && (
              <span className="font-semibold text-slate-500" data-testid="libro-summary-causali"> {summaryCats.map((c) => `+ ${c}`).join(" ")}</span>
            )}
          </p>
        </div>
      </div>
      <p className="px-3 py-1.5 text-[10px] text-slate-400 bg-slate-50 border-b border-slate-100">Suggerimento: clicca una cella con ore per <b>rettificare</b>, una cella vuota per <b>inserire</b> una prestazione.</p>
      <div className="overflow-x-auto">
        <table className="text-[11px] border-collapse w-full">
          <tbody>
            {mergedMatrix.map((m, idx) => {
              const showSvc = m.service !== prevSvc;
              if (showSvc) { prevSvc = m.service; }
              zi += 1;
              const bg = zi % 2 ? "bg-white" : "bg-[#EEF5FB]";
              return (
                <>
                  {showSvc && (
                    <tr key={`hdr-${idx}`} className="bg-[#315766] text-white">
                      <td className="px-2 py-1.5 text-left font-bold sticky left-0 bg-[#315766] w-[180px] min-w-[180px]">{m.service}</td>
                      {dayNumCells("bg-[#315766] text-white")}
                    </tr>
                  )}
                  <tr key={idx} className={bg}>
                    <td className={`px-2 py-1 text-slate-700 sticky left-0 ${bg} w-[180px] min-w-[180px]`}>{m.entity}</td>
                    {days.map((d) => {
                      const val = fmtDec(m.days?.[d]);
                      return (
                        <td key={d} onClick={() => onCell(m, d, !!m.days?.[d])}
                          data-testid={`libro-cell-${idx}-${d}`}
                          className={`px-2 py-1.5 text-center cursor-pointer transition-colors ${m.days?.[d] ? "hover:bg-sky-100 font-medium" : "hover:bg-emerald-50 text-slate-300"}`}>
                          {val || "·"}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center font-bold text-slate-800">{fmtDec(m.total_minutes)}</td>
                  </tr>
                </>
              );
            })}
            {mergedInd.length > 0 && (
              <>
                <tr className="bg-[#6E6AA8] text-white" data-testid="libro-ore-indirette">
                  <td className="px-2 py-1.5 text-left font-bold sticky left-0 bg-[#6E6AA8] w-[180px] min-w-[180px]">ORE INDIRETTE</td>
                  {dayNumCells("bg-[#6E6AA8] text-white")}
                </tr>
                {mergedInd.map((g, i) => (
                  <tr key={`ind-${i}`} className="bg-[#F3F2FA]">
                    <td className="px-2 py-1 text-slate-700 sticky left-0 bg-[#F3F2FA] w-[180px] min-w-[180px]">{g.label || g.indirect_type}</td>
                    {days.map((d) => {
                      const val = fmtDec(g.days?.[d]);
                      return (
                        <td key={d} onClick={() => onIndCell(g, d, !!g.days?.[d])}
                          data-testid={`libro-ind-cell-${i}-${d}`}
                          className={`px-2 py-1.5 text-center cursor-pointer transition-colors ${g.days?.[d] ? "hover:bg-violet-100 font-medium" : "hover:bg-violet-50 text-slate-300"}`}>
                          {val || "·"}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-center font-bold text-slate-800">{fmtDec(g.total_minutes)}</td>
                  </tr>
                ))}
              </>
            )}
            {assenzaDefs.map((def) => {
              const present = (def.cells && Object.keys(def.cells).length > 0) || (def.total || 0) > 0;
              if (!present && !extraAssCausali.has(def.causale)) return null;
              return [
                <tr key={`ass-${def.causale}`} className={`${def.rowCls} border-t border-slate-200`} data-testid={`libro-assenza-row-${def.causale}`}>
                  <td className={`px-2 py-1 font-semibold sticky left-0 ${def.rowCls} ${def.labelCls}`}>{def.label}</td>
                  {days.map((d) => assenzaCell(def, d))}
                  <td className="px-2 py-1 text-center font-bold text-slate-800">{fmtDec(def.total)}</td>
                </tr>,
                ...(def.causale === "congedo" ? (dataset.congedo_notes || []).map((n) => (
                  <tr key={`cg-note-${n.request_id}`} className={def.rowCls} data-testid={`libro-congedo-note-${n.request_id}`}>
                    <td colSpan={ndays + 2} className={`px-2 pb-1.5 pt-0 text-[10px] ${def.labelCls}`}>
                      <span className="font-semibold">Nota Congedo ({n.days.length > 1 ? `gg ${n.days[0]}–${n.days[n.days.length - 1]}` : `g ${n.days[0]}`}):</span> {n.note}
                    </td>
                  </tr>
                )) : []),
              ];
            })}
            {(dataset.malattia_rows || []).map((mr, mi) => (
              <tr key={`mal-${mi}`} className="bg-[#FDECEC]" data-testid={`libro-malattia-row-${mi}`}>
                <td className="px-2 py-1 font-semibold text-rose-700 sticky left-0 bg-[#FDECEC]">{mr.label || `Malattia ${mr.index}`}</td>
                {days.map((d) => <td key={d} className="px-1 py-1 text-center font-bold text-rose-700">{mr.days?.[d] ? "M" : ""}</td>)}
                <td className="px-2 py-1 text-center"></td>
              </tr>
            ))}
            {extraAssCausali.has("malattia") && (
              <tr className="bg-[#FDECEC] border-t border-slate-200" data-testid="libro-malattia-row-new">
                <td className="px-2 py-1 font-semibold text-rose-700 sticky left-0 bg-[#FDECEC]">Malattia (nuova)</td>
                {days.map((d) => (
                  <td key={d} data-testid={`libro-ass-cell-malattia-${d}`}
                    onClick={onAssenzaInsert ? () => onAssenzaInsert("malattia", wdOf(d)) : undefined}
                    className="px-1 py-1 text-center cursor-pointer hover:bg-amber-50 text-slate-300">·</td>
                ))}
                <td className="px-2 py-1 text-center"></td>
              </tr>
            )}
            {onAddRow && (
              <tr className="bg-white border-t border-slate-200">
                <td colSpan={ndays + 2} className="px-3 py-2">
                  <button type="button" onClick={onAddRow} data-testid="libro-add-row"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#315766] hover:text-[#20404b] rounded-lg px-2.5 py-1.5 border border-dashed border-[#315766]/30 hover:bg-slate-50 transition-colors">
                    <Plus className="h-3.5 w-3.5" /> Aggiungi riga
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {dataset.legge104_alert?.exceeded && (
        <div className="px-3 py-2.5 bg-amber-50 border-t border-amber-200 flex items-start gap-2" data-testid="libro-legge104-alert">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[11px] text-amber-800 leading-snug">{dataset.legge104_alert.message}</p>
        </div>
      )}
    </div>
  );
}

function ConsuntivoSection({ onChecksChanged, ctx }) {
  const nowRome = romeYM();
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState(ctx?.appaltoId || "");
  const [servicesList, setServicesList] = useState([]);
  const [selServices, setSelServices] = useState([]);
  const [year, setYear] = useState(ctx?.year || nowRome.year);
  const [month, setMonth] = useState(ctx?.month || nowRome.month);
  const [view, setView] = useState("select"); // select | index | libro
  const [meta, setMeta] = useState(null);
  const [idx, setIdx] = useState(0);
  const [libro, setLibro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [insert, setInsert] = useState(null);
  const [pickList, setPickList] = useState(null);
  const [indEdit, setIndEdit] = useState(null);
  const [indInsert, setIndInsert] = useState(null);
  const [indPick, setIndPick] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [checkStatus, setCheckStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [extraRows, setExtraRows] = useState([]);
  const [addRow, setAddRow] = useState(null);
  const [assenzaInsert, setAssenzaInsert] = useState(null);
  const [savingAss, setSavingAss] = useState(false);

  const loadCheckStatus = async () => {
    if (!appaltoId) { setCheckStatus(null); return; }
    try {
      const { data } = await api.get(`/office/consuntivo/check-status?appalto_id=${appaltoId}&year=${year}&month=${month}`);
      setCheckStatus(data);
    } catch { setCheckStatus(null); }
  };
  useEffect(() => { loadCheckStatus(); /* eslint-disable-next-line */ }, [appaltoId, year, month]);
  const checkedSet = new Set((checkStatus?.services || []).filter((s) => s.checked).map((s) => s.key));
  const chipList = (() => {
    const ind = (checkStatus?.services || []).find((s) => s.key === "ore_indirette" && (s.in_month || s.checked));
    if (ind && !servicesList.some((s) => s.key === "ore_indirette"))
      return [...servicesList, { key: "ore_indirette", label: ind.label || "Ore indirette" }];
    return servicesList;
  })();

  const doCheck = async () => {
    if (selServices.length === 0) { toast.error("Seleziona almeno un servizio"); return; }
    setChecking(true);
    try {
      const { data } = await api.post(`/office/consuntivo/check`, { appalto_id: appaltoId, year, month, services: selServices });
      setCheckStatus(data);
      toast.success("Check manuale registrato per i servizi selezionati");
      onChecksChanged && onChecksChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setChecking(false); }
  };

  const doUncheck = async (serviceKey) => {
    try {
      const { data } = await api.post(`/office/consuntivo/uncheck`, { appalto_id: appaltoId, year, month, service_type: serviceKey });
      setCheckStatus(data);
      toast.success("Check rimosso dal servizio");
      onChecksChanged && onChecksChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { api.get("/my-appalti").then((r) => setAppalti(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setAppaltoId(ctx?.appaltoId || "");
    setYear(ctx?.year || nowRome.year);
    setMonth(ctx?.month || nowRome.month);
    setView("select");
    /* eslint-disable-next-line */
  }, [ctx?.appaltoId, ctx?.month, ctx?.year]);
  useEffect(() => {
    setSelServices([]); setServicesList([]);
    if (appaltoId) api.get(`/office/consuntivo/services?appalto_id=${appaltoId}`).then((r) => setServicesList(r.data?.services || [])).catch(() => {});
  }, [appaltoId]);
  const svcParam = () => selServices.join(",");

  const openConsuntivo = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/office/consuntivo/workers?appalto_id=${appaltoId}&year=${year}&month=${month}&services=${svcParam()}`);
      setMeta(data); setView("index"); setIdx(0); setLibro(null);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const openLibro = async (i) => {
    setLoading(true);
    try {
      const w = meta.workers[i];
      const { data } = await api.get(`/office/consuntivo/libro?appalto_id=${appaltoId}&year=${year}&month=${month}&worker_id=${w.id}&services=${svcParam()}`);
      setLibro(data); setIdx(i); setView("libro"); setExtraRows([]);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const assenzaPresent = (ds, causale) => {
    if (causale === "malattia") return (ds.malattia_rows || []).length > 0;
    const map = { ferie: "ferie", permesso: "permesso_days", permesso_104: "legge104_cells",
      diritto_studio: "ds_cells", permesso_lutto: "lutto_cells", permesso_elettorale: "elett_cells",
      permesso_sindacale: "sind_cells", assemblea_sindacale: "assemblea_cells", permesso_non_retribuito: "pnr_cells", congedo: "congedo_cells" };
    const totMap = { ferie: "ferie_total", permesso: "permesso_total", permesso_104: "legge104_total",
      diritto_studio: "ds_total", permesso_lutto: "lutto_total", permesso_elettorale: "elett_total",
      permesso_sindacale: "sind_total", assemblea_sindacale: "assemblea_total", permesso_non_retribuito: "pnr_total", congedo: "congedo_total" };
    const cellsKey = causale === "ferie" ? (ds.ferie_cells ? "ferie_cells" : "ferie_days") : map[causale];
    const cells = ds[cellsKey] || {};
    return Object.keys(cells).length > 0 || (ds[totMap[causale]] || 0) > 0;
  };

  const pruneExtra = (ds) => setExtraRows((prev) => prev.filter((r) => {
    if (r.kind === "prestazione") return !(ds.matrix || []).some((m) => m.service_type === r.service_type && m.entity_id === r.entity_id && (m.role || null) === (r.role || null));
    if (r.kind === "indirect") return !(ds.indirect_matrix || []).some((g) => g.indirect_type === r.indirect_type);
    if (r.kind === "assenza") return !assenzaPresent(ds, r.causale);
    return true;
  }));

  const reloadLibro = async () => {
    const w = meta.workers[idx];
    const { data } = await api.get(`/office/consuntivo/libro?appalto_id=${appaltoId}&year=${year}&month=${month}&worker_id=${w.id}&services=${svcParam()}`);
    setLibro(data);
    pruneExtra(data.dataset);
  };

  const saveAssenzaInsert = async () => {
    const a = assenzaInsert;
    if (!a) return;
    if (a.causale === "malattia" && !(a.puc || "").trim()) { toast.error("Inserisci il numero di protocollo PUC"); return; }
    if (a.causale === "permesso" && !(a.start && a.end)) { toast.error("Indica la fascia oraria (dalle / alle)"); return; }
    if (a.causale === "permesso_non_retribuito" && !(a.note || "").trim()) { toast.error("La nota è obbligatoria per il Permesso non retribuito"); return; }
    setSavingAss(true);
    try {
      await api.post(`/office/leave-entries`, {
        worker_id: meta.workers[idx].id, appalto_id: appaltoId, causale: a.causale, work_date: a.work_date,
        start_time: a.start || null, end_time: a.end || null, puc_number: a.puc || null, note: a.note || null,
      });
      toast.success("Assenza registrata nel consuntivo");
      setAssenzaInsert(null);
      await reloadLibro();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingAss(false); }
  };

  const appaltoNome = (appalti.find((a) => a.id === appaltoId) || {}).nome || "";
  const handleAddPlaceholder = (ph) => {
    const ds = libro?.dataset || {};
    if (ph.kind === "prestazione") {
      const dup = (ds.matrix || []).some((m) => m.service_type === ph.service_type && m.entity_id === ph.entity_id && (m.role || null) === (ph.role || null))
        || extraRows.some((r) => r.kind === "prestazione" && r.service_type === ph.service_type && r.entity_id === ph.entity_id && (r.role || null) === (ph.role || null));
      if (dup) { toast.error("Questa entità è già presente nel consuntivo per questo servizio"); return; }
    }
    if (ph.kind === "indirect") {
      const dup = (ds.indirect_matrix || []).some((g) => g.indirect_type === ph.indirect_type)
        || extraRows.some((r) => r.kind === "indirect" && r.indirect_type === ph.indirect_type);
      if (dup) { toast.error("Questa tipologia di ora indiretta è già presente"); return; }
    }
    if (ph.kind === "assenza") {
      const dup = extraRows.some((r) => r.kind === "assenza" && r.causale === ph.causale);
      if (dup) { setAddRow(null); return; }
    }
    setExtraRows((prev) => [...prev, ph]);
    setAddRow(null);
    toast.success("Riga aggiunta · compila una cella per registrare");
  };

  const saveCorrection = async () => {
    try {
      await api.post(`/work-entries/${edit.entry.id}/correct`, buildCorrectionPayload(edit));
      toast.success("Rettifica salvata · il lavoratore è stato notificato");
      setEdit(null);
      await reloadLibro();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const deletePrestazione = async () => {
    try {
      await api.post(`/work-entries/${edit.entry.id}/cancel`, { from_export: true });
      toast.success("Prestazione eliminata · il lavoratore è stato notificato");
      setEdit(null);
      await reloadLibro();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveInsert = async () => {
    const roles = ROLES_BY_SERVICE[insert.service_type];
    if (!insert.start || !insert.end) { toast.error("Inserisci orario di inizio e fine"); return; }
    if (roles && !insert.role) { toast.error("Seleziona il ruolo/mansione"); return; }
    if (!insert.reason.trim()) { toast.error("La motivazione è obbligatoria"); return; }
    try {
      await api.post(`/office/work-entries`, {
        worker_id: meta.workers[idx].id, service_type: insert.service_type, work_date: insert.work_date,
        start_time: insert.start, end_time: insert.end, entity_id: insert.entity_id,
        entity_kind: insert.entity_kind || null,
        role_or_task: insert.role || null, reason: insert.reason.trim(), with_anomalies: true,
      });
      toast.success("Prestazione inserita · generate 2 anomalie (apertura + chiusura) · lavoratore notificato");
      setInsert(null);
      await reloadLibro();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveIndEdit = async () => {
    const h = Number(indEdit.hours);
    if (!h || h <= 0) { toast.error("Indica il numero di ore"); return; }
    try {
      await api.patch(`/office/indirect-hours/${indEdit.entry.id}`, {
        appalto_id: appaltoId, date: indEdit.entry.date, indirect_type: indEdit.entry.indirect_type, hours: h,
      });
      toast.success("Ora indiretta aggiornata · lavoratore notificato");
      setIndEdit(null); await reloadLibro();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteIndEntry = async () => {
    try {
      await api.post(`/office/indirect-hours/${indEdit.entry.id}/cancel`, { from_export: true });
      toast.success("Ora indiretta eliminata · lavoratore notificato");
      setIndEdit(null); await reloadLibro();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveIndInsert = async () => {
    const h = Number(indInsert.hours);
    if (!h || h <= 0) { toast.error("Indica il numero di ore"); return; }
    try {
      await api.post(`/office/indirect-hours`, {
        worker_id: meta.workers[idx].id, appalto_id: appaltoId, date: indInsert.date,
        indirect_type: indInsert.indirect_type, hours: h,
      });
      toast.success("Ora indiretta inserita · lavoratore notificato");
      setIndInsert(null); await reloadLibro();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/office/consuntivo/excel?appalto_id=${appaltoId}&year=${year}&month=${month}&services=${svcParam()}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `Consuntivo_${year}_${month}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); } finally { setDownloading(false); }
  };

  if (view === "select") {
    const years = [nowRome.year - 1, nowRome.year, nowRome.year + 1];
    return (
      <div data-testid="consuntivo-select" className="max-w-xl">
        <p className="text-sm text-slate-600 mb-4">Seleziona i <b>servizi</b> da includere nel Consuntivo Libri Presenze.</p>
        <div className="space-y-4">
          {appaltoId && (
            <div data-testid="consuntivo-services">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">Servizi ({selServices.length} selezionati)</Label>
                {chipList.length > 0 && (
                  <button type="button" data-testid="consuntivo-services-all"
                    onClick={() => setSelServices(selServices.length === chipList.length ? [] : chipList.map((s) => s.key))}
                    className="text-xs font-semibold text-primary hover:underline">
                    {selServices.length === chipList.length ? "Deseleziona tutti" : "Seleziona tutti"}
                  </button>
                )}
              </div>
              {chipList.length === 0 ? (
                <p className="text-xs text-slate-400 mt-1">Nessun servizio configurato per questo appalto.</p>
              ) : (
                <>
                <div className="flex flex-wrap gap-2 mt-2">
                  {chipList.map((s) => {
                    const on = selServices.includes(s.key);
                    const checked = checkedSet.has(s.key);
                    let cls = "bg-white text-slate-600 border-slate-300 hover:border-primary/50";
                    if (on) cls = "bg-primary text-white border-primary";
                    else if (checked) cls = "bg-emerald-50 text-emerald-700 border-emerald-300 hover:border-emerald-400";
                    return (
                      <button key={s.key} type="button" data-testid={`consuntivo-service-${s.key}`}
                        data-checked={checked ? "1" : "0"}
                        onClick={() => setSelServices(on ? selServices.filter((x) => x !== s.key) : [...selServices, s.key])}
                        className={`text-xs rounded-full px-3 py-1.5 border transition-colors inline-flex items-center gap-1 ${cls}`}>
                        {checked && <Check className={`h-3.5 w-3.5 ${on ? "text-emerald-200" : "text-emerald-600"}`} data-testid={`consuntivo-service-check-${s.key}`} />}
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                {checkedSet.size > 0 && (
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="consuntivo-checked-list">
                    <p className="text-[11px] font-semibold text-emerald-700 mb-1.5 inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Servizi controllati ({MONTH_NAMES[month - 1]} {year})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(checkStatus?.services || []).filter((s) => s.checked).map((s) => (
                        <span key={s.key} className="inline-flex items-center gap-1 text-[11px] bg-white border border-emerald-200 text-emerald-700 rounded-full pl-2 pr-1 py-0.5" data-testid={`consuntivo-checked-${s.key}`}>
                          {s.label}
                          <button type="button" onClick={() => doUncheck(s.key)} data-testid={`consuntivo-uncheck-${s.key}`}
                            title="Rimuovi il check da questo servizio"
                            className="rounded-full hover:bg-emerald-100 p-0.5 text-emerald-600"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
          )}
          <Button disabled={!appaltoId || selServices.length === 0 || loading} onClick={openConsuntivo} data-testid="consuntivo-open" className="rounded-lg">
            {loading ? "Apertura…" : "Apri Consuntivo"}
          </Button>
        </div>
      </div>
    );
  }

  if (view === "index") {
    return (
      <div data-testid="consuntivo-index">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <button onClick={() => setView("select")} data-testid="consuntivo-back-select" className="text-xs text-primary inline-flex items-center gap-1 mb-1"><ArrowLeft className="h-3.5 w-3.5" /> Cambia appalto/mese</button>
            <h3 className="font-heading text-lg font-bold text-slate-900">Indice · {meta.appalto_nome} · {MONTH_NAMES[month - 1]} {year}</h3>
            {selServices.length > 0 && (
              <p className="text-xs text-slate-500 mt-0.5" data-testid="consuntivo-check-hint">
                {selServices.filter((k) => checkedSet.has(k)).length}/{selServices.length} servizi selezionati già controllati
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={doCheck} disabled={checking || selServices.length === 0}
              data-testid="consuntivo-check-btn"
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
              <Check className="h-4 w-4 mr-2" /> {checking ? "Salvataggio…" : "Check manuale effettuato"}
            </Button>
            <Button variant="outline" onClick={downloadExcel} disabled={downloading || meta.workers.length === 0} data-testid="consuntivo-download" className="rounded-lg">
              <Download className="h-4 w-4 mr-2" /> {downloading ? "Generazione…" : "Scarica Consuntivo Excel"}
            </Button>
          </div>
        </div>
        {meta.workers.length === 0 ? (
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="consuntivo-empty">Nessun lavoratore con prestazioni per questo appalto e mese.</CardContent></Card>
        ) : (
          <div className="rounded-xl border border-slate-200 divide-y" data-testid="consuntivo-worker-list">
            {meta.workers.map((w, i) => (
              <button key={w.id} onClick={() => openLibro(i)} data-testid={`consuntivo-worker-${i}`}
                className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <span className="font-medium text-slate-800">{w.cognome} {w.nome}</span>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const total = meta.workers.length;
  return (
    <div data-testid="consuntivo-libro">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => openLibro(idx - 1)} disabled={idx <= 0 || loading} data-testid="consuntivo-prev" className="rounded-lg"><ChevronLeft className="h-4 w-4 mr-1" /> Precedente</Button>
          <span className="text-sm font-semibold text-slate-600" data-testid="consuntivo-position">{idx + 1} / {total}</span>
          <Button variant="outline" size="sm" onClick={() => openLibro(idx + 1)} disabled={idx >= total - 1 || loading} data-testid="consuntivo-next">Successivo <ChevronRight className="h-4 w-4 ml-1" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setView("index")} data-testid="consuntivo-index-btn" className="rounded-lg"><BookOpen className="h-4 w-4 mr-1" /> Indice</Button>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={doCheck} disabled={checking || selServices.length === 0}
            data-testid="consuntivo-check-btn-2"
            className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
            <Check className="h-4 w-4 mr-2" /> {checking ? "Salvataggio…" : "Check manuale effettuato"}
          </Button>
          <Button variant="outline" onClick={downloadExcel} disabled={downloading} data-testid="consuntivo-download-2" className="rounded-lg">
            <Download className="h-4 w-4 mr-2" /> {downloading ? "Generazione…" : "Scarica Consuntivo Excel"}
          </Button>
        </div>
      </div>
      {libro && <LibroGrid data={libro}
        onRettifica={(e) => setEdit({ entry: e, start: e.start_time, end: e.end_time || "", reason: "" })}
        onSelectMulti={(list) => setPickList(list)}
        onInsert={(ctx) => setInsert({ ...ctx, start: "", end: "", role: ctx.role || "", reason: "" })}
        onIndRettifica={(e) => setIndEdit({ entry: e, hours: e.duration_minutes ? String(e.duration_minutes / 60) : "" })}
        onIndSelectMulti={(list) => setIndPick(list)}
        onIndInsert={(ctx) => setIndInsert({ ...ctx, hours: "" })}
        extraRows={extraRows}
        onAssenzaInsert={(causale, work_date) => setAssenzaInsert({ causale, work_date, start: "", end: "", puc: "", note: "" })}
        onAddRow={() => setAddRow({ step: 1 })} />}
      {libro && (
        <div className="mt-3">
          <LibroNotes notes={libro.dataset?.libro_notes} editor="office" testidPrefix="office-libro-notes"
            onSave={async (text) => {
              try {
                const { data } = await api.put("/office/libro-notes", { worker_id: libro.worker.id, appalto_id: libro.appalto_id, year: libro.year, month: libro.month, note: text });
                setLibro((l) => ({ ...l, dataset: { ...l.dataset, libro_notes: data } }));
                toast.success("Note ufficio salvate");
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} />
        </div>
      )}
      {libro && (
        <div className="mt-5">
          <h4 className="font-heading font-bold text-slate-800 mb-2 text-sm">Prestazioni del mese <span className="text-slate-400 font-normal">· rettifica interna (audit + notifica al lavoratore)</span></h4>
          <div className="rounded-xl border border-slate-200 divide-y" data-testid="consuntivo-entries">
            {libro.entries.length === 0 && <p className="p-4 text-sm text-slate-500">Nessuna prestazione da rettificare.</p>}
            {libro.entries.map((e) => (
              <div key={e.id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`consuntivo-entry-${e.id}`}>
                <div className="text-sm min-w-0">
                  <span className="font-semibold text-slate-800">{e.work_date}</span>
                  <span className="text-slate-500"> · {svcLabel(e.service_type)} · {e.entity?.titolo}</span>
                  <span className="text-slate-700"> · {e.start_time}–{e.end_time || "—"}</span>
                  {e.corretta && <Badge className="ml-2 bg-amber-100 text-amber-700 border-0 text-[10px]">rettificata</Badge>}
                </div>
                <Button variant="outline" size="sm" className="rounded-lg shrink-0" data-testid={`consuntivo-edit-${e.id}`}
                  onClick={() => setEdit({ entry: e, start: e.start_time, end: e.end_time || "", reason: "" })}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Rettifica
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={edit != null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="consuntivo-edit-dialog">
          <DialogHeader>
            <DialogTitle>Rettifica prestazione</DialogTitle>
            <DialogDescription>{edit?.entry?.work_date} · {edit && svcLabel(edit.entry.service_type)} · {edit?.entry?.entity?.titolo}</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1"><Label className="text-xs text-slate-500">Inizio</Label><TimePicker value={edit.start} onChange={(v) => setEdit({ ...edit, start: v })} /></div>
                <div className="flex-1"><Label className="text-xs text-slate-500">Fine</Label><TimePicker value={edit.end} onChange={(v) => setEdit({ ...edit, end: v })} /></div>
              </div>
              <EntryRoleSiteCorrection entry={edit.entry} corr={edit.corr} onChange={(c) => setEdit((e) => ({ ...e, corr: c }))} />
              <div>
                <Label className="text-xs text-slate-500">Motivazione (facoltativa)</Label>
                <Textarea value={edit.reason} onChange={(ev) => setEdit({ ...edit, reason: ev.target.value })} data-testid="consuntivo-edit-reason" placeholder="Motivo della rettifica (facoltativo)" rows={2} />
              </div>
              <p className="text-[11px] text-slate-400">Il valore originale resta tracciato nello storico; il lavoratore riceverà una notifica.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={deletePrestazione} data-testid="consuntivo-edit-delete"
              className="text-destructive border-destructive/30 hover:bg-destructive/5 mr-auto">Elimina prestazione</Button>
            <Button variant="ghost" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveCorrection} disabled={correctionIncomplete(edit)} data-testid="consuntivo-edit-save">Salva rettifica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pickList != null} onOpenChange={(o) => !o && setPickList(null)}>
        <DialogContent data-testid="consuntivo-pick-dialog">
          <DialogHeader>
            <DialogTitle>Scegli la prestazione</DialogTitle>
            <DialogDescription>Più prestazioni in questo giorno: seleziona quella da rettificare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(pickList || []).map((e) => (
              <button key={e.id} data-testid={`consuntivo-pick-${e.id}`}
                onClick={() => { setPickList(null); setEdit({ entry: e, start: e.start_time, end: e.end_time || "", reason: "" }); }}
                className="w-full text-left rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 text-sm">
                <b>{e.start_time}–{e.end_time || "—"}</b> · {svcLabel(e.service_type)} · {e.entity?.titolo}{e.role_or_task ? ` (${e.role_or_task})` : ""}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={insert != null} onOpenChange={(o) => !o && setInsert(null)}>
        <DialogContent data-testid="consuntivo-insert-dialog">
          <DialogHeader>
            <DialogTitle>Inserisci prestazione</DialogTitle>
            <DialogDescription>{insert?.work_date} · {insert && svcLabel(insert.service_type)} · {insert?.entity_label}</DialogDescription>
          </DialogHeader>
          {insert && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="insert-context">
                Lavoratore: <b>{meta.workers[idx].cognome} {meta.workers[idx].nome}</b> · Giorno: <b>{insert.work_date}</b> · Servizio: <b>{svcLabel(insert.service_type)}</b> · Entità: <b>{insert.entity_label}</b>
              </div>
              {ROLES_BY_SERVICE[insert.service_type] && !insert.role && (
                <div>
                  <Label className="text-xs text-slate-500">Ruolo / mansione</Label>
                  <Select value={insert.role} onValueChange={(v) => setInsert({ ...insert, role: v })}>
                    <SelectTrigger className="rounded-lg" data-testid="insert-role"><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
                    <SelectContent>{ROLES_BY_SERVICE[insert.service_type].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex gap-3">
                <div className="flex-1"><Label className="text-xs text-slate-500">Inizio dichiarato</Label><TimePicker value={insert.start} onChange={(v) => setInsert({ ...insert, start: v })} /></div>
                <div className="flex-1"><Label className="text-xs text-slate-500">Fine dichiarata</Label><TimePicker value={insert.end} onChange={(v) => setInsert({ ...insert, end: v })} /></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Motivazione (obbligatoria)</Label>
                <Textarea value={insert.reason} onChange={(ev) => setInsert({ ...insert, reason: ev.target.value })} data-testid="insert-reason" placeholder="Motivo dell'inserimento" rows={2} />
              </div>
              <p className="text-[11px] text-amber-600">Verranno generate 2 anomalie (mancata timbratura di apertura e di chiusura), da valutare nel Controllo timbrature. Nessuna timbratura reale viene simulata.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInsert(null)}>Annulla</Button>
            <Button onClick={saveInsert} data-testid="consuntivo-insert-save">Inserisci prestazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={indPick != null} onOpenChange={(o) => !o && setIndPick(null)}>
        <DialogContent data-testid="consuntivo-ind-pick-dialog">
          <DialogHeader>
            <DialogTitle>Scegli la registrazione</DialogTitle>
            <DialogDescription>Più ore indirette in questo giorno: seleziona quella da modificare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(indPick || []).map((e) => (
              <button key={e.id} data-testid={`consuntivo-ind-pick-${e.id}`}
                onClick={() => { setIndPick(null); setIndEdit({ entry: e, hours: e.duration_minutes ? String(e.duration_minutes / 60) : "" }); }}
                className="w-full text-left rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 text-sm">
                <b>{e.duration_minutes ? (e.duration_minutes / 60) + " h" : ""}</b> · {e.indirect_type}{e.note ? ` ${e.note}` : ""}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={indEdit != null} onOpenChange={(o) => !o && setIndEdit(null)}>
        <DialogContent data-testid="consuntivo-ind-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica ora indiretta</DialogTitle>
            <DialogDescription>{indEdit?.entry?.date} · {indEdit?.entry?.indirect_type}</DialogDescription>
          </DialogHeader>
          {indEdit && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Numero di ore</Label>
                <Input type="number" step="0.5" min="0" value={indEdit.hours}
                  onChange={(e) => setIndEdit({ ...indEdit, hours: e.target.value })}
                  data-testid="consuntivo-ind-edit-hours" placeholder="Es. 3" />
              </div>
              <p className="text-[11px] text-slate-400">Ore indirette: nessuna timbratura né anomalia. Il valore originale resta tracciato; il lavoratore riceverà una notifica.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={deleteIndEntry} data-testid="consuntivo-ind-delete"
              className="text-destructive border-destructive/30 hover:bg-destructive/5 mr-auto">Elimina</Button>
            <Button variant="ghost" onClick={() => setIndEdit(null)}>Annulla</Button>
            <Button onClick={saveIndEdit} data-testid="consuntivo-ind-edit-save">Salva modifica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={indInsert != null} onOpenChange={(o) => !o && setIndInsert(null)}>
        <DialogContent data-testid="consuntivo-ind-insert-dialog">
          <DialogHeader>
            <DialogTitle>Inserisci ora indiretta</DialogTitle>
            <DialogDescription>{indInsert?.date} · {indInsert?.indirect_type}</DialogDescription>
          </DialogHeader>
          {indInsert && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                Lavoratore: <b>{meta.workers[idx].cognome} {meta.workers[idx].nome}</b> · Giorno: <b>{indInsert.date}</b> · Causale: <b>{indInsert.indirect_type}</b>
              </div>
              <div className="flex gap-3">
                <div className="flex-1"><Label className="text-xs text-slate-500">Numero di ore</Label>
                  <Input type="number" step="0.5" min="0" value={indInsert.hours}
                    onChange={(e) => setIndInsert({ ...indInsert, hours: e.target.value })}
                    data-testid="consuntivo-ind-insert-hours" placeholder="Es. 3" /></div>
              </div>
              <p className="text-[11px] text-slate-400">Ore indirette: nessuna timbratura, nessuna anomalia, nessun errore di timbratura.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIndInsert(null)}>Annulla</Button>
            <Button onClick={saveIndInsert} data-testid="consuntivo-ind-insert-save">Inserisci</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {addRow && (
        <AddRowDialog appaltoId={appaltoId} appaltoNome={appaltoNome} servicesList={servicesList}
          onClose={() => setAddRow(null)} onAdd={handleAddPlaceholder} />
      )}

      <Dialog open={assenzaInsert != null} onOpenChange={(o) => !o && setAssenzaInsert(null)}>
        <DialogContent data-testid="consuntivo-assenza-insert-dialog">
          <DialogHeader>
            <DialogTitle>Registra assenza</DialogTitle>
            <DialogDescription>{assenzaInsert && (CAUSALE_LABELS_IT[assenzaInsert.causale] || assenzaInsert.causale)} · {assenzaInsert?.work_date}</DialogDescription>
          </DialogHeader>
          {assenzaInsert && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                Lavoratore: <b>{meta.workers[idx].cognome} {meta.workers[idx].nome}</b> · Giorno: <b>{assenzaInsert.work_date}</b> · Causale: <b>{CAUSALE_LABELS_IT[assenzaInsert.causale] || assenzaInsert.causale}</b>
              </div>
              {assenzaInsert.causale === "malattia" ? (
                <div>
                  <Label className="text-xs text-slate-500">Numero protocollo PUC (da 8 a 13 cifre)</Label>
                  <Input value={assenzaInsert.puc} onChange={(e) => setAssenzaInsert({ ...assenzaInsert, puc: e.target.value })}
                    data-testid="assenza-puc" placeholder="Es. 12345678" maxLength={13} inputMode="numeric" />
                </div>
              ) : (
                <div>
                  <div className="flex gap-3">
                    <div className="flex-1"><Label className="text-xs text-slate-500">Dalle {assenzaInsert.causale === "permesso" ? "(obbligatorio)" : "(facoltativo)"}</Label>
                      <TimePicker value={assenzaInsert.start} onChange={(v) => setAssenzaInsert({ ...assenzaInsert, start: v })} /></div>
                    <div className="flex-1"><Label className="text-xs text-slate-500">Alle {assenzaInsert.causale === "permesso" ? "(obbligatorio)" : "(facoltativo)"}</Label>
                      <TimePicker value={assenzaInsert.end} onChange={(v) => setAssenzaInsert({ ...assenzaInsert, end: v })} /></div>
                  </div>
                  {assenzaInsert.causale !== "permesso" && (
                    <p className="text-[11px] text-slate-400 mt-1">Senza orario = giornata intera ({CAUSALE_SYMBOL_IT[assenzaInsert.causale] || "—"}), senza attribuzione di ore.</p>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Nota {assenzaInsert.causale === "permesso_non_retribuito" ? "(obbligatoria)" : "(facoltativa)"}</Label>
                <Textarea value={assenzaInsert.note} onChange={(e) => setAssenzaInsert({ ...assenzaInsert, note: e.target.value })}
                  data-testid="assenza-note" rows={2} placeholder="Nota" />
              </div>
              <p className="text-[11px] text-amber-600">Registrata dall'Ufficio come già approvata: nessuna richiesta/notifica verso il Lavoratore.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssenzaInsert(null)}>Annulla</Button>
            <Button onClick={saveAssenzaInsert} disabled={savingAss} data-testid="assenza-save">{savingAss ? "Salvataggio…" : "Registra assenza"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function AddRowDialog({ appaltoId, appaltoNome, servicesList, onClose, onAdd }) {
  const [step, setStep] = useState("type");
  const [svcKey, setSvcKey] = useState("");
  const [role, setRole] = useState("");
  const [entities, setEntities] = useState(null);
  const [entQuery, setEntQuery] = useState("");
  const [entLoading, setEntLoading] = useState(false);
  const [indTypes, setIndTypes] = useState([]);
  const [assParent, setAssParent] = useState(null);

  const cfg = svcKey ? SERVICES[slugFromKey(svcKey)] : null;
  const roles = cfg ? cfg.roles : null;
  const kindKey = cfg ? effEntityKindKey(cfg, role, svcKey) : null;
  const entKind = kindKey ? ENTITY_KINDS[kindKey] : null;
  const isAppaltoKind = (entKind && entKind.backendKind === "appalto") || (isCasaAsa(svcKey, role) && Array.isArray(entities) && entities.length === 0);
  const needRole = !!roles;
  const roleReady = !needRole || !!role;

  useEffect(() => {
    if (step !== "indirect") return;
    api.get("/indirect-types").then((r) => setIndTypes(r.data || [])).catch(() => setIndTypes([]));
  }, [step]);

  useEffect(() => {
    if (step !== "prestazione" || !entKind || isAppaltoKind || !roleReady) return;
    setEntLoading(true); setEntities(null);
    api.get(entKind.adminApi()).then((r) => {
      let list = r.data || [];
      if (entKind.backendKind === "operative_site") list = list.filter((d) => (d.service_types || []).includes(svcKey));
      list = list.filter((d) => !d.appalto_id || d.appalto_id === appaltoId);
      setEntities(list);
    }).catch(() => setEntities([])).finally(() => setEntLoading(false));
    /* eslint-disable-next-line */
  }, [step, kindKey, role, svcKey, roleReady]);

  const addPrestazione = (ent) => {
    onAdd({ kind: "prestazione", service_type: svcKey, role: needRole ? role : null,
      entity_id: isAppaltoKind ? appaltoId : ent.id, entity: isAppaltoKind ? appaltoNome : entKind.title(ent),
      entity_kind: isAppaltoKind ? "appalto" : (entKind?.backendKind || null),
      service: "", days: {}, total_minutes: 0 });
  };

  const q = entQuery.trim().toLowerCase();
  const filteredEnts = (entities || []).filter((d) => !q || `${entKind.title(d)} ${entKind.subtitle(d)}`.toLowerCase().includes(q));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-row-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aggiungi riga al consuntivo</DialogTitle>
          <DialogDescription>Scegli il tipo di riga; poi seleziona le opzioni già disponibili nel contesto.</DialogDescription>
        </DialogHeader>

        {step === "type" && (
          <div className="grid gap-2">
            <button data-testid="add-row-prestazione" onClick={() => setStep("prestazione")} className="text-left rounded-lg border border-slate-200 px-4 py-3 hover:border-[#315766]/40 hover:bg-slate-50">
              <p className="font-semibold text-slate-800 text-sm">Prestazione</p><p className="text-xs text-slate-500">Servizio + entità (utente/gruppo/sede) del contesto</p>
            </button>
            <button data-testid="add-row-indiretta" onClick={() => setStep("indirect")} className="text-left rounded-lg border border-slate-200 px-4 py-3 hover:border-[#6E6AA8]/50 hover:bg-slate-50">
              <p className="font-semibold text-slate-800 text-sm">Ora indiretta</p><p className="text-xs text-slate-500">Tipologia di ora indiretta</p>
            </button>
            <button data-testid="add-row-assenza" onClick={() => setStep("assenza")} className="text-left rounded-lg border border-slate-200 px-4 py-3 hover:border-amber-300 hover:bg-slate-50">
              <p className="font-semibold text-slate-800 text-sm">Assenza giustificata</p><p className="text-xs text-slate-500">Ferie, Malattia, Permessi retribuiti, Permessi speciali, PNR</p>
            </button>
          </div>
        )}

        {step === "prestazione" && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Servizio</Label>
              <Select value={svcKey} onValueChange={(v) => { setSvcKey(v); setRole(""); setEntities(null); }}>
                <SelectTrigger data-testid="add-row-service"><SelectValue placeholder="Scegli il servizio" /></SelectTrigger>
                <SelectContent>{servicesList.filter((s) => s.key !== "ore_indirette").map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {cfg && needRole && (
              <div>
                <Label className="text-xs text-slate-500">Ruolo / mansione</Label>
                <Select value={role} onValueChange={(v) => { setRole(v); setEntities(null); }}>
                  <SelectTrigger data-testid="add-row-role"><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {cfg && roleReady && isAppaltoKind && (
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm text-slate-600 mb-2">Servizio senza sede/utente: la riga sarà associata all'appalto <b>{appaltoNome}</b>.</p>
                <Button onClick={() => addPrestazione(null)} data-testid="add-row-confirm-appalto" className="rounded-lg">Aggiungi riga</Button>
              </div>
            )}
            {cfg && roleReady && !isAppaltoKind && entKind && (
              <div>
                <Label className="text-xs text-slate-500">{entKind.hint || "Seleziona l'entità"}</Label>
                <SearchBox value={entQuery} onChange={setEntQuery} placeholder={entKind.searchPlaceholder || "Cerca…"} testid="add-row-entity-search" />
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y" data-testid="add-row-entity-list">
                  {entLoading && <p className="p-3 text-sm text-slate-500">Caricamento…</p>}
                  {!entLoading && filteredEnts.length === 0 && <p className="p-3 text-sm text-slate-500">{entKind.emptyMessage || "Nessuna entità disponibile."}</p>}
                  {!entLoading && filteredEnts.map((d) => (
                    <button key={d.id} data-testid={`add-row-entity-${d.id}`} onClick={() => addPrestazione(d)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                      <span className="font-semibold text-slate-800">{entKind.title(d)}</span>
                      <span className="text-slate-500 block text-xs">{entKind.subtitle(d)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === "indirect" && (
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Tipologia ora indiretta</Label>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y" data-testid="add-row-indtype-list">
              {indTypes.length === 0 && <p className="p-3 text-sm text-slate-500">Caricamento…</p>}
              {indTypes.map((t) => (
                <button key={t} data-testid={`add-row-indtype-${t}`} onClick={() => onAdd({ kind: "indirect", indirect_type: t, label: t, note: null, days: {}, total_minutes: 0 })}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm font-medium text-slate-800">{t}</button>
              ))}
            </div>
          </div>
        )}

        {step === "assenza" && (
          <div className="space-y-2">
            {!assParent ? (
              <div className="grid gap-2" data-testid="add-row-assenza-list">
                {ASSENZA_MENU.map((m) => (
                  <button key={m.key} data-testid={`add-row-assenza-${m.key}`}
                    onClick={() => m.children ? setAssParent(m.key) : onAdd({ kind: "assenza", causale: m.key })}
                    className="text-left rounded-lg border border-slate-200 px-4 py-2.5 hover:bg-slate-50 text-sm font-medium text-slate-800 flex items-center justify-between">
                    {m.label}{m.children && <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid gap-2" data-testid="add-row-assenza-speciali">
                <button onClick={() => setAssParent(null)} className="text-xs text-primary inline-flex items-center gap-1 mb-1"><ArrowLeft className="h-3.5 w-3.5" /> Indietro</button>
                {ASSENZA_MENU.find((m) => m.key === "__speciali").children.map((c) => (
                  <button key={c.key} data-testid={`add-row-assenza-${c.key}`} onClick={() => onAdd({ kind: "assenza", causale: c.key })}
                    className="text-left rounded-lg border border-slate-200 px-4 py-2.5 hover:bg-slate-50 text-sm font-medium text-slate-800">{c.label}</button>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step !== "type" && <Button variant="ghost" onClick={() => { setStep("type"); setAssParent(null); }} className="mr-auto">Cambia tipo</Button>}
          <Button variant="ghost" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SpecificaSostituzioneForm({ onPublished }) {
  const [appalti, setAppalti] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [svcKey, setSvcKey] = useState("");
  const [role, setRole] = useState("");
  const [entId, setEntId] = useState("");
  const [entities, setEntities] = useState([]);
  const [entLoading, setEntLoading] = useState(false);
  const [absentId, setAbsentId] = useState("");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/my-appalti").then((r) => setAppalti(r.data || [])).catch(() => setAppalti([]));
    api.get("/office/matchmaking/list").then((r) => setWorkers(r.data.workers || [])).catch(() => setWorkers([]));
  }, []);

  const appalto = appalti.find((a) => a.id === appaltoId);
  const svcOptions = (appalto?.servizi || []).filter((s) => s.stato === "attivo")
    .map((s) => ({ key: s.slug, label: SERVICES[slugFromKey(s.slug)]?.label || s.nome || s.slug }));
  const cfg = svcKey ? SERVICES[slugFromKey(svcKey)] : null;
  const roles = cfg ? cfg.roles : null;
  const needRole = !!roles;
  const kindKey = cfg ? effEntityKindKey(cfg, role, svcKey) : null;
  const entKind = kindKey ? ENTITY_KINDS[kindKey] : null;
  const isAppaltoKind = (entKind && entKind.backendKind === "appalto") || (isCasaAsa(svcKey, role) && Array.isArray(entities) && entities.length === 0);
  const roleReady = !needRole || !!role;
  const workerOptions = workers.filter((w) => (w.appalto_ids || []).includes(appaltoId));

  useEffect(() => {
    if (!svcKey || !entKind || isAppaltoKind || !roleReady) { setEntities([]); return; }
    setEntLoading(true);
    api.get(entKind.adminApi()).then((r) => {
      let list = r.data || [];
      if (entKind.backendKind === "operative_site" || entKind.backendKind === "school_user")
        list = list.filter((d) => !d.service_types || d.service_types.includes(svcKey));
      list = list.filter((d) => !d.appalto_id || d.appalto_id === appaltoId);
      setEntities(list);
    }).catch(() => setEntities([])).finally(() => setEntLoading(false));
    /* eslint-disable-next-line */
  }, [svcKey, kindKey, role, appaltoId, roleReady]);

  const resetSvc = () => { setSvcKey(""); setRole(""); setEntId(""); setEntities([]); };
  const canPublish = appaltoId && svcKey && roleReady && (isAppaltoKind || !entKind || !!entId)
    && absentId && date && start && end && !saving;

  const publish = async () => {
    setSaving(true);
    try {
      const entObj = entities.find((e) => e.id === entId);
      const payload = {
        appalto_id: appaltoId, service: svcKey, role: needRole ? role : null,
        entity_id: isAppaltoKind ? null : (entId || null),
        entity_kind: isAppaltoKind ? "appalto" : (entKind?.backendKind || null),
        entity_label: isAppaltoKind ? (appalto?.nome || "Appalto") : (entObj ? entKind.title(entObj) : null),
        absent_worker_id: absentId, date, start_time: start, end_time: end, note: note.trim() || null,
      };
      await api.post("/office/substitutions", payload);
      toast.success("Sostituzione pubblicata · stato Da coprire");
      resetSvc(); setAbsentId(""); setDate(""); setStart(""); setEnd(""); setNote("");
      onPublished && onPublished();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6" data-testid="specifica-sostituzione">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Appalto</Label>
              <Select value={appaltoId} onValueChange={(v) => { setAppaltoId(v); resetSvc(); setAbsentId(""); }}>
                <SelectTrigger className="rounded-lg" data-testid="sub-appalto"><SelectValue placeholder="Seleziona appalto" /></SelectTrigger>
                <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {appaltoId && (
              <div>
                <Label className="text-xs text-slate-500">Servizio</Label>
                <Select value={svcKey} onValueChange={(v) => { setSvcKey(v); setRole(""); setEntId(""); }}>
                  <SelectTrigger className="rounded-lg" data-testid="sub-service"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
                  <SelectContent>{svcOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {cfg && needRole && (
              <div>
                <Label className="text-xs text-slate-500">Ruolo / mansione</Label>
                <Select value={role} onValueChange={(v) => { setRole(v); setEntId(""); }}>
                  <SelectTrigger className="rounded-lg" data-testid="sub-role"><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {cfg && roleReady && !isAppaltoKind && entKind && (
              <div>
                <Label className="text-xs text-slate-500">{entKind.hint || "Seleziona l'entità"}</Label>
                <Select value={entId} onValueChange={setEntId}>
                  <SelectTrigger className="rounded-lg" data-testid="sub-entity"><SelectValue placeholder={entLoading ? "Caricamento…" : (entKind.searchPlaceholder || "Seleziona…")} /></SelectTrigger>
                  <SelectContent>
                    {entities.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">{entKind.emptyMessage || "Nessuna entità disponibile."}</div>}
                    {entities.map((d) => <SelectItem key={d.id} value={d.id}>{entKind.title(d)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {appaltoId && (
              <div>
                <Label className="text-xs text-slate-500">Lavoratore da sostituire</Label>
                <Select value={absentId} onValueChange={setAbsentId}>
                  <SelectTrigger className="rounded-lg" data-testid="sub-absent"><SelectValue placeholder="Seleziona lavoratore" /></SelectTrigger>
                  <SelectContent>
                    {workerOptions.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Nessun lavoratore in questo appalto.</div>}
                    {workerOptions.map((w) => <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Data</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="sub-date"
                    className="w-full h-10 rounded-lg border border-input bg-background px-3 text-left text-sm flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <span className={date ? "text-slate-800" : "text-slate-400"}>
                      {date ? date.split("-").reverse().join("/") : "Seleziona data"}
                    </span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single"
                    selected={date ? new Date(date + "T00:00:00") : undefined}
                    onSelect={(d) => {
                      if (!d) { setDate(""); return; }
                      const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
                      setDate(`${y}-${m}-${dd}`);
                    }}
                    initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <TimePicker label="Ora inizio" value={start} onChange={setStart} testid="sub-start" />
            <TimePicker label="Ora fine" value={end} onChange={setEnd} testid="sub-end" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Nota (facoltativa)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="sub-note" placeholder="Eventuali indicazioni…" className="resize-y" />
          </div>
          <div className="flex justify-end pt-1">
            <Button onClick={publish} disabled={!canPublish} data-testid="sub-publish" className="rounded-lg bg-[#5B4A7D] hover:bg-[#4a3c68] text-white">
              {saving ? "Pubblico…" : "Pubblica sostituzione"}
            </Button>
          </div>
        </div>
      </div>
  );
}

const SUB_TONE = {
  disponibile: { card: "border-sky-200 bg-sky-50/70", badge: "bg-sky-100 text-sky-700 border-sky-200", label: "Disponibile" },
  urgente: { card: "border-orange-300 bg-orange-50/80", badge: "bg-orange-100 text-orange-700 border-orange-300", label: "Urgente" },
  coperta: { card: "border-emerald-200 bg-emerald-50/70", badge: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Assegnata" },
  conclusa: { card: "border-slate-200 bg-slate-50", badge: "bg-slate-100 text-slate-600 border-slate-200", label: "Conclusa" },
  annullata: { card: "border-slate-200 bg-slate-50/60 opacity-70", badge: "bg-slate-100 text-slate-500 border-slate-200", label: "Annullata" },
};

function ArchivioSostituzioni({ reloadKey, onChange, highlightId, onHighlightDone }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashId, setFlashId] = useState(null);
  const [fAppalto, setFAppalto] = useState("all");
  const [fService, setFService] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [q, setQ] = useState("");
  const [confirmSub, setConfirmSub] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/office/substitutions").then((r) => setItems(r.data.substitutions || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [reloadKey]);

  useEffect(() => {
    if (!highlightId || loading) return;
    setFlashId(highlightId);
    const s = setTimeout(() => {
      const el = document.getElementById(`arc-card-${highlightId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    const t = setTimeout(() => { setFlashId(null); onHighlightDone && onHighlightDone(); }, 2600);
    return () => { clearTimeout(s); clearTimeout(t); };
    /* eslint-disable-next-line */
  }, [highlightId, loading]);

  const fmtDate = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "long" }); } catch { return iso; } };

  const appaltoOpts = Array.from(new Map(items.filter((s) => s.appalto_id).map((s) => [s.appalto_id, s.appalto_nome || s.appalto_id])).entries());
  const serviceOpts = Array.from(new Set(items.map((s) => s.service))).map((k) => ({ key: k, label: SERVICES[slugFromKey(k)]?.label || k }));

  const filtered = items.filter((s) => {
    if (fAppalto !== "all" && s.appalto_id !== fAppalto) return false;
    if (fService !== "all" && s.service !== fService) return false;
    if (fStatus !== "all" && s.ui_status !== fStatus) return false;
    if (fFrom && s.date < fFrom) return false;
    if (fTo && s.date > fTo) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = `${s.absent_worker_label || ""} ${s.substitute_worker_label || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const doCancel = async () => {
    if (!confirmSub) return;
    setBusy(true);
    try {
      await api.post(`/office/substitutions/${confirmSub.id}/cancel`);
      toast.success("Sostituzione annullata");
      setConfirmSub(null);
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const Field = ({ label, value }) => value ? <p className="text-xs text-slate-600"><span className="text-slate-400">{label}:</span> {value}</p> : null;

  return (
    <div data-testid="archivio-sostituzioni">
      {/* Filtri */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Appalto</Label>
            <Select value={fAppalto} onValueChange={setFAppalto}>
              <SelectTrigger className="rounded-lg" data-testid="arc-f-appalto"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli appalti</SelectItem>
                {appaltoOpts.map(([id, nome]) => <SelectItem key={id} value={id}>{nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Servizio</Label>
            <Select value={fService} onValueChange={setFService}>
              <SelectTrigger className="rounded-lg" data-testid="arc-f-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i servizi</SelectItem>
                {serviceOpts.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Stato</Label>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger className="rounded-lg" data-testid="arc-f-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="disponibile">Disponibile</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
                <SelectItem value="coperta">Assegnata</SelectItem>
                <SelectItem value="conclusa">Conclusa</SelectItem>
                <SelectItem value="annullata">Annullata</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2 lg:col-span-1">
            <div>
              <Label className="text-xs text-slate-500">Dal</Label>
              <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="arc-f-from" className="rounded-lg" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Al</Label>
              <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="arc-f-to" className="rounded-lg" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Lavoratore</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="arc-f-worker" placeholder="Da sostituire o sostituto…" className="rounded-lg" />
          </div>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <p className="text-sm text-slate-400 py-6 text-center">Caricamento…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center" data-testid="arc-empty">Nessuna sostituzione trovata.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="arc-list">
          {filtered.map((s) => {
            const t = SUB_TONE[s.ui_status] || SUB_TONE.disponibile;
            return (
              <div key={s.id} id={`arc-card-${s.id}`} data-testid={`arc-card-${s.id}`} className={`rounded-xl border p-4 ${t.card} transition-all duration-500 ${flashId === s.id ? "ring-2 ring-amber-400" : ""}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md border ${t.badge}`}>{t.label}</span>
                  <span className="text-xs font-semibold text-slate-500">{SERVICES[slugFromKey(s.service)]?.label || s.service}</span>
                </div>
                <p className="text-base font-bold text-slate-900 capitalize leading-tight">{fmtDate(s.date)}</p>
                <p className="text-lg font-extrabold text-slate-900 flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" />{s.start_time}–{s.end_time}</p>
                <div className="mt-2 space-y-0.5">
                  <Field label="Appalto" value={s.appalto_nome} />
                  {s.entity_kind !== "appalto" && <Field label="Destinazione" value={s.entity_label} />}
                  <Field label="Ruolo" value={s.role} />
                  <Field label="Da sostituire" value={s.absent_worker_label} />
                  <Field label="Sostituto" value={s.substitute_worker_label} />
                  <Field label="Nota" value={s.note} />
                </div>
                {s.can_cancel && (
                  <div className="mt-3 flex justify-end">
                    <Button variant="outline" size="sm" data-testid={`arc-cancel-${s.id}`} onClick={() => setConfirmSub(s)}
                      className="rounded-lg border-slate-300 text-slate-600 hover:bg-slate-100">Annulla sostituzione</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!confirmSub} onOpenChange={(o) => !o && setConfirmSub(null)}>
        <DialogContent className="rounded-2xl" data-testid="arc-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading">Annulla sostituzione</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Confermi di voler annullare questa sostituzione?</p>
          {confirmSub?.ui_status === "coperta" && (
            <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              La sostituzione è già stata accettata da <b>{confirmSub.substitute_worker_label}</b>: verrà annullata anche per lui.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmSub(null)} className="rounded-lg" data-testid="arc-confirm-no">Indietro</Button>
            <Button onClick={doCancel} disabled={busy} data-testid="arc-confirm-yes" className="rounded-lg bg-[#5B4A7D] hover:bg-[#4a3c68] text-white">
              {busy ? "Annullo…" : "Annulla sostituzione"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SostituzioniSuggerite({ reloadKey, onChange, highlightKey, onHighlightDone }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [flashKey, setFlashKey] = useState(null);
  const [edit, setEdit] = useState(null); // suggerimento in modifica

  const load = () => {
    setLoading(true);
    api.get("/office/substitutions/suggestions").then((r) => setItems(r.data.suggestions || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [reloadKey]);

  useEffect(() => {
    if (!highlightKey || loading) return;
    setFlashKey(highlightKey);
    const s = setTimeout(() => {
      const el = document.getElementById(`sugg-card-${highlightKey}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    const t = setTimeout(() => { setFlashKey(null); onHighlightDone && onHighlightDone(); }, 2600);
    return () => { clearTimeout(s); clearTimeout(t); };
    /* eslint-disable-next-line */
  }, [highlightKey, loading]);

  const fmtDate = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "long" }); } catch { return iso; } };

  const confirm = async (s, over = {}) => {
    setBusy(s.key);
    try {
      const body = {
        appalto_id: s.appalto_id, service: s.service, role: s.role,
        entity_id: s.entity_id, entity_kind: s.entity_kind, entity_label: s.entity_label,
        absent_worker_id: s.absent_worker_id,
        date: over.date ?? s.date, start_time: over.start_time ?? s.start_time,
        end_time: over.end_time ?? s.end_time, note: over.note ?? null, key: s.key,
      };
      await api.post("/office/substitutions/suggestions/confirm", body);
      toast.success("Sostituzione pubblicata", { description: "Ora è nella Bacheca e nell'Archivio." });
      setEdit(null); load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const ignore = async (s) => {
    setBusy(s.key);
    try {
      await api.post("/office/substitutions/suggestions/ignore", { key: s.key });
      toast.success("Suggerimento ignorato");
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const Field = ({ label, value }) => value ? <p className="text-xs text-slate-600"><span className="text-slate-400">{label}:</span> {value}</p> : null;

  return (
    <div data-testid="sostituzioni-suggerite">
      <p className="text-xs text-slate-500 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
        Proposte automatiche da assenze e Agenda. Non ancora pubblicate: diventano sostituzioni reali solo con <b>Conferma</b>.
      </p>
      {loading ? (
        <p className="text-sm text-slate-400 py-6 text-center">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center" data-testid="sugg-empty">Nessuna sostituzione suggerita al momento.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="sugg-list">
          {items.map((s) => (
            <div key={s.key} id={`sugg-card-${s.key}`} data-testid={`sugg-card-${s.key}`} className={`rounded-xl border border-[#E1CFD8] bg-[#FBF6F9] p-4 transition-all duration-500 ${flashKey === s.key ? "ring-2 ring-amber-400" : ""}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-md border bg-[#F0E1E7] text-[#4A3340] border-[#E1CFD8]">Suggerita</span>
                <span className="text-xs font-semibold text-slate-500">{SERVICES[slugFromKey(s.service)]?.label || s.service}</span>
              </div>
              <p className="text-base font-bold text-slate-900 capitalize leading-tight">{fmtDate(s.date)}</p>
              <p className="text-lg font-extrabold text-slate-900 flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" />{s.start_time}–{s.end_time}{s.partial && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">parziale</span>}</p>
              <div className="mt-2 space-y-0.5">
                <Field label="Appalto" value={s.appalto_nome} />
                <Field label="Lavoratore assente" value={s.absent_worker_label} />
                {s.entity_kind !== "appalto" && <Field label="Destinazione" value={s.entity_label} />}
                <Field label="Ruolo" value={s.role} />
              </div>
              <p className="mt-2 text-[11px] text-[#5B4A7D] italic">{s.source_label}</p>
              <div className="mt-3 flex justify-end gap-2 flex-wrap">
                <Button variant="ghost" size="sm" data-testid={`sugg-ignore-${s.key}`} disabled={busy === s.key} onClick={() => ignore(s)}
                  className="rounded-lg text-slate-500 hover:bg-slate-100">Ignora suggerimento</Button>
                <Button variant="outline" size="sm" data-testid={`sugg-edit-${s.key}`} disabled={busy === s.key} onClick={() => setEdit({ ...s })}
                  className="rounded-lg border-slate-300">Modifica</Button>
                <Button size="sm" data-testid={`sugg-confirm-${s.key}`} disabled={busy === s.key} onClick={() => confirm(s)}
                  className="rounded-lg bg-[#5B4A7D] hover:bg-[#4a3c68] text-white">{busy === s.key ? "…" : "Conferma"}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-2xl" data-testid="sugg-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Modifica suggerimento</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">{SERVICES[slugFromKey(edit.service)]?.label || edit.service}{edit.entity_label ? ` · ${edit.entity_label}` : ""}{edit.role ? ` · ${edit.role}` : ""}</p>
              <div>
                <Label className="text-xs text-slate-500">Data</Label>
                <Input type="date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} data-testid="sugg-edit-date" className="rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TimePicker label="Ora inizio" value={edit.start_time} onChange={(v) => setEdit({ ...edit, start_time: v })} testid="sugg-edit-start" />
                <TimePicker label="Ora fine" value={edit.end_time} onChange={(v) => setEdit({ ...edit, end_time: v })} testid="sugg-edit-end" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Nota (facoltativa)</Label>
                <Textarea value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} rows={2} data-testid="sugg-edit-note" className="resize-y" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setEdit(null)} className="rounded-lg" data-testid="sugg-edit-cancel">Annulla</Button>
                <Button onClick={() => confirm(edit, { date: edit.date, start_time: edit.start_time, end_time: edit.end_time, note: edit.note || null })}
                  disabled={busy === edit.key} data-testid="sugg-edit-confirm" className="rounded-lg bg-[#5B4A7D] hover:bg-[#4a3c68] text-white">Conferma e pubblica</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ElencoAssegnazioniSection({ deepLink, onDeepLinkDone }) {
  const [tab, setTab] = useState(deepLink?.tab || "specifica");
  const [reloadKey, setReloadKey] = useState(0);
  const [badges, setBadges] = useState({ suggerite: 0, archivio: 0 });
  const [notify, setNotify] = useState(true);
  useEffect(() => { if (deepLink?.tab) { setTab(deepLink.tab); setReloadKey((k) => k + 1); } }, [deepLink]);
  const loadBadges = () => api.get("/office/substitutions/badges").then((r) => setBadges({ suggerite: r.data?.suggerite || 0, archivio: r.data?.archivio || 0 })).catch(() => {});
  useEffect(() => { loadBadges(); }, [reloadKey]);
  useEffect(() => { api.get("/office/substitutions/notify-pref").then((r) => setNotify(r.data?.enabled !== false)).catch(() => {}); }, []);
  const toggleNotify = async (v) => {
    setNotify(v);
    try { await api.put("/office/substitutions/notify-pref", { enabled: v }); toast.success(v ? "Notifiche sostituzioni attive" : "Notifiche sostituzioni disattivate"); }
    catch { setNotify(!v); toast.error("Impossibile aggiornare la preferenza"); }
  };
  const bump = () => setReloadKey((k) => k + 1);
  const tabCls = (k) => `relative px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-[#22364D] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;
  const NumBadge = ({ n, testid }) => n > 0 ? (
    <span data-testid={testid} className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold align-middle">{n}</span>
  ) : null;
  return (
    <div data-testid="sostituzioni">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => { setTab("specifica"); loadBadges(); }} data-testid="sub-tab-specifica" className={tabCls("specifica")}>Specifica Sostituzione</button>
          <button type="button" onClick={() => { setTab("suggerite"); bump(); }} data-testid="sub-tab-suggerite" className={tabCls("suggerite")}>Sostituzioni Suggerite<NumBadge n={badges.suggerite} testid="sub-tab-suggerite-badge" /></button>
          <button type="button" onClick={() => { setTab("archivio"); bump(); }} data-testid="sub-tab-archivio" className={tabCls("archivio")}>Archivio Sostituzioni<NumBadge n={badges.archivio} testid="sub-tab-archivio-badge" /></button>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5" data-testid="sub-notify-toggle-wrap">
          <BellRing className="h-4 w-4 text-slate-500" />
          <Label htmlFor="sub-notify" className="text-xs font-medium text-slate-600 cursor-pointer">Notifiche sostituzioni</Label>
          <Switch id="sub-notify" checked={notify} onCheckedChange={toggleNotify} data-testid="sub-notify-toggle" />
        </div>
      </div>
      {tab === "specifica" && <SpecificaSostituzioneForm onPublished={bump} />}
      {tab === "suggerite" && <SostituzioniSuggerite reloadKey={reloadKey} onChange={loadBadges} highlightKey={deepLink?.tab === "suggerite" ? deepLink.key : null} onHighlightDone={onDeepLinkDone} />}
      {tab === "archivio" && <ArchivioSostituzioni reloadKey={reloadKey} onChange={loadBadges} highlightId={deepLink?.tab === "archivio" ? deepLink.id : null} onHighlightDone={onDeepLinkDone} />}
    </div>
  );
}

const PuzzleLeft = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" className={className}>
    <path d="M4 4 H13 V8 A3 3 0 1 1 13 14 V20 H4 Z" />
  </svg>
);
const PuzzleRight = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" className={className}>
    <path d="M11 4 H20 V20 H11 V14 A3 3 0 1 0 11 8 Z" />
  </svg>
);

function ElencoAssegnazioniTab({ onEdit }) {
  const [appalti, setAppalti] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appaltoId, setAppaltoId] = useState("");
  const [service, setService] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // assegnati | da_verificare | non_assegnati
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [commOpen, setCommOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/my-appalti").then((r) => setAppalti(r.data || [])).catch(() => setAppalti([]));
    api.get("/office/matchmaking/list").then((r) => setWorkers(r.data.workers || [])).catch(() => setWorkers([])).finally(() => setLoading(false));
  }, []);

  const categoryOf = (w) => {
    if (!w.assignments_count) return "non_assegnati";
    const c = w.contract_hours;
    const incoherent = c ? !(w.weekly_hours >= c * 0.9 && w.weekly_hours <= c * 1.1) : false;
    if (w.status === "draft" || w.has_temporary || incoherent) return "da_verificare";
    return "assegnati";
  };
  const hoursBadgeCls = (w) => {
    if (!w.assignments_count) return "bg-slate-100 text-slate-600 border-slate-200";
    if (w.has_temporary) return "bg-amber-100 text-amber-700 border-amber-200";
    const c = w.contract_hours;
    if (!c) return "bg-slate-100 text-slate-600 border-slate-200";
    return (w.weekly_hours >= c * 0.9 && w.weekly_hours <= c * 1.1) ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700 border-rose-200";
  };

  const serviceOpts = useMemo(() => {
    const m = new Map();
    workers.forEach((w) => (w.assignments || []).forEach((a) => {
      if (appaltoId && a.appalto_id !== appaltoId) return;
      if (a.service) m.set(a.service, svcLabel(a.service));
    }));
    return [...m.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [workers, appaltoId]);

  const onAppalto = (v) => { const nv = v === "__all__" ? "" : v; setAppaltoId(nv); setService(""); };
  const onService = (v) => setService(v === "__all__" ? "" : v);

  const baseFiltered = useMemo(() => workers.filter((w) => {
    if (appaltoId && !(w.appalto_ids || []).includes(appaltoId)) return false;
    if (service && !(w.assignments || []).some((a) => a.service === service && (!appaltoId || a.appalto_id === appaltoId))) return false;
    if (q.trim() && !w.label.toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  }), [workers, appaltoId, service, q]);

  const counts = useMemo(() => {
    const c = { assegnati: 0, da_verificare: 0, non_assegnati: 0 };
    baseFiltered.forEach((w) => { c[categoryOf(w)] += 1; });
    return c;
    /* eslint-disable-next-line */
  }, [baseFiltered]);

  const filtered = useMemo(
    () => (statusFilter ? baseFiltered.filter((w) => categoryOf(w) === statusFilter) : baseFiltered),
    /* eslint-disable-next-line */
    [baseFiltered, statusFilter]
  );

  // Selezione (rispetta i filtri correnti): solo lavoratori con almeno un'assegnazione.
  const selectableIds = useMemo(() => filtered.filter((w) => w.assignments_count > 0).map((w) => w.id), [filtered]);
  const selectedFilteredIds = useMemo(() => selectableIds.filter((id) => selected.has(id)), [selectableIds, selected]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(selectableIds));
  const deselectAll = () => setSelected(new Set());

  const toggle = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => setExpanded(new Set(filtered.map((w) => w.id)));
  const collapseAll = () => setExpanded(new Set());

  const quickBtn = (key, label, activeCls) => (
    <button type="button" data-testid={`el-quick-${key}`} onClick={() => setStatusFilter((s) => (s === key ? null : key))}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${statusFilter === key ? activeCls : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {label} <span className="opacity-70">({counts[key]})</span>
    </button>
  );

  const selCls = "rounded-lg";
  return (
    <div className="space-y-4" data-testid="elenco-assegnazioni">
      {/* Filtri principali */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4 space-y-3" data-testid="el-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Appalto</Label>
            <Select value={appaltoId || "__all__"} onValueChange={onAppalto}>
              <SelectTrigger className={selCls} data-testid="el-appalto"><SelectValue placeholder="Tutti gli appalti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli appalti</SelectItem>
                {appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Servizio</Label>
            <Select value={service || "__all__"} onValueChange={onService}>
              <SelectTrigger className={selCls} data-testid="el-service"><SelectValue placeholder="Tutti i servizi" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i servizi</SelectItem>
                {serviceOpts.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Cerca lavoratore</Label>
            <SearchBox value={q} onChange={setQ} placeholder="Nome o cognome…" testid="el-search" />
          </div>
        </div>
        {/* Filtri rapidi di stato */}
        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="el-quick-filters">
          {quickBtn("assegnati", "Assegnati", "bg-emerald-600 border-emerald-600 text-white")}
          {quickBtn("da_verificare", "Da verificare", "bg-amber-500 border-amber-500 text-white")}
          {quickBtn("non_assegnati", "Non assegnati", "bg-slate-600 border-slate-600 text-white")}
          {statusFilter && (
            <button type="button" data-testid="el-quick-clear" onClick={() => setStatusFilter(null)} className="text-xs text-slate-500 hover:underline ml-1">Azzera stato</button>
          )}
        </div>
      </div>

      {/* Barra azioni elenco */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500" data-testid="el-count">{filtered.length} lavorator{filtered.length === 1 ? "e" : "i"}</p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={allSelected ? deselectAll : selectAll} data-testid="el-select-all"
            className="text-xs font-semibold text-[#5B4A7D] hover:underline">
            {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
          </button>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={expandAll} data-testid="el-expand-all" className="text-xs font-semibold text-[#5B4A7D] hover:underline">Espandi tutti</button>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={collapseAll} data-testid="el-collapse-all" className="text-xs font-semibold text-slate-500 hover:underline">Comprimi tutti</button>
        </div>
      </div>

      {/* Azione di comunicazione (visibile con almeno 1 selezionato) */}
      {selectedFilteredIds.length > 0 && (
        <div data-testid="el-comm-bar" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#A99BD6] bg-[#EFEAF6] px-4 py-3">
          <p className="text-sm font-semibold text-[#4A4470]">
            {selectedFilteredIds.length} lavorator{selectedFilteredIds.length === 1 ? "e selezionato" : "i selezionati"}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="rounded-lg text-slate-600" onClick={deselectAll} data-testid="el-comm-clear">Deseleziona</Button>
            <Button size="sm" className="rounded-lg bg-[#5B4A7D] hover:bg-[#4A3D68]" onClick={() => setCommOpen(true)} data-testid="el-comm-open">
              <Megaphone className="h-4 w-4 mr-1.5" /> Comunica assegnazioni
            </Button>
          </div>
        </div>
      )}

      {/* Elenco accordion */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100 overflow-hidden" data-testid="el-list">
        {loading && <p className="p-4 text-sm text-slate-400">Caricamento…</p>}
        {!loading && filtered.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">Nessun lavoratore corrisponde ai filtri.</p>}
        {!loading && filtered.map((w) => {
          const open = expanded.has(w.id);
          return (
            <div key={w.id} data-testid={`el-row-${w.id}`}>
              <div className="flex items-center gap-2 pl-3 sm:pl-4">
                {w.assignments_count > 0 ? (
                  <Checkbox checked={selected.has(w.id)} onCheckedChange={() => toggleSel(w.id)}
                    data-testid={`el-select-${w.id}`} className="shrink-0" aria-label={`Seleziona ${w.label}`} />
                ) : <span className="inline-block w-4 shrink-0" />}
                <button type="button" onClick={() => toggle(w.id)} data-testid={`el-toggle-${w.id}`}
                  className="flex-1 min-w-0 flex items-center gap-3 pr-3 sm:pr-4 py-3 text-left hover:bg-slate-50 transition-colors">
                  <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
                  <p className="font-semibold text-slate-800 text-sm flex-1 min-w-0 truncate">{w.label}</p>
                  {w.contract_hours ? (
                    <span className="hidden sm:inline text-[11px] text-slate-400">contratto <b className="text-slate-600">{w.contract_hours} h</b></span>
                  ) : null}
                  <span data-testid={`el-hours-${w.id}`} className={`shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-md border ${hoursBadgeCls(w)}`}>{w.weekly_hours || 0} h</span>
                  {w.status === "done" && <span data-testid={`el-status-${w.id}`} className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-200">Terminato</span>}
                  {w.status === "draft" && <span data-testid={`el-status-${w.id}`} className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-md border bg-amber-50 text-amber-700 border-amber-200">Bozza</span>}
                </button>
              </div>
              {open && (
                <div className="px-3 sm:px-4 pb-3 pl-11" data-testid={`el-detail-${w.id}`}>
                  {(w.assignments || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-1">Nessuna assegnazione registrata.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(w.assignments || []).map((a, i) => (
                        <div key={i} data-testid={`el-assign-${w.id}-${i}`} className="flex items-center gap-2 text-sm rounded-lg bg-slate-50 border border-slate-100 px-3 py-1.5">
                          <span className="font-semibold text-slate-700 shrink-0">{svcLabel(a.service)}</span>
                          <ArrowRightLeft className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                          <span className="text-slate-600 min-w-0 flex-1 truncate">{a.entity_label || "—"}</span>
                          {a.temporary ? (
                            <span className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 border border-amber-200">Temporaneo</span>
                          ) : (
                            <span className="shrink-0 text-[11px] font-semibold text-slate-700">{a.weekly_hours ?? "—"} h</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2.5">
                    <Button variant="outline" size="sm" onClick={() => onEdit(w.id)} data-testid={`el-edit-${w.id}`} className="rounded-lg border-[#A99BD6] text-[#5B4A7D] hover:bg-[#EFEAF6]">
                      <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifica assegnazioni
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ComunicaAssegnazioniDialog open={commOpen} onClose={() => setCommOpen(false)}
        workerIds={selectedFilteredIds} appaltoId={appaltoId} />
    </div>
  );
}

function ComunicaAssegnazioniDialog({ open, onClose, workerIds, appaltoId }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [important, setImportant] = useState(true);
  const [email, setEmail] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setPreview(null); setActiveIdx(0); setImportant(true); setEmail(false);
    api.post("/office/matchmaking/communicate/preview", { worker_ids: workerIds, appalto_id: appaltoId || null })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore anteprima"))
      .finally(() => setLoading(false));
    /* eslint-disable-next-line */
  }, [open]);

  const groups = preview?.groups || [];
  const active = groups[activeIdx];

  const confirm = async () => {
    if (!important && !email) return;
    setSending(true);
    try {
      const r = await api.post("/office/matchmaking/communicate/send",
        { worker_ids: workerIds, appalto_id: appaltoId || null, important, email });
      const d = r.data || {};
      const parts = [];
      if (important) parts.push(`${d.important_sent || 0} comunicazioni importanti`);
      if (email) parts.push(`${d.emails_sent || 0} email`);
      let msg = `Inviate: ${parts.join(" · ")}`;
      if (d.reference_missing_skipped) msg += ` · ${d.reference_missing_skipped} saltate (appalto senza indirizzo)`;
      if (d.email_skipped_no_email) msg += ` · ${d.email_skipped_no_email} senza email`;
      if (d.emails_failed) msg += ` · ${d.emails_failed} email non riuscite`;
      toast.success(msg);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="comm-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Comunica assegnazioni</DialogTitle>
          <DialogDescription>
            {preview ? `${preview.worker_count} lavoratori · ${preview.group_count} comunicazioni (una per appalto)` : "Preparazione anteprima…"}
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-slate-500 py-4">Caricamento anteprima…</p>}

        {!loading && preview && (
          <div className="space-y-4">
            {/* Canali */}
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Modalità di comunicazione</p>
              <div className="flex items-center justify-between">
                <Label htmlFor="ca-important" className="text-sm font-medium text-slate-700">Comunicazione importante <span className="text-xs text-slate-400">(in Orbit, con lettura tracciata)</span></Label>
                <Switch id="ca-important" checked={important} onCheckedChange={setImportant} data-testid="comm-assign-important" />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="ca-email" className="text-sm font-medium text-slate-700">Email <span className="text-xs text-slate-400">(all'indirizzo del lavoratore)</span></Label>
                <Switch id="ca-email" checked={email} onCheckedChange={setEmail} data-testid="comm-assign-email" />
              </div>
            </div>

            {/* Anomalie */}
            {preview.missing_reference_appalti?.length > 0 && (
              <div data-testid="comm-assign-warn-reference" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Appalti senza indirizzo di riferimento: <b>{preview.missing_reference_appalti.join(", ")}</b>. Le relative comunicazioni <b>non</b> verranno inviate. Imposta l'indirizzo in Amministratore → Appalto.</span>
              </div>
            )}
            {email && preview.workers_without_email?.length > 0 && (
              <div data-testid="comm-assign-warn-email" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Senza email (non riceveranno l'email, ma la Comunicazione importante sì): <b>{preview.workers_without_email.join(", ")}</b></span>
              </div>
            )}

            {/* Anteprima navigabile */}
            {groups.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna assegnazione da comunicare per i lavoratori selezionati.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1 space-y-1 max-h-[38vh] overflow-y-auto pr-1" data-testid="comm-assign-nav">
                  {groups.map((g, i) => (
                    <button key={i} type="button" onClick={() => setActiveIdx(i)} data-testid={`comm-assign-nav-${i}`}
                      className={`w-full text-left rounded-lg border px-2.5 py-2 transition-colors ${i === activeIdx ? "border-[#5B4A7D] bg-[#EFEAF6]" : "border-slate-200 hover:bg-slate-50"}`}>
                      <p className="text-xs font-bold text-slate-800 truncate">{g.worker_name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{g.appalto_name} · {g.total_hours} h{g.reference_missing ? " · ⚠ no indirizzo" : ""}</p>
                    </button>
                  ))}
                </div>
                <div className="sm:col-span-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 max-h-[38vh] overflow-y-auto">
                    <pre data-testid="comm-assign-text" className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{active?.text}</pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-lg" onClick={onClose} data-testid="comm-assign-cancel">Annulla</Button>
          <Button className="rounded-lg bg-[#5B4A7D] hover:bg-[#4A3D68]" data-testid="comm-assign-confirm"
            disabled={sending || loading || (!important && !email) || (groups.length === 0)} onClick={confirm}>
            {sending ? "Invio…" : "Conferma e invia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssegnazioniSection() {
  const [tab, setTab] = useState("gestione");
  const [preselectId, setPreselectId] = useState(null);
  const tabCls = (k) => `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-[#22364D] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;
  const goEdit = (wid) => { setPreselectId(wid); setTab("gestione"); };
  return (
    <div data-testid="assegnazioni-tabs">
      <div className="flex items-center gap-2 mb-4">
        <button type="button" onClick={() => setTab("gestione")} data-testid="asg-tab-gestione" className={tabCls("gestione")}>Gestione Assegnazioni</button>
        <button type="button" onClick={() => setTab("elenco")} data-testid="asg-tab-elenco" className={tabCls("elenco")}>Elenco Assegnazioni</button>
      </div>
      {tab === "gestione"
        ? <MatchmakingSection preselectWorkerId={preselectId} onPreselectConsumed={() => setPreselectId(null)} />
        : <ElencoAssegnazioniTab onEdit={goEdit} />}
    </div>
  );
}

function MatchmakingSection({ preselectWorkerId, onPreselectConsumed }) {
  const [appalti, setAppalti] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [wq, setWq] = useState("");
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState("none");
  const [assignments, setAssignments] = useState([]);
  const [loadingW, setLoadingW] = useState(false);
  const [saving, setSaving] = useState(false);
  // builder
  const [svcKey, setSvcKey] = useState("");
  const [svcAppalto, setSvcAppalto] = useState("");
  const [ents, setEnts] = useState([]);
  const [entId, setEntId] = useState("");
  const [hours, setHours] = useState("");
  const [temp, setTemp] = useState(false);

  const loadWorkers = () => { setLoadingW(true); api.get("/office/matchmaking/workers").then((r) => setWorkers(r.data.workers || [])).catch(() => setWorkers([])).finally(() => setLoadingW(false)); };
  useEffect(() => { api.get("/my-appalti").then((r) => setAppalti(r.data || [])).catch(() => setAppalti([])); loadWorkers(); }, []);

  const resetBuilder = () => { setSvcKey(""); setSvcAppalto(""); setEnts([]); setEntId(""); setHours(""); setTemp(false); };
  const openWorker = async (w) => {
    setSel(w); resetBuilder();
    try { const { data } = await api.get(`/office/matchmaking/worker/${w.id}`); setStatus(data.status || "none"); setAssignments(data.assignments || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!preselectWorkerId || loadingW || !workers.length) return;
    const w = workers.find((x) => x.id === preselectWorkerId);
    if (w) openWorker(w);
    onPreselectConsumed && onPreselectConsumed();
    /* eslint-disable-next-line */
  }, [preselectWorkerId, loadingW, workers]);

  // opzioni servizio = (appalto, servizio) per gli appalti del lavoratore
  const svcOptions = (() => {
    if (!sel) return [];
    const out = [];
    (sel.appalto_ids || []).forEach((aid) => {
      const ap = appalti.find((a) => a.id === aid);
      (ap?.servizi || []).filter((s) => s.stato === "attivo").forEach((s) => out.push({ key: `${aid}::${s.slug}`, appalto_id: aid, service: s.slug, label: `${svcLabel(s.slug)} · ${ap?.nome || ""}` }));
    });
    return out;
  })();

  const cfg = svcKey ? SERVICES[slugFromKey(svcKey)] : null;
  const kindKey = cfg ? (cfg.entityKind || (cfg.roleEntity ? Object.values(cfg.roleEntity).find((k) => k !== "appalto") : null)) : null;
  const entKind = kindKey ? ENTITY_KINDS[kindKey] : null;
  const isAppaltoKind = entKind && entKind.backendKind === "appalto";

  useEffect(() => {
    if (!svcKey || !entKind || isAppaltoKind) { setEnts([]); return; }
    api.get(entKind.adminApi()).then((r) => {
      let list = (r.data || []).filter((d) => (!d.appalto_id || d.appalto_id === svcAppalto));
      if (entKind.backendKind === "operative_site" || entKind.backendKind === "school_user") list = list.filter((d) => !d.service_types || d.service_types.includes(svcKey));
      setEnts(list);
    }).catch(() => setEnts([]));
    /* eslint-disable-next-line */
  }, [svcKey, svcAppalto]);

  const pickService = (v) => { const o = svcOptions.find((x) => x.key === v); setSvcKey(o?.service || ""); setSvcAppalto(o?.appalto_id || ""); setEntId(""); setHours(""); setTemp(false); };
  const pickEntity = (v) => {
    setEntId(v); const d = ents.find((e) => e.id === v);
    const def = d ? (d.monte_ore_per_service?.[svcKey] ?? d.monte_ore_settimanale ?? "") : "";
    setHours(def === null ? "" : String(def || ""));
  };

  const addAssignment = () => {
    if (!svcKey) { toast.error("Seleziona un servizio"); return; }
    if (!isAppaltoKind && !entId) { toast.error("Seleziona la destinazione (unità/utente)"); return; }
    const d = ents.find((e) => e.id === entId);
    setAssignments((a) => [...a, {
      id: undefined, appalto_id: svcAppalto, service: svcKey,
      entity_id: isAppaltoKind ? null : entId, entity_kind: isAppaltoKind ? "appalto" : (entKind?.backendKind || null),
      entity_label: isAppaltoKind ? (appalti.find((x) => x.id === svcAppalto)?.nome || "Appalto") : (d ? entKind.title(d) : entId),
      weekly_hours: temp ? null : (hours === "" ? null : Number(String(hours).replace(",", "."))),
      temporary: temp,
    }]);
    resetBuilder();
  };
  const removeAssignment = (i) => setAssignments((a) => a.filter((_, idx) => idx !== i));

  const totalHours = assignments.filter((a) => !a.temporary).reduce((s, a) => s + (Number(a.weekly_hours) || 0), 0);

  const save = async (st) => {
    if (!sel) return;
    setSaving(true);
    try {
      await api.put(`/office/matchmaking/worker/${sel.id}`, { status: st, assignments });
      toast.success(st === "done" ? "Assegnazione terminata" : "Salvato in bozza");
      setStatus(st);
      setWorkers((ws) => ws.map((w) => w.id === sel.id ? { ...w, status: st, assignments_count: assignments.length, weekly_hours: totalHours, has_temporary: assignments.some((a) => a.temporary) } : w));
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const cardBg = (s) => s === "draft" ? "bg-amber-50 border-amber-200" : s === "done" ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200";
  const contractBadgeCls = (w) => {
    const count = w.id === sel?.id ? assignments.length : (w.assignments_count || 0);
    const hasTemp = w.id === sel?.id ? assignments.some((a) => a.temporary) : !!w.has_temporary;
    const total = w.id === sel?.id ? totalHours : (w.weekly_hours || 0);
    if (count === 0) return "bg-slate-100 text-slate-600 border-slate-200";
    if (hasTemp) return "bg-amber-100 text-amber-700 border-amber-200";
    const c = w.contract_hours;
    return (total >= c * 0.9 && total <= c * 1.1) ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700 border-rose-200";
  };
  const filtered = workers.filter((w) => !wq.trim() || w.label.toLowerCase().includes(wq.trim().toLowerCase()));

  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="matchmaking">
      {/* Colonna sinistra 1/3 */}
      <div className="lg:w-1/3 shrink-0">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-9 w-9 rounded-lg bg-[#EFEAF6] border border-[#A99BD6] flex items-center justify-center"><PuzzleLeft className="h-5 w-5 text-[#5B4A7D]" /></div>
            <p className="font-heading font-bold text-slate-900 text-sm">Lavoratori</p>
          </div>
          <SearchBox value={wq} onChange={setWq} placeholder="Cerca lavoratore…" testid="mm-search" />
          <div className="mt-2 space-y-2 max-h-[62vh] overflow-y-auto pr-1" data-testid="mm-worker-list">
            {loadingW && <p className="text-sm text-slate-400 p-2">Caricamento…</p>}
            {!loadingW && filtered.map((w) => (
              <button key={w.id} data-testid={`mm-worker-${w.id}`} onClick={() => openWorker(w)}
                className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${cardBg(w.status)} ${sel?.id === w.id ? "ring-2 ring-[#5B4A7D]" : "hover:border-slate-300"}`}>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-800 text-sm flex-1 min-w-0 truncate">{w.label}</p>
                  {w.contract_hours ? (
                    <span data-testid={`mm-contract-badge-${w.id}`} className={`shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-md border transition-colors ${contractBadgeCls(w)}`}>{w.contract_hours} h</span>
                  ) : null}
                </div>
                <p className="text-[11px] text-slate-500">{w.status === "done" ? "Terminato" : w.status === "draft" ? "Bozza" : "Da assegnare"}{w.assignments_count ? ` · ${w.assignments_count} assegnazioni` : ""}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Colonna destra 2/3 */}
      <div className="lg:w-2/3">
        {!sel ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="mm-empty">
            <PuzzleRight className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-500">Seleziona un lavoratore</p>
            <p className="text-xs text-slate-400 mt-1">Scegli a sinistra per configurare le sue assegnazioni.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 space-y-4" data-testid="mm-config">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-[#EFEAF6] border border-[#A99BD6] flex items-center justify-center"><PuzzleRight className="h-5 w-5 text-[#5B4A7D]" /></div>
              <div><p className="font-heading font-bold text-slate-900">{sel.label}</p>
                <p className="text-[11px] text-slate-500">Stato: {status === "done" ? "Terminato" : status === "draft" ? "Bozza" : "Non iniziato"}</p></div>
            </div>

            {/* Builder nuova assegnazione */}
            <div className="rounded-xl border border-slate-200 p-3 space-y-3 bg-slate-50/60">
              <p className="text-xs font-semibold text-slate-500 uppercase">Nuova assegnazione · Servizio → Destinazione → Monte ore</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Servizio</Label>
                  <Select value={svcKey ? `${svcAppalto}::${svcKey}` : ""} onValueChange={pickService}>
                    <SelectTrigger className="rounded-lg" data-testid="mm-service"><SelectValue placeholder="Scegli servizio" /></SelectTrigger>
                    <SelectContent>{svcOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {svcKey && !isAppaltoKind && (
                  <div><Label className="text-xs text-slate-500">Destinazione ({entKind?.title ? (kindKey || "unità") : "unità"})</Label>
                    <Select value={entId} onValueChange={pickEntity}>
                      <SelectTrigger className="rounded-lg" data-testid="mm-entity"><SelectValue placeholder="Utente / sede / scuola / unità" /></SelectTrigger>
                      <SelectContent>{ents.map((e) => <SelectItem key={e.id} value={e.id}>{entKind.title(e)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                {!temp && (
                  <div><Label className="text-xs text-slate-500">Monte ore settimanale</Label>
                    <Input type="number" min="0" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="mm-hours" className="w-40" placeholder="es. 20" /></div>
                )}
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pb-2" data-testid="mm-temp-toggle">
                  <input type="checkbox" checked={temp} onChange={(e) => setTemp(e.target.checked)} className="h-4 w-4 accent-[#5B4A7D]" />
                  Monte ore temporaneo
                </label>
                <div className="flex-1" />
                <Button onClick={addAssignment} data-testid="mm-add" className="rounded-lg"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
              </div>
            </div>

            {/* Assegnazioni essenziali */}
            <div className="space-y-2" data-testid="mm-assignments">
              {assignments.length === 0 && <p className="text-sm text-slate-400">Nessuna assegnazione. Aggiungine una qui sopra.</p>}
              {assignments.map((a, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`mm-assignment-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">{svcLabel(a.service)} → {a.entity_label}</p>
                    <p className="text-[11px] text-slate-500">{a.temporary ? "Monte ore temporaneo (non conteggiato)" : `${a.weekly_hours ?? "—"} h settimanali`}</p>
                  </div>
                  <button onClick={() => removeAssignment(i)} data-testid={`mm-remove-${i}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              <p className="text-sm text-slate-600" data-testid="mm-total">Monte ore settimanale assegnato: <b className="text-slate-900">{totalHours} h</b></p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="mm-save-draft" className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50">Lascia in bozza</Button>
                <Button onClick={() => save("done")} disabled={saving} data-testid="mm-save-done" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">Assegnazione terminata</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ComunicazioneNuovaSection() {
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [service, setService] = useState("");
  const [entity, setEntity] = useState(null);
  const [selected, setSelected] = useState([]);
  const [aud, setAud] = useState(null);
  const [loadingAud, setLoadingAud] = useState(false);
  const [wq, setWq] = useState("");
  const [showWorkers, setShowWorkers] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pushOn, setPushOn] = useState(true);
  const [emailOn, setEmailOn] = useState(false);
  const [recipOpen, setRecipOpen] = useState(false);
  const [rq, setRq] = useState("");

  useEffect(() => { api.get("/my-appalti").then((r) => setAppalti(r.data || [])).catch(() => setAppalti([])); }, []);

  const appalto = appalti.find((a) => a.id === appaltoId);
  const services = (appalto?.servizi || []).filter((s) => s.stato === "attivo");

  useEffect(() => {
    setLoadingAud(true);
    const p = new URLSearchParams();
    if (appaltoId) p.set("appalto_id", appaltoId);
    if (service) p.set("service", service);
    if (entity) { p.set("entity_id", entity.entity_id); p.set("entity_kind", entity.entity_kind); }
    api.get(`/office/communications/audience?${p.toString()}`)
      .then((r) => setAud(r.data)).catch(() => setAud({ count: 0, workers: [], entities: [], has_entity_level: false }))
      .finally(() => setLoadingAud(false));
  }, [appaltoId, service, entity]);

  const onAppalto = (v) => { setAppaltoId(v); setService(""); setEntity(null); setSelected([]); setShowWorkers(false); };
  const onService = (v) => { setService(v); setEntity(null); setSelected([]); setShowWorkers(false); };

  const workers = aud?.workers || [];
  const entities = aud?.entities || [];
  const count = selected.length > 0 ? selected.length : (aud?.count ?? 0);
  const toggleW = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const entityLevel = aud?.entity_level || null; // "school" | "operative_site" | null
  const levelLabel = entityLevel === "school" ? "Scuola di assegnazione" : "Sede operativa";
  const levelPlaceholder = entityLevel === "school" ? "Tutte le scuole di assegnazione" : "Tutte le sedi operative";

  const perimeterMsg = (() => {
    if (selected.length === 1) { const w = workers.find((x) => x.id === selected[0]); return `Comunica con ${w ? w.label : "1 lavoratore"}`; }
    if (selected.length > 1) return `Comunica con ${selected.length} lavoratori selezionati`;
    if (entity) {
      if (entity.entity_kind === "school") return `Comunica con i lavoratori assegnati alla scuola ${entity.label}`;
      return `Comunica con i lavoratori della sede ${entity.label}`;
    }
    if (service) return `Comunica con i lavoratori del servizio ${svcLabel(service)} dell'appalto ${appalto?.nome || ""}`;
    if (appaltoId) return `Comunica con i lavoratori dell'appalto ${appalto?.nome || ""}`;
    return "Comunica con tutti i lavoratori del tuo account Ufficio";
  })();

  const filteredWorkers = workers.filter((w) => !wq.trim() || w.label.toLowerCase().includes(wq.trim().toLowerCase()));

  // Destinatari finali (per il conteggio email reale): selezione manuale se presente, altrimenti tutto il perimetro
  const finalRecipients = selected.length > 0 ? workers.filter((w) => selected.includes(w.id)) : workers;
  const emailReachable = finalRecipients.filter((w) => w.has_email).length;
  const emailMissing = count - emailReachable;

  const canSend = msg.trim().length > 0 && count >= 1 && !loadingAud;
  const doSend = async () => {
    setSending(true);
    try {
      const payload = { text: msg.trim(), push: pushOn, email: emailOn };
      if (appaltoId) payload.appalto_id = appaltoId;
      if (service) payload.service = service;
      if (entity) { payload.entity_id = entity.entity_id; payload.entity_kind = entity.entity_kind; }
      if (selected.length > 0) payload.worker_ids = selected;
      const { data } = await api.post("/office/communications/send", payload);
      toast.success(`Comunicazione inviata a ${data.recipient_count} lavorator${data.recipient_count === 1 ? "e" : "i"}`);
      setMsg(""); setSelected([]); setShowWorkers(false); setConfirmOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="comunicazione-nuova">
      {/* Imbuto destinatari (nessun filtro obbligatorio) */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 space-y-3" data-testid="cn-funnel">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Destinatari · restringi progressivamente (facoltativo)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Appalto</Label>
            <Select value={appaltoId || "__all__"} onValueChange={(v) => onAppalto(v === "__all__" ? "" : v)}>
              <SelectTrigger className="rounded-lg" data-testid="cn-appalto"><SelectValue placeholder="Tutti gli appalti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli appalti</SelectItem>
                {appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {appaltoId && (
            <div>
              <Label className="text-xs text-slate-500">Servizio</Label>
              <Select value={service} onValueChange={onService}>
                <SelectTrigger className="rounded-lg" data-testid="cn-service"><SelectValue placeholder="Tutti i servizi" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.slug} value={s.slug}>{svcLabel(s.slug)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        {service && aud?.has_entity_level && (
          <div>
            <Label className="text-xs text-slate-500">{levelLabel}</Label>
            <Select value={entity?.entity_id || ""} onValueChange={(v) => { setEntity(v ? entities.find((e) => e.entity_id === v) : null); setSelected([]); }}>
              <SelectTrigger className="rounded-lg" data-testid="cn-entity"><SelectValue placeholder={levelPlaceholder} /></SelectTrigger>
              <SelectContent>{entities.map((e) => <SelectItem key={e.entity_id} value={e.entity_id}>{e.label}{e.subtitle ? ` · ${e.subtitle}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <button type="button" onClick={() => setShowWorkers((v) => !v)} data-testid="cn-toggle-workers"
            className="text-xs font-semibold text-[#5B4A7D] inline-flex items-center gap-1 hover:underline">
            {showWorkers ? "Nascondi" : "Restringi a singoli lavoratori"} {selected.length > 0 ? `(${selected.length} selezionati)` : ""}
          </button>
          {showWorkers && (
            <div className="mt-2">
              <SearchBox value={wq} onChange={setWq} placeholder="Cerca lavoratore…" testid="cn-worker-search" />
              <div className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-slate-200 divide-y" data-testid="cn-worker-list">
                {loadingAud && <p className="p-3 text-sm text-slate-400">Caricamento…</p>}
                {!loadingAud && filteredWorkers.length === 0 && <p className="p-3 text-sm text-slate-400">Nessun lavoratore nel perimetro corrente.</p>}
                {!loadingAud && filteredWorkers.map((w) => (
                  <label key={w.id} data-testid={`cn-worker-${w.id}`} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={selected.includes(w.id)} onChange={() => toggleW(w.id)} className="h-4 w-4 accent-[#5B4A7D]" />
                    <span className="text-slate-800">{w.label}</span>
                  </label>
                ))}
              </div>
              {selected.length > 0 && (
                <button type="button" onClick={() => setSelected([])} data-testid="cn-clear-workers" className="mt-1.5 text-xs text-slate-500 hover:underline">Azzera selezione (torna a tutti)</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Perimetro dinamico + conteggio */}
      <div className="rounded-2xl border border-[#A99BD6] bg-[#EFEAF6] p-4 flex items-start gap-3" data-testid="cn-perimeter">
        <div className="h-10 w-10 rounded-xl bg-white border border-[#BCB6D2] flex items-center justify-center shrink-0">
          <Megaphone className="h-5 w-5 text-[#5B4A7D]" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <p className="font-heading font-bold text-slate-900" data-testid="cn-perimeter-msg">{perimeterMsg}</p>
          <button type="button" data-testid="cn-count" disabled={count < 1}
            onClick={() => { setRq(""); setRecipOpen(true); }}
            className={`text-sm font-semibold mt-0.5 inline-flex items-center gap-1 ${count < 1 ? "text-slate-400 cursor-default" : "text-[#5B4A7D] hover:underline"}`}>
            {loadingAud ? "…" : count} destinatar{count === 1 ? "io" : "i"}
            {count >= 1 && <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      <Dialog open={recipOpen} onOpenChange={setRecipOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="cn-recipients-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Users className="h-5 w-5 text-[#5B4A7D]" /> {count} destinatar{count === 1 ? "io" : "i"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400 -mt-1">Elenco informativo. Per restringere i destinatari usa "Restringi a singoli lavoratori".</p>
          <Input value={rq} onChange={(e) => setRq(e.target.value)} placeholder="Cerca per nome o cognome…" data-testid="cn-recipients-search" className="rounded-lg" />
          <div className="space-y-1.5" data-testid="cn-recipients-list">
            {(() => {
              const list = finalRecipients.filter((w) => !rq.trim() || (w.label || "").toLowerCase().includes(rq.trim().toLowerCase()));
              if (list.length === 0) return <p className="text-sm text-slate-400 py-2 text-center">Nessun lavoratore trovato.</p>;
              return list.map((w) => (
                <div key={w.id} data-testid={`cn-recip-${w.id}`} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2">
                  <span className="h-7 w-7 rounded-full bg-[#EFEAF6] text-[#5B4A7D] text-[11px] font-bold flex items-center justify-center shrink-0">
                    {(w.nome?.[0] || "").toUpperCase()}{(w.cognome?.[0] || "").toUpperCase()}
                  </span>
                  <p className="text-sm text-slate-800 truncate">{w.nome} {w.cognome}</p>
                </div>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Testo della comunicazione */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
        <Label className="text-xs text-slate-500">Testo della comunicazione</Label>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={6} data-testid="cn-message"
          placeholder="Scrivi qui la comunicazione…" className="mt-1 resize-y" />

        {/* Canali di recapito */}
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="cn-channels">
          <p className="text-xs font-semibold text-slate-500 mb-2">Canali di recapito</p>
          <div className="flex items-center gap-2 py-1.5">
            <span className="h-8 w-8 rounded-lg bg-[#EFEAF6] text-[#5B4A7D] border border-[#D8CEEA] flex items-center justify-center shrink-0"><MessageSquare className="h-4 w-4" /></span>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800">Orbit</p><p className="text-[11px] text-slate-400">Canale principale, sempre attivo</p></div>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-200">Sempre</span>
          </div>
          <label className="flex items-center gap-2 py-1.5 cursor-pointer" data-testid="cn-push-row">
            <span className="h-8 w-8 rounded-lg bg-sky-100 text-sky-700 border border-sky-200 flex items-center justify-center shrink-0"><BellRing className="h-4 w-4" /></span>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800">Notifica push</p><p className="text-[11px] text-slate-400">Avviso sul dispositivo dei lavoratori</p></div>
            <Checkbox checked={pushOn} onCheckedChange={(v) => setPushOn(!!v)} data-testid="cn-push-toggle" />
          </label>
          <label className="flex items-center gap-2 py-1.5 cursor-pointer" data-testid="cn-email-row">
            <span className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center shrink-0"><MailWarning className="h-4 w-4" /></span>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800">Invio ufficiale anche tramite email</p><p className="text-[11px] text-slate-400">Copia istituzionale all'email personale</p></div>
            <Checkbox checked={emailOn} onCheckedChange={(v) => setEmailOn(!!v)} data-testid="cn-email-toggle" />
          </label>
          {emailOn && count >= 1 && (
            <p className="text-[11px] text-slate-500 mt-1 pl-10" data-testid="cn-email-reach">
              {emailReachable} destinatar{emailReachable === 1 ? "io" : "i"} email{emailMissing > 0 ? ` · ${emailMissing} senza email associata` : ""}
            </p>
          )}
        </div>

        <div className="mt-3 flex items-center justify-end">
          <Button data-testid="cn-send-btn" disabled={!canSend} onClick={() => setConfirmOpen(true)}
            className="rounded-lg bg-[#5B4A7D] hover:bg-[#4a3c68] text-white">
            <Send className="h-4 w-4 mr-2" /> Invia comunicazione
          </Button>
        </div>
        {!canSend && (
          <p className="text-[11px] text-slate-400 mt-2 text-right">
            {msg.trim().length === 0 ? "Scrivi il testo della comunicazione." : count < 1 ? "Nessun destinatario nel perimetro corrente." : ""}
          </p>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="cn-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'invio?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                Stai per inviare questa comunicazione a <b>{count}</b> lavorator{count === 1 ? "e" : "i"}.
                <span className="block text-slate-500 mt-0.5">{perimeterMsg}</span>
                <span className="block mt-2 font-semibold text-slate-700">Canali</span>
                <span className="block text-slate-600">Orbit: {count}</span>
                <span className="block text-slate-600">Push: {pushOn ? "attiva" : "disattivata"}</span>
                {emailOn && <span className="block text-slate-600">Email: {emailReachable} destinatar{emailReachable === 1 ? "io" : "i"}{emailMissing > 0 ? ` (${emailMissing} senza email)` : ""}</span>}
                <span className="block mt-2 text-slate-400 text-xs">L'invio è definitivo. La comunicazione in Orbit viene consegnata sempre, indipendentemente da push ed email.</span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cn-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="cn-confirm-send" disabled={sending} onClick={(e) => { e.preventDefault(); doSend(); }}>
              {sending ? "Invio…" : "Invia"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SentCommDetail({ commId, onClose }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  useEffect(() => {
    if (!commId) { setD(null); return; }
    setD(null); setFilter("all"); setQ("");
    api.get(`/office/communications/${commId}/detail`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [commId]);

  const s = d?.summary;
  const recips = (d?.recipients || []).filter((r) => {
    if (filter === "pending" && r.status !== "pending") return false;
    if (filter === "read" && r.status !== "read") return false;
    if (filter === "failed" && r.status !== "failed") return false;
    if (q.trim() && !(r.label || "").toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });
  const badge = (st) => {
    if (st === "read") return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-200">Letta e archiviata</span>;
    if (st === "failed") return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 border border-rose-200">Non recapitata</span>;
    return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 border border-amber-200">In attesa di lettura</span>;
  };
  const chip = (key, label, count) => (
    <button type="button" data-testid={`sent-filter-${key}`} onClick={() => setFilter(key)}
      className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${filter === key ? "bg-[#22364D] border-[#22364D] text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {label}{typeof count === "number" ? ` (${count})` : ""}
    </button>
  );

  return (
    <Dialog open={!!commId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="sent-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Inbox className="h-5 w-5 text-primary" /> Comunicazione inviata</DialogTitle>
        </DialogHeader>
        {d === null && <p className="text-sm text-slate-400 py-4">Caricamento…</p>}
        {d === false && <p className="text-sm text-rose-500 py-4">Impossibile caricare il dettaglio.</p>}
        {d && (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-slate-500">{fmtDT(d.sent_at)}{d.perimeter_summary ? ` · ${d.perimeter_summary}` : ""}</p>
              <p className="text-sm text-slate-800 whitespace-pre-wrap mt-1 rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid="sent-detail-text">{d.text}</p>
            </div>
            <div className="rounded-xl bg-[#F5F3FA] border border-[#E1DCEF] px-3 py-2 text-sm font-semibold text-[#4a3c68]" data-testid="sent-detail-summary">
              {s.total} destinatar{s.total === 1 ? "io" : "i"} · {s.read} lett{s.read === 1 ? "a" : "e"} · {s.pending} in attesa{s.failed > 0 ? ` · ${s.failed} non recapitat${s.failed === 1 ? "a" : "e"}` : ""}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {chip("all", "Tutti", s.total)}
              {chip("pending", "In attesa", s.pending)}
              {chip("read", "Letti", s.read)}
              {(d.supports_failed || s.failed > 0) && chip("failed", "Non recapitati", s.failed)}
            </div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca lavoratore…" data-testid="sent-detail-search" className="rounded-lg" />
            <div className="space-y-1.5" data-testid="sent-detail-recipients">
              {recips.length === 0 && <p className="text-sm text-slate-400 py-2 text-center">Nessun destinatario per questo filtro.</p>}
              {recips.map((r) => (
                <div key={r.id} data-testid={`sent-recip-${r.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.label}</p>
                    {r.status === "read" && r.read_at && <p className="text-[11px] text-emerald-600">Letta il {fmtDT(r.read_at)}</p>}
                  </div>
                  {badge(r.status)}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ComunicazioniInviateSection() {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    api.get("/office/communications/sent").then((r) => setItems(r.data.communications || [])).catch(() => setItems([]));
  }, []);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  return (
    <div className="max-w-2xl" data-testid="comunicazioni-inviate">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#E1DEEE] to-[#CFC9E2] border border-[#BCB6D2] shadow-inner flex items-center justify-center shrink-0">
            <Inbox className="h-5 w-5 text-primary" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">Comunicazioni inviate</h2>
            <p className="text-sm text-slate-500 mt-1">Storico delle comunicazioni generali inviate dal tuo Account Ufficio. Tocca per vedere lo stato dei destinatari.</p>
          </div>
        </div>
        {items === null && <p className="mt-5 text-sm text-slate-400">Caricamento…</p>}
        {items !== null && items.length === 0 && (
          <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center" data-testid="comunicazioni-inviate-placeholder">
            <p className="text-sm font-semibold text-slate-600">Nessuna comunicazione ancora</p>
            <p className="text-xs text-slate-400 mt-1">Le comunicazioni che invii compariranno qui, dalla più recente.</p>
          </div>
        )}
        {items !== null && items.length > 0 && (
          <div className="mt-5 space-y-3" data-testid="comunicazioni-inviate-list">
            {items.map((c) => (
              <button key={c.id} data-testid={`sent-comm-${c.id}`} onClick={() => setOpenId(c.id)}
                className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:bg-slate-50 hover:shadow transition-all">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-slate-500">{fmt(c.sent_at)}</span>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-[#EFEAF6] text-[#5B4A7D] border border-[#D8CEEA]">{c.recipient_count} destinatar{c.recipient_count === 1 ? "io" : "i"}</span>
                </div>
                <p className="text-sm text-slate-800 line-clamp-2 whitespace-pre-wrap">{c.text}</p>
                {c.perimeter_summary && <p className="text-xs text-slate-400 mt-1">{c.perimeter_summary}</p>}
              </button>
            ))}
          </div>
        )}
      </div>
      <SentCommDetail commId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function fmtHrs(min) {
  if (!min) return "";
  return fmtNum(min / 60);
}

function RendicontazioneSection({ ctx }) {
  const nowRome = romeYM();
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState(ctx?.appaltoId || "");
  const [services, setServices] = useState([]);
  const [service, setService] = useState("");
  const [year, setYear] = useState(ctx?.year || nowRome.year);
  const [month, setMonth] = useState(ctx?.month || nowRome.month);
  const [view, setView] = useState("select"); // select | report
  const [report, setReport] = useState(null);
  const [activeCat, setActiveCat] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [insert, setInsert] = useState(null);
  const [pick, setPick] = useState(null);

  const refreshReport = async () => {
    const { data } = await api.get(`/office/rendicontazione/report?appalto_id=${appaltoId}&service=${service}&year=${report.year}&month=${report.month}`);
    setReport(data);
  };

  const openCell = async (u, ed, d, minutes) => {
    if (!ed.worker_id) return;
    const wd = `${report.year}-${String(report.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const isPre = report?.kind === "prepost";
    const isBlue = report?.kind === "bluebox";
    const isNidoK = report?.kind === "nido";
    const isCasaK = report?.kind === "casa_riposo";
    const isCEK = report?.kind === "centro_estivo";
    const isSCK = report?.kind === "spazio_compiti";
    if (!minutes) {
      if (isSCK) {
        setInsert({ sc: true, role: "Educatore", entity_id: u.site_id, site_id: u.site_id,
                    worker_id: ed.worker_id, work_date: wd, entity_label: `${u.site_name}`,
                    worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
      } else if (isCEK) {
        const isEdu = u.role === "Educatore";
        setInsert({ ce: true, role: u.role, entity_id: isEdu ? u.entity_id : u.site_id, ce_entity_id: u.entity_id,
                    worker_id: ed.worker_id, work_date: wd,
                    entity_label: isEdu ? `${u.entity_name} · ${u.role}` : `${report.appalto_nome} · ${u.role}`,
                    worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
      } else if (isCasaK) {
        setInsert({ casa_riposo: true, role: u.role, worker_id: ed.worker_id, work_date: wd,
                    entity_id: report.appalto_id, entity_label: `${report.appalto_nome} · ${u.role}`,
                    worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
      } else if (isNidoK) {
        const isEdu = u.role === "Educatore";
        setInsert({ nido: true, role: u.role, sala_id: u.sala_id || null, worker_id: ed.worker_id, work_date: wd,
                    entity_id: isEdu ? u.sala_id : report.appalto_id,
                    entity_label: isEdu ? `${report.appalto_nome} · ${u.sala_name}` : `${report.appalto_nome} · Ausiliari`,
                    worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
      } else if (isBlue) {
        setInsert({ bluebox: true, worker_id: ed.worker_id, work_date: wd, entity_id: report.appalto_id,
                    entity_label: report.appalto_nome, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
      } else if (isPre) {
        setInsert({ prepost: true, worker_id: ed.worker_id, work_date: wd, site_id: u.site_id,
                    service_slug: u.service_slug, role: u.insert_role, entity_label: `${u.user_name} · ${u.scuola}`,
                    worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
      } else {
        setInsert({ worker_id: ed.worker_id, work_date: wd, entity_id: u.user_id,
                    user_name: u.user_name, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
      }
      return;
    }
    try {
      const url = isSCK
        ? `/office/rendicontazione/spazio_compiti/cell?appalto_id=${appaltoId}&year=${report.year}&month=${report.month}&day=${d}&site_id=${u.site_id}&worker_id=${ed.worker_id}&service=${report.service || service}`
        : isCEK
        ? `/office/rendicontazione/casa_riposo/cell?appalto_id=${appaltoId}&year=${report.year}&month=${report.month}&day=${d}&worker_id=${ed.worker_id}&role=${encodeURIComponent(u.role)}`
        : isNidoK
        ? `/office/rendicontazione/nido/cell?appalto_id=${appaltoId}&year=${report.year}&month=${report.month}&day=${d}&worker_id=${ed.worker_id}&role=${encodeURIComponent(u.role)}${u.role === "Educatore" ? `&sala_id=${u.sala_id}` : ""}`
        : isBlue
        ? `/office/rendicontazione/bluebox/cell?appalto_id=${appaltoId}&year=${report.year}&month=${report.month}&day=${d}&worker_id=${ed.worker_id}`
        : isPre
        ? `/office/rendicontazione/prepost/cell?appalto_id=${appaltoId}&year=${report.year}&month=${report.month}&day=${d}&site_id=${u.site_id}&service_slug=${u.service_slug}&worker_id=${ed.worker_id}&role_group=${u.role_group}`
        : `/office/rendicontazione/cell?appalto_id=${appaltoId}&year=${report.year}&month=${report.month}&day=${d}&user_id=${u.user_id}&worker_id=${ed.worker_id}&service=${service}`;
      const { data } = await api.get(url);
      const list = data.entries || [];
      const uname = isSCK ? u.site_name : isCEK ? (u.role === "Educatore" ? `${u.entity_name}` : u.role) : isCasaK ? u.role : isNidoK ? (u.role === "Educatore" ? u.sala_name : "Ausiliari") : isBlue ? report.appalto_nome : isPre ? `${u.user_name} · ${u.scuola}` : u.user_name;
      const nidoInsert = () => {
        const isEdu = u.role === "Educatore";
        return { nido: true, role: u.role, sala_id: u.sala_id || null, worker_id: ed.worker_id, work_date: wd,
                 entity_id: isEdu ? u.sala_id : report.appalto_id,
                 entity_label: isEdu ? `${report.appalto_nome} · ${u.sala_name}` : `${report.appalto_nome} · Ausiliari`,
                 worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" };
      };
      if (list.length === 1) setEdit({ entry: list[0], start: list[0].start_time, end: list[0].end_time || "", reason: "", user_name: uname, worker_name: ed.worker_name, day: d });
      else if (list.length > 1) setPick({ list, user_name: uname, worker_name: ed.worker_name, day: d });
      else setInsert(isSCK
        ? { sc: true, role: "Educatore", entity_id: u.site_id, site_id: u.site_id, worker_id: ed.worker_id, work_date: wd, entity_label: `${u.site_name}`, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" }
        : isCEK
        ? { ce: true, role: u.role, entity_id: u.role === "Educatore" ? u.entity_id : u.site_id, ce_entity_id: u.entity_id, worker_id: ed.worker_id, work_date: wd, entity_label: u.role === "Educatore" ? `${u.entity_name} · ${u.role}` : `${report.appalto_nome} · ${u.role}`, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" }
        : isCasaK
        ? { casa_riposo: true, role: u.role, worker_id: ed.worker_id, work_date: wd, entity_id: report.appalto_id, entity_label: `${report.appalto_nome} · ${u.role}`, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" }
        : isNidoK
        ? nidoInsert()
        : isBlue
        ? { bluebox: true, worker_id: ed.worker_id, work_date: wd, entity_id: report.appalto_id, entity_label: report.appalto_nome, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" }
        : isPre
        ? { prepost: true, worker_id: ed.worker_id, work_date: wd, site_id: u.site_id, service_slug: u.service_slug, role: u.insert_role, entity_label: uname, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" }
        : { worker_id: ed.worker_id, work_date: wd, entity_id: u.user_id, user_name: u.user_name, worker_name: ed.worker_name, day: d, start: "", end: "", reason: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveCorrection = async () => {
    try {
      await api.post(`/work-entries/${edit.entry.id}/correct`, buildCorrectionPayload(edit));
      toast.success("Rettifica salvata · il lavoratore è stato notificato");
      setEdit(null);
      await refreshReport();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const deletePrestazione = async () => {
    try {
      await api.post(`/work-entries/${edit.entry.id}/cancel`, { from_export: true });
      toast.success("Prestazione eliminata · il lavoratore è stato notificato");
      setEdit(null);
      await refreshReport();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveInsert = async () => {
    if (!insert.start || !insert.end) { toast.error("Inserisci orario di inizio e fine"); return; }
    if (!insert.reason.trim()) { toast.error("La motivazione è obbligatoria"); return; }
    try {
      if (insert.sc) {
        await api.post(`/office/work-entries`, {
          worker_id: insert.worker_id, service_type: report?.service || "spazio_compiti", work_date: insert.work_date,
          start_time: insert.start, end_time: insert.end, entity_id: insert.entity_id,
          role_or_task: ["assistenza_scuolabus", "assistenza_mensa"].includes(report?.service) ? null : insert.role, reason: insert.reason.trim(), with_anomalies: true,
        });
      } else if (insert.ce) {
        await api.post(`/office/work-entries`, {
          worker_id: insert.worker_id, service_type: "centro_estivo", work_date: insert.work_date,
          start_time: insert.start, end_time: insert.end, entity_id: insert.entity_id,
          role_or_task: insert.role, reason: insert.reason.trim(), with_anomalies: true,
        });
      } else if (insert.casa_riposo) {
        await api.post(`/office/work-entries`, {
          worker_id: insert.worker_id, service_type: "casa_di_riposo", work_date: insert.work_date,
          start_time: insert.start, end_time: insert.end, entity_id: insert.entity_id,
          role_or_task: insert.role, reason: insert.reason.trim(), with_anomalies: true,
        });
      } else if (insert.nido) {
        await api.post(`/office/work-entries`, {
          worker_id: insert.worker_id, service_type: "nido", work_date: insert.work_date,
          start_time: insert.start, end_time: insert.end, entity_id: insert.entity_id,
          role_or_task: insert.role, reason: insert.reason.trim(), with_anomalies: true,
        });
      } else if (insert.bluebox) {
        await api.post(`/office/work-entries`, {
          worker_id: insert.worker_id, service_type: "blue_box", work_date: insert.work_date,
          start_time: insert.start, end_time: insert.end, entity_id: insert.entity_id,
          role_or_task: null, reason: insert.reason.trim(), with_anomalies: true,
        });
      } else if (insert.prepost) {
        await api.post(`/office/work-entries`, {
          worker_id: insert.worker_id, service_type: insert.service_slug, work_date: insert.work_date,
          start_time: insert.start, end_time: insert.end, entity_id: insert.site_id,
          role_or_task: insert.role, reason: insert.reason.trim(), with_anomalies: true,
        });
      } else {
        await api.post(`/office/work-entries`, {
          worker_id: insert.worker_id, service_type: service, work_date: insert.work_date,
          start_time: insert.start, end_time: insert.end, entity_id: insert.entity_id,
          role_or_task: null, reason: insert.reason.trim(), with_anomalies: true,
        });
      }
      toast.success("Prestazione inserita · generate 2 anomalie (apertura + chiusura) · lavoratore notificato");
      setInsert(null);
      await refreshReport();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { api.get("/my-appalti").then((r) => setAppalti(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setAppaltoId(ctx?.appaltoId || "");
    setYear(ctx?.year || nowRome.year);
    setMonth(ctx?.month || nowRome.month);
    setView("select");
    /* eslint-disable-next-line */
  }, [ctx?.appaltoId, ctx?.month, ctx?.year]);
  const loadSvcStatus = () => {
    if (!appaltoId) { setServices([]); setService(""); return; }
    api.get(`/office/rendicontazione/services-status?appalto_id=${appaltoId}&year=${year}&month=${month}`).then((r) => {
      const list = r.data?.services || [];
      setServices(list);
      setService((prev) => (list.some((s) => s.key === prev) ? prev : (list[0]?.key || "")));
    }).catch(() => { setServices([]); setService(""); });
  };
  useEffect(() => { loadSvcStatus(); /* eslint-disable-next-line */ }, [appaltoId, year, month]);
  useEffect(() => { if (view === "select" && appaltoId) loadSvcStatus(); /* eslint-disable-next-line */ }, [view]);

  const flagService = async (key) => {
    try {
      const { data } = await api.post(`/office/rendicontazione/check`, { appalto_id: appaltoId, year, month, service: key });
      setServices(data?.services || []);
      toast.success("Servizio marcato come controllato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
  };
  const unflagService = async (key) => {
    try {
      const { data } = await api.post(`/office/rendicontazione/uncheck`, { appalto_id: appaltoId, year, month, service: key });
      setServices(data?.services || []);
      toast.success("Check rimosso: controllo riaperto");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
  };

  const openReport = async () => {
    if (!appaltoId || !service) { toast.error("Seleziona appalto e servizio"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/office/rendicontazione/report?appalto_id=${appaltoId}&service=${service}&year=${year}&month=${month}`);
      setReport(data);
      setActiveCat(data.categories?.[0]?.key || "");
      setView("report");
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/office/rendicontazione/excel?appalto_id=${appaltoId}&service=${service}&year=${year}&month=${month}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `Rendicontazione_${year}_${month}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); } finally { setDownloading(false); }
  };

  if (view === "select") {
    const controlled = services.filter((s) => s.controlled);
    const toCheck = services.filter((s) => !s.controlled);
    return (
      <div data-testid="rendic-select" className="max-w-2xl">
        <p className="text-sm text-slate-600 mb-4">Seleziona il <b>servizio</b> da rendicontare (uno alla volta). Vengono mostrati solo i servizi con prestazioni nel periodo.</p>
        {!appaltoId ? (
          <p className="text-sm text-amber-600" data-testid="rendic-no-ctx">Seleziona prima Appalto e periodo nella Home Esportazione dati.</p>
        ) : services.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="rendic-no-services">Nessun servizio con prestazioni da rendicontare per il periodo selezionato.</p>
        ) : (
          <div className="space-y-6">
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Servizi disponibili</Label>
              <div className="flex flex-wrap gap-2" data-testid="rendic-services">
                {services.map((s) => {
                  const sel = service === s.key;
                  return (
                    <button key={s.key} type="button" data-testid={`rendic-service-chip-${s.key}`}
                      onClick={() => setService(s.key)}
                      className={`group inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-all ${sel ? "border-primary ring-2 ring-primary/30" : "border-slate-200 hover:border-primary/40"} ${s.controlled ? "bg-emerald-50" : "bg-white"}`}>
                      <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-white ${s.controlled ? "bg-emerald-500" : "bg-slate-300"}`}>
                        {s.controlled ? <Check className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </span>
                      <span className={`font-medium ${s.controlled ? "text-emerald-800" : "text-slate-700"}`}>{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="rendic-controlled-box">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Servizi controllati</p>
              {controlled.length === 0 ? (
                <p className="text-xs text-slate-400">Nessun servizio ancora controllato.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {controlled.map((s) => (
                    <span key={s.key} className="inline-flex items-center gap-2 rounded-lg bg-emerald-100 text-emerald-800 px-2.5 py-1 text-xs font-medium" data-testid={`rendic-controlled-${s.key}`}>
                      <Check className="h-3.5 w-3.5" /> {s.label}
                      {s.quadratura_ok ? <span className="text-emerald-600/70">· Quadratura OK</span> : <span className="text-emerald-600/70">· check manuale</span>}
                      {s.manual_checked && (
                        <button type="button" title="Rimuovi check (riapri controllo)" data-testid={`rendic-uncheck-${s.key}`} onClick={() => unflagService(s.key)} className="ml-1 rounded hover:bg-emerald-200/60 p-0.5"><X className="h-3.5 w-3.5" /></button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={openReport} disabled={loading || !service} data-testid="rendic-open" className="rounded-lg">
                {loading ? "Apertura…" : "Apri rendicontazione"}
              </Button>
              {service && !services.find((s) => s.key === service)?.manual_checked && (
                <Button variant="outline" onClick={() => flagService(service)} data-testid="rendic-flag" className="rounded-lg">
                  <Check className="h-4 w-4 mr-1" /> Segna come controllato
                </Button>
              )}
              {service && services.find((s) => s.key === service)?.manual_checked && (
                <Button variant="outline" onClick={() => unflagService(service)} data-testid="rendic-unflag" className="rounded-lg">
                  <X className="h-4 w-4 mr-1" /> Rimuovi check
                </Button>
              )}
            </div>
            {toCheck.length > 0 && <p className="text-[11px] text-slate-400">Servizi ancora da verificare: {toCheck.map((s) => s.label).join(", ")}</p>}
          </div>
        )}
      </div>
    );
  }

  // ---- report view ----
  const isPrepost = report?.kind === "prepost";
  const isBluebox = report?.kind === "bluebox";
  const isNido = report?.kind === "nido";
  const isCasaRiposo = report?.kind === "casa_riposo";
  const isCentroEstivo = report?.kind === "centro_estivo";
  const isSpazioCompiti = report?.kind === "spazio_compiti";
  const isUserSvc = report?.service === "adm" || report?.service === "rest";
  const reportTitle = isSpazioCompiti ? `Rendicontazione ${report.service_label || "Spazio Compiti"}` : isCentroEstivo ? "Rendicontazione Centro Estivo" : isCasaRiposo ? "Rendicontazione Casa di riposo" : isNido ? "Rendicontazione Nido" : isBluebox ? "Rendicontazione Blue Box" : isPrepost ? "Rendicontazione PRE/Post" : isUserSvc ? `Rendicontazione ${report.service_label}` : "Rendicontazione Assistenza Scolastica";
  const cats = report?.categories || [];
  const cat = cats.find((c) => c.key === activeCat) || cats[0];
  const days = report?.days || [];
  const isWeekend = (d) => { const w = new Date(report.year, report.month - 1, d).getDay(); return w === 0 || w === 6; };
  const isSat = (d) => new Date(report.year, report.month - 1, d).getDay() === 6;
  const isSun = (d) => new Date(report.year, report.month - 1, d).getDay() === 0;
  const wkCell = (d) => `${isWeekend(d) ? "bg-[#E7ECF1]" : ""} ${isSat(d) ? "border-l border-slate-300" : ""} ${isSun(d) ? "border-r border-slate-300" : ""}`;
  const pendingUserIds = new Set((cat?.signals || []).filter((s) => s.status === "pending" && s.user_id).map((s) => s.user_id));

  // PRE/Post: raggruppa i blocchi backend (Sede+Servizio+Ruolo) per Sede+Servizio -> operatori consecutivi.
  const prepostGroups = [];
  if (isPrepost && cat) {
    let curg = null;
    for (const b of cat.users) {
      const gk = `${b.site_id}|${b.service_slug}`;
      if (!curg || curg.gk !== gk) {
        curg = { gk, tipo: b.tipo, sede: b.user_name, rows: [], tot: 0, daySub: {}, pending: false };
        prepostGroups.push(curg);
      }
      for (const ed of b.educatori) curg.rows.push({ b, ed });
      curg.tot += b.tot_user_min || 0;
      if (pendingUserIds.has(b.user_id)) curg.pending = true;
    }
    for (const g of prepostGroups) {
      for (const d of days) {
        let s = 0;
        for (const { ed } of g.rows) s += ed.days[String(d)] || 0;
        if (s) g.daySub[String(d)] = s;
      }
    }
  }

  const validateSignal = async (s) => {
    try {
      const { data } = await api.post(`/office/rendicontazione/signal/validate`, {
        appalto_id: appaltoId, year: report.year, month: report.month, signal_key: s.key,
        type: s.type, user_id: s.user_id || null, date: s.date || null, message: s.message, service });
      setReport(data);
      toast.success("Segnalazione convalidata");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const correctSignal = (s) => {
    const list = s.work_entries || [];
    if (list.length === 0) { toast.error("Nessuna prestazione collegata"); return; }
    const mk = (we) => ({ entry: we, start: we.start_time, end: we.end_time || "", reason: "",
      user_name: s.user_name, worker_name: we.worker_name, day: parseInt(we.work_date.slice(8, 10)) });
    if (list.length === 1) setEdit(mk(list[0]));
    else setPick({ list, user_name: s.user_name, worker_name: list[0].worker_name, day: null, showDate: true });
  };

  return (
    <div data-testid="rendic-report">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <button onClick={() => setView("select")} data-testid="rendic-back" className="text-xs text-primary inline-flex items-center gap-1 mb-1"><ArrowLeft className="h-3.5 w-3.5" /> Cambia selezione</button>
          <h3 className="font-heading text-lg font-bold text-slate-900">{reportTitle}</h3>
          <p className="text-xs text-slate-500">{report.appalto_nome} · {MONTH_NAMES[report.month - 1]} {report.year}</p>
        </div>
        <Button variant="outline" onClick={downloadExcel} disabled={downloading} data-testid="rendic-download" className="rounded-lg">
          <Download className="h-4 w-4 mr-2" /> {downloading ? "Generazione…" : "Scarica Rendicontazione Excel"}
        </Button>
      </div>

      {isPrepost && report.summary && (
        <div className="mb-4" data-testid="rendic-prepost-summary">
          <div className="flex flex-wrap gap-2 mb-2">
            {[["Totale PRE", report.summary.tot_pre], ["Totale POST", report.summary.tot_post],
              ["Totale Educatore", report.summary.tot_edu], ["Totale Sorveglianza", report.summary.tot_sorv],
              ["Totale ore", report.summary.tot_min]].map(([lbl, v]) => (
              <div key={lbl} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">{lbl}</p>
                <p className="text-sm font-bold text-slate-900">{fmtHrs(v) || "0"}</p>
              </div>
            ))}
            <div className={`rounded-lg px-3 py-1.5 border ${report.summary.quadratura_ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Quadratura</p>
              <p className={`text-sm font-bold inline-flex items-center gap-1 ${report.summary.quadratura_ok ? "text-emerald-700" : "text-amber-700"}`}>
                {report.summary.quadratura_ok ? <><Check className="h-3.5 w-3.5" /> OK</> : <><AlertTriangle className="h-3.5 w-3.5" /> {report.summary.pending_count}</>}
              </p>
            </div>
          </div>
          {report.service_days_note && <p className="text-[11px] text-slate-400 italic">{report.service_days_note}</p>}
        </div>
      )}

      {isBluebox && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-3 text-sm">
            <span className="text-slate-600">Operatori: <b className="text-slate-900" data-testid="rendic-bb-n-operatori">{report.summary?.n_operatori || 0}</b></span>
            <span className="text-slate-600">Ore erogate: <b className="text-slate-900" data-testid="rendic-bb-total">{fmtHrs(report.total_min) || "0"}</b></span>
          </div>
          {(!report.operators || report.operators.length === 0) ? (
            <p className="text-sm text-slate-500" data-testid="rendic-bb-empty">Nessuna prestazione Blue Box registrata in questo mese.</p>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="rendic-bb-matrix">
              <table className="text-xs border-collapse">
                <thead>
                  <tr className="bg-[#31708A] text-white">
                    <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[160px] z-10">Operatore</th>
                    {days.map((d) => (
                      <th key={d} className={`px-1 py-2 w-8 ${isWeekend(d) ? "bg-[#2a6076]" : ""} ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{d}</th>
                    ))}
                    <th className="px-2 py-2 min-w-[80px]">Tot. operatore</th>
                  </tr>
                </thead>
                <tbody>
                  {report.operators.map((op, oi) => (
                    <tr key={op.worker_id} className={`${oi % 2 === 0 ? "bg-white" : "bg-[#EAF3F9]"} border-t border-slate-100`}>
                      <td className={`sticky left-0 px-2 py-1.5 font-semibold text-slate-900 ${oi % 2 === 0 ? "bg-white" : "bg-[#EAF3F9]"}`} data-testid={`rendic-bb-operator-${op.worker_id}`}>{op.worker_name}</td>
                      {days.map((d) => {
                        const mn = (op.days || {})[String(d)] || 0;
                        return (
                          <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                            <button type="button"
                              data-testid={`rendic-bb-cell-${op.worker_id}-${d}`}
                              onClick={() => openCell(null, { worker_id: op.worker_id, worker_name: op.worker_name }, d, mn)}
                              className="w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 hover:bg-primary/10 cursor-pointer">
                              {mn ? fmtHrs(mn) : <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" />}
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(op.tot_min) || "0"}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#31708A] text-white font-semibold">
                    <td className="sticky left-0 bg-[#31708A] px-2 py-2 text-right">Totale giorno</td>
                    {days.map((d) => (
                      <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((report.day_totals || {})[String(d)] || 0)}</td>
                    ))}
                    <td className="px-2 py-2 text-center bg-[#20506A]" data-testid="rendic-bb-grand-total">{fmtHrs(report.total_min) || "0"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {isNido && (() => {
        const nDays = report.days || [];
        const hasData = (report.sale || []).length > 0 || (report.ausiliari || []).length > 0;
        const dayTh = (extra) => nDays.map((d) => (
          <th key={d} className={`px-1 py-2 w-8 ${isWeekend(d) ? "bg-[#2a6076]" : ""} ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{d}</th>
        ));
        return (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-3 text-sm">
              <span className="text-slate-600">Sale: <b className="text-slate-900" data-testid="rendic-nido-n-sale">{report.summary?.n_sale || 0}</b></span>
              <span className="text-slate-600">Educatori: <b className="text-slate-900">{report.summary?.n_educatori || 0}</b></span>
              <span className="text-slate-600">Ausiliari: <b className="text-slate-900">{report.summary?.n_ausiliari || 0}</b></span>
              <span className="text-slate-600">Ore erogate: <b className="text-slate-900" data-testid="rendic-nido-total">{fmtHrs(report.total_min) || "0"}</b></span>
            </div>
            {!hasData ? (
              <p className="text-sm text-slate-500" data-testid="rendic-nido-empty">Nessuna prestazione Nido registrata in questo mese.</p>
            ) : (
              <>
                {/* Sezione Educatori */}
                <div className="overflow-x-auto border border-slate-200 rounded-lg mb-6" data-testid="rendic-nido-edu">
                  <div className="bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1.5">Educatori</div>
                  <table className="text-xs border-collapse">
                    <thead>
                      <tr className="bg-[#31708A] text-white">
                        <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[110px] z-10">Sala</th>
                        <th className="px-2 py-2 text-left min-w-[140px]">Educatore</th>
                        {dayTh()}
                        <th className="px-2 py-2 min-w-[80px]">Tot. educatore</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.sale || []).length === 0 && (
                        <tr><td colSpan={nDays.length + 3} className="px-3 py-2 text-slate-500">Nessun educatore con prestazioni nel mese.</td></tr>
                      )}
                      {(report.sale || []).map((sala, si) => {
                        const eds = (sala.educatori && sala.educatori.length) ? sala.educatori : [{ worker_id: null, worker_name: "—", days: {}, tot_min: 0 }];
                        const bg = si % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]";
                        return [
                          ...eds.map((ed, ei) => (
                            <tr key={sala.sala_id + "-" + (ed.worker_id || ei)} className={`${bg} ${ei === 0 ? "border-t-2 border-slate-300" : "border-t border-slate-100"}`}>
                              {ei === 0 && (
                                <td rowSpan={eds.length} className={`sticky left-0 px-2 py-1.5 font-semibold text-slate-900 align-top border-t-2 border-slate-300 ${bg}`} data-testid={`rendic-nido-sala-${sala.sala_id}`}>{sala.sala_name}</td>
                              )}
                              <td className="px-2 py-1.5 text-slate-700">{ed.worker_name}</td>
                              {nDays.map((d) => {
                                const mn = ed.days[String(d)] || 0;
                                const clickable = !!ed.worker_id;
                                return (
                                  <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                                    <button type="button" disabled={!clickable}
                                      data-testid={`rendic-nido-edu-cell-${sala.sala_id}-${ed.worker_id || "none"}-${d}`}
                                      onClick={() => openCell({ nido: true, role: "Educatore", sala_id: sala.sala_id, sala_name: sala.sala_name }, ed, d, mn)}
                                      className={`w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 ${clickable ? "hover:bg-primary/10 cursor-pointer" : "cursor-default"}`}>
                                      {mn ? fmtHrs(mn) : (clickable ? <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" /> : "")}
                                    </button>
                                  </td>
                                );
                              })}
                              <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(ed.tot_min) || "0"}</td>
                            </tr>
                          )),
                          <tr key={sala.sala_id + "-tot"} className="bg-[#20506A] text-white font-semibold" data-testid={`rendic-nido-sala-total-${sala.sala_id}`}>
                            <td className="sticky left-0 bg-[#20506A] px-2 py-1.5 text-right" colSpan={2}>Totale {sala.sala_name}</td>
                            {nDays.map((d) => (
                              <td key={d} className={`px-1 py-1.5 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{sala.day_totals[String(d)] ? fmtHrs(sala.day_totals[String(d)]) : ""}</td>
                            ))}
                            <td className="px-2 py-1.5 text-center">{fmtHrs(sala.tot_min) || "0"}</td>
                          </tr>,
                        ];
                      })}
                      <tr className="bg-[#31708A] text-white font-semibold">
                        <td className="sticky left-0 bg-[#31708A] px-2 py-2 text-right" colSpan={2}>Totale Educatori</td>
                        {nDays.map((d) => (
                          <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((report.edu_day_totals || {})[String(d)] || 0)}</td>
                        ))}
                        <td className="px-2 py-2 text-center bg-[#20506A]" data-testid="rendic-nido-edu-total">{fmtHrs(report.edu_total_min) || "0"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Sezione Ausiliari */}
                {(report.ausiliari || []).length > 0 && (
                  <div className="overflow-x-auto border border-slate-200 rounded-lg mb-6" data-testid="rendic-nido-aux">
                    <div className="bg-[#EAF3F9] text-slate-700 text-xs font-semibold px-3 py-1.5">Ausiliari</div>
                    <table className="text-xs border-collapse">
                      <thead>
                        <tr className="bg-[#31708A] text-white">
                          <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[250px] z-10">Ausiliario</th>
                          {dayTh()}
                          <th className="px-2 py-2 min-w-[80px]">Tot. ausiliario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.ausiliari.map((aux) => (
                          <tr key={aux.worker_id} className="bg-[#EAF3F9] border-t border-slate-100">
                            <td className="sticky left-0 bg-[#EAF3F9] px-2 py-1.5 font-semibold text-slate-900" data-testid={`rendic-nido-aux-${aux.worker_id}`}>{aux.worker_name}</td>
                            {nDays.map((d) => {
                              const mn = aux.days[String(d)] || 0;
                              return (
                                <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                                  <button type="button"
                                    data-testid={`rendic-nido-aux-cell-${aux.worker_id}-${d}`}
                                    onClick={() => openCell({ nido: true, role: "Ausiliario" }, aux, d, mn)}
                                    className="w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 hover:bg-primary/10 cursor-pointer">
                                    {mn ? fmtHrs(mn) : <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" />}
                                  </button>
                                </td>
                              );
                            })}
                            <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(aux.tot_min) || "0"}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#31708A] text-white font-semibold">
                          <td className="sticky left-0 bg-[#31708A] px-2 py-2 text-right">Totale Ausiliari</td>
                          {nDays.map((d) => (
                            <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((report.aux_day_totals || {})[String(d)] || 0)}</td>
                          ))}
                          <td className="px-2 py-2 text-center bg-[#20506A]" data-testid="rendic-nido-aux-total">{fmtHrs(report.aux_total_min) || "0"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totale giorno complessivo */}
                <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="rendic-nido-totals">
                  <table className="text-xs border-collapse">
                    <tbody>
                      <tr className="bg-[#20506A] text-white font-semibold">
                        <td className="sticky left-0 bg-[#20506A] px-2 py-2 text-right min-w-[250px]">Totale giorno (Nido)</td>
                        {nDays.map((d) => (
                          <td key={d} className={`px-1 py-2 w-8 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((report.day_totals || {})[String(d)] || 0)}</td>
                        ))}
                        <td className="px-2 py-2 text-center min-w-[80px]" data-testid="rendic-nido-grand-total">{fmtHrs(report.total_min) || "0"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale Educatori</p>
                    <p className="text-sm font-bold text-slate-900" data-testid="rendic-nido-summary-edu">{fmtHrs(report.edu_total_min) || "0"}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-[#EAF3F9] px-3 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale Ausiliari</p>
                    <p className="text-sm font-bold text-slate-900" data-testid="rendic-nido-summary-aux">{fmtHrs(report.aux_total_min) || "0"}</p>
                  </div>
                  <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale generale</p>
                    <p className="text-sm font-bold text-primary" data-testid="rendic-nido-summary-total">{fmtHrs(report.total_min) || "0"}</p>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}

      {isCasaRiposo && (() => {
        const nDays = report.days || [];
        const roles = report.roles || [];
        return (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-3 text-sm">
              <span className="text-slate-600">Ruoli: <b className="text-slate-900" data-testid="rendic-cr-n-ruoli">{report.summary?.n_ruoli || 0}</b></span>
              <span className="text-slate-600">Operatori: <b className="text-slate-900">{report.summary?.n_operatori || 0}</b></span>
              <span className="text-slate-600">Ore erogate: <b className="text-slate-900" data-testid="rendic-cr-total">{fmtHrs(report.total_min) || "0"}</b></span>
            </div>
            {roles.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="rendic-cr-empty">Nessuna prestazione Casa di riposo registrata in questo mese.</p>
            ) : (
              <>
                {roles.map((role, ri) => (
                  <div key={role.role} className="overflow-x-auto border border-slate-200 rounded-lg mb-6" data-testid={`rendic-cr-role-${ri}`}>
                    <div className="bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1.5 uppercase tracking-wide" data-testid={`rendic-cr-role-label-${ri}`}>{role.role}</div>
                    <table className="text-xs border-collapse">
                      <thead>
                        <tr className="bg-[#31708A] text-white">
                          <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[200px] z-10">Operatore</th>
                          {nDays.map((d) => (
                            <th key={d} className={`px-1 py-2 w-8 ${isWeekend(d) ? "bg-[#2a6076]" : ""} ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{d}</th>
                          ))}
                          <th className="px-2 py-2 min-w-[80px]">Tot. operatore</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(role.operators || []).map((op, oi) => (
                          <tr key={op.worker_id} className={`${oi % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]"} border-t border-slate-100`}>
                            <td className={`sticky left-0 px-2 py-1.5 font-semibold text-slate-900 ${oi % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]"}`}>{op.worker_name}</td>
                            {nDays.map((d) => {
                              const mn = op.days[String(d)] || 0;
                              return (
                                <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                                  <button type="button"
                                    data-testid={`rendic-cr-cell-${ri}-${op.worker_id}-${d}`}
                                    onClick={() => openCell({ role: role.role }, op, d, mn)}
                                    className="w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 hover:bg-primary/10 cursor-pointer">
                                    {mn ? fmtHrs(mn) : <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" />}
                                  </button>
                                </td>
                              );
                            })}
                            <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(op.tot_min) || "0"}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#31708A] text-white font-semibold">
                          <td className="sticky left-0 bg-[#31708A] px-2 py-2 text-right">Totale {role.role}</td>
                          {nDays.map((d) => (
                            <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((role.day_totals || {})[String(d)] || 0)}</td>
                          ))}
                          <td className="px-2 py-2 text-center bg-[#20506A]" data-testid={`rendic-cr-role-total-${ri}`}>{fmtHrs(role.tot_min) || "0"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="rendic-cr-totals">
                  <table className="text-xs border-collapse">
                    <tbody>
                      <tr className="bg-[#20506A] text-white font-semibold">
                        <td className="sticky left-0 bg-[#20506A] px-2 py-2 text-right min-w-[200px]">Totale giorno</td>
                        {nDays.map((d) => (
                          <td key={d} className={`px-1 py-2 w-8 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((report.day_totals || {})[String(d)] || 0)}</td>
                        ))}
                        <td className="px-2 py-2 text-center min-w-[80px]" data-testid="rendic-cr-grand-total">{fmtHrs(report.total_min) || "0"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {roles.map((role, ri) => (
                    <div key={role.role} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale {role.role}</p>
                      <p className="text-sm font-bold text-slate-900" data-testid={`rendic-cr-summary-${ri}`}>{fmtHrs(role.tot_min) || "0"}</p>
                    </div>
                  ))}
                  <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale generale</p>
                    <p className="text-sm font-bold text-primary" data-testid="rendic-cr-summary-total">{fmtHrs(report.total_min) || "0"}</p>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}

      {isCentroEstivo && (() => {
        const nDays = report.days || [];
        const sedi = report.sedi || [];
        const hasData = sedi.length > 0;
        const dayTh = () => nDays.map((d) => (
          <th key={d} className={`px-1 py-2 w-8 ${isWeekend(d) ? "bg-[#2a6076]" : ""} ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{d}</th>
        ));
        const tipBadge = (tip) => (
          <span className={`inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 ${tip === "gruppo" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{tip === "gruppo" ? "Gruppo" : "Utente"}</span>
        );
        const entTable = (sede, blocks, subTitle, dayTotals, subTotal, totLabel, kindKey) => (
          <div className="overflow-x-auto border border-slate-200 rounded-lg mb-4" data-testid={`rendic-ce-${kindKey}-${sede.site_id}`}>
            <div className={`text-xs font-semibold px-3 py-1.5 uppercase tracking-wide ${kindKey === "gruppi" ? "bg-violet-50 text-violet-800" : "bg-sky-50 text-sky-800"}`}>Educatori · {subTitle}</div>
            <table className="text-xs border-collapse">
              <thead>
                <tr className="bg-[#31708A] text-white">
                  <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[130px] z-10">Entità</th>
                  <th className="px-2 py-2 min-w-[70px]">Tipologia</th>
                  <th className="px-2 py-2 min-w-[90px]">Monte ore</th>
                  <th className="px-2 py-2 min-w-[70px]">Tot. entità</th>
                  <th className="px-2 py-2 text-left min-w-[140px]">Educatore</th>
                  {dayTh()}
                  <th className="px-2 py-2 min-w-[80px]">Tot. educatore</th>
                </tr>
              </thead>
              <tbody>
                {blocks.length === 0 && (
                  <tr><td colSpan={nDays.length + 6} className="px-3 py-2 text-slate-400 italic">Nessun {kindKey === "gruppi" ? "Gruppo" : "Utente"} in questa Sede.</td></tr>
                )}
                {blocks.map((en, ni) => {
                  const eds = (en.educatori && en.educatori.length) ? en.educatori : [{ worker_id: null, worker_name: "—", days: {}, tot_min: 0 }];
                  const bg = ni % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]";
                  const monteTxt = en.monte_ore == null ? "—" : `${en.monte_ore} ${en.monte_ore_kind === "giornaliero" ? "h/gg" : "h/sett"}`;
                  return [
                    ...eds.map((ed, ei) => (
                      <tr key={en.entity_id + "-" + (ed.worker_id || ei)} className={`${bg} ${ei === 0 ? "border-t-2 border-slate-300" : "border-t border-slate-100"}`}>
                        {ei === 0 && <td rowSpan={eds.length} className={`sticky left-0 px-2 py-1.5 font-semibold text-slate-900 align-top border-t-2 border-slate-300 ${bg}`} data-testid={`rendic-ce-entity-${en.entity_id}`}>{en.entity_name}</td>}
                        {ei === 0 && <td rowSpan={eds.length} className="px-2 py-1.5 text-center align-top">{tipBadge(en.tipologia)}</td>}
                        {ei === 0 && <td rowSpan={eds.length} className="px-2 py-1.5 text-center align-top text-slate-700" data-testid={`rendic-ce-monte-${en.entity_id}`}>{monteTxt}</td>}
                        {ei === 0 && <td rowSpan={eds.length} className="px-2 py-1.5 text-center align-top font-semibold text-slate-900">{fmtHrs(en.tot_min) || "0"}</td>}
                        <td className="px-2 py-1.5 text-slate-700">{ed.worker_name}</td>
                        {nDays.map((d) => {
                          const mn = ed.days[String(d)] || 0;
                          const clickable = !!ed.worker_id;
                          return (
                            <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                              <button type="button" disabled={!clickable}
                                data-testid={`rendic-ce-edu-cell-${en.entity_id}-${ed.worker_id || "none"}-${d}`}
                                onClick={() => openCell({ ce: true, role: "Educatore", entity_id: en.entity_id, entity_name: en.entity_name }, ed, d, mn)}
                                className={`w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 ${clickable ? "hover:bg-primary/10 cursor-pointer" : "cursor-default"}`}>
                                {mn ? fmtHrs(mn) : (clickable ? <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" /> : "")}
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(ed.tot_min) || "0"}</td>
                      </tr>
                    )),
                    <tr key={en.entity_id + "-tot"} className="bg-[#20506A] text-white font-semibold" data-testid={`rendic-ce-entity-total-${en.entity_id}`}>
                      <td className="sticky left-0 bg-[#20506A] px-2 py-1.5 text-right" colSpan={5}>Totale {en.entity_name}</td>
                      {nDays.map((d) => (
                        <td key={d} className={`px-1 py-1.5 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{en.day_totals[String(d)] ? fmtHrs(en.day_totals[String(d)]) : ""}</td>
                      ))}
                      <td className="px-2 py-1.5 text-center">{fmtHrs(en.tot_min) || "0"}</td>
                    </tr>,
                  ];
                })}
                <tr className="bg-[#31708A] text-white font-semibold">
                  <td className="sticky left-0 bg-[#31708A] px-2 py-2 text-right" colSpan={5}>{totLabel}</td>
                  {nDays.map((d) => (
                    <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((dayTotals || {})[String(d)] || 0)}</td>
                  ))}
                  <td className="px-2 py-2 text-center bg-[#20506A]" data-testid={`rendic-ce-${kindKey}-total-${sede.site_id}`}>{fmtHrs(subTotal) || "0"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
        return (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-3 text-sm">
              <span className="text-slate-600">Sedi: <b className="text-slate-900" data-testid="rendic-ce-n-sedi">{report.summary?.n_sedi || 0}</b></span>
              <span className="text-slate-600">Educatori: <b className="text-slate-900" data-testid="rendic-ce-n-edu">{report.summary?.n_educatori || 0}</b></span>
              <span className="text-slate-600">Ore erogate: <b className="text-slate-900" data-testid="rendic-ce-total">{fmtHrs(report.total_min) || "0"}</b></span>
              <span data-testid="rendic-ce-quad" className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${report.quadratura_ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {report.quadratura_ok ? <><Check className="h-3.5 w-3.5" /> Quadratura OK</> : <><AlertTriangle className="h-3.5 w-3.5" /> Verifica rendicontazione · {report.pending_count} segnalazion{report.pending_count === 1 ? "e" : "i"}</>}
              </span>
            </div>
            {report.service_days_note && <p className="text-[11px] text-slate-400 italic mb-3">{report.service_days_note}</p>}

            {!hasData ? (
              <p className="text-sm text-slate-500" data-testid="rendic-ce-empty">Nessuna prestazione Centro Estivo registrata in questo mese.</p>
            ) : (
              <>
                {sedi.map((sede) => {
                  const sPending = (sede.signals || []).filter((s) => s.status === "pending");
                  return (
                    <div key={sede.site_id} className="mb-8 rounded-2xl border border-slate-200 overflow-hidden" data-testid={`rendic-ce-sede-${sede.site_id}`}>
                      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-900 text-white">
                        <h4 className="font-heading text-sm font-bold" data-testid={`rendic-ce-sede-name-${sede.site_id}`}>Sede {sede.site_name}</h4>
                        <div className="flex items-center gap-3 text-xs">
                          <span>Ore Sede: <b>{fmtHrs(sede.tot_min) || "0"}</b></span>
                          <span className={`inline-flex items-center gap-1 font-semibold rounded-full px-2 py-0.5 ${sede.quadratura_ok ? "bg-emerald-500/20 text-emerald-200" : "bg-amber-500/20 text-amber-200"}`}>
                            {sede.quadratura_ok ? <><Check className="h-3 w-3" /> OK</> : <><AlertTriangle className="h-3 w-3" /> {sede.pending_count}</>}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        {sPending.length > 0 && (
                          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden" data-testid={`rendic-ce-signals-${sede.site_id}`}>
                            <div className="bg-amber-100/70 text-amber-800 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Segnalazioni da verificare</div>
                            <div className="divide-y divide-amber-100">
                              {sPending.map((s) => (
                                <div key={s.key} data-testid={`rendic-ce-signal-${s.type}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                                  <div className="min-w-0">
                                    {s.user_name && <p className="text-sm font-semibold text-slate-800">{s.user_name}</p>}
                                    <p className="text-xs text-slate-600">{s.message}</p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {s.work_entries && s.work_entries.length > 0 && (
                                      <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`rendic-ce-signal-correct-${s.type}`} onClick={() => correctSignal(s)}><Pencil className="h-3.5 w-3.5 mr-1" /> Correggi</Button>
                                    )}
                                    <Button size="sm" className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`rendic-ce-signal-validate-${s.type}`} onClick={() => validateSignal(s)}><Check className="h-3.5 w-3.5 mr-1" /> Convalida</Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {entTable(sede, sede.gruppi || [], "Gruppi", sede.gruppi_day_totals, sede.gruppi_total_min, "Totale Gruppi", "gruppi")}
                        {entTable(sede, sede.utenti || [], "Utenti", sede.utenti_day_totals, sede.utenti_total_min, "Totale Utenti", "utenti")}

                        <div className="overflow-x-auto border border-slate-200 rounded-lg mb-6">
                          <table className="text-xs border-collapse">
                            <tbody>
                              <tr className="bg-[#20506A] text-white font-semibold">
                                <td className="sticky left-0 bg-[#20506A] px-2 py-2 text-right min-w-[220px]">Totale Educatori (Sede)</td>
                                {nDays.map((d) => (
                                  <td key={d} className={`px-1 py-2 w-8 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((sede.edu_day_totals || {})[String(d)] || 0)}</td>
                                ))}
                                <td className="px-2 py-2 text-center min-w-[80px]" data-testid={`rendic-ce-edu-total-${sede.site_id}`}>{fmtHrs(sede.edu_total_min) || "0"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {(sede.roles || []).map((role) => (
                          <div key={role.role} className="overflow-x-auto border border-slate-200 rounded-lg mb-4" data-testid={`rendic-ce-role-${sede.site_id}-${role.role}`}>
                            <div className="bg-[#EAF3F9] text-slate-700 text-xs font-semibold px-3 py-1.5 uppercase tracking-wide">{role.role}</div>
                            <table className="text-xs border-collapse">
                              <thead>
                                <tr className="bg-[#31708A] text-white">
                                  <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[220px] z-10">Operatore</th>
                                  {dayTh()}
                                  <th className="px-2 py-2 min-w-[80px]">Tot. operatore</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(role.operators || []).map((op, oi) => (
                                  <tr key={op.worker_id} className={`${oi % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]"} border-t border-slate-100`}>
                                    <td className={`sticky left-0 px-2 py-1.5 font-semibold text-slate-900 ${oi % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]"}`}>{op.worker_name}</td>
                                    {nDays.map((d) => {
                                      const mn = op.days[String(d)] || 0;
                                      return (
                                        <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                                          <button type="button"
                                            data-testid={`rendic-ce-role-cell-${role.role}-${op.worker_id}-${d}`}
                                            onClick={() => openCell({ ce: true, role: role.role, site_id: op.site_id }, op, d, mn)}
                                            className="w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 hover:bg-primary/10 cursor-pointer">
                                            {mn ? fmtHrs(mn) : <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" />}
                                          </button>
                                        </td>
                                      );
                                    })}
                                    <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(op.tot_min) || "0"}</td>
                                  </tr>
                                ))}
                                <tr className="bg-[#31708A] text-white font-semibold">
                                  <td className="sticky left-0 bg-[#31708A] px-2 py-2 text-right">Totale {role.role}</td>
                                  {nDays.map((d) => (
                                    <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs((role.day_totals || {})[String(d)] || 0)}</td>
                                  ))}
                                  <td className="px-2 py-2 text-center bg-[#20506A]" data-testid={`rendic-ce-role-total-${sede.site_id}-${role.role}`}>{fmtHrs(role.tot_min) || "0"}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ))}

                        <div className="overflow-x-auto border border-primary/30 rounded-lg">
                          <table className="text-xs border-collapse">
                            <tbody>
                              <tr className="bg-primary text-white font-bold">
                                <td className="sticky left-0 bg-primary px-2 py-2 text-right min-w-[220px]">Totale Sede {sede.site_name}</td>
                                {nDays.map((d) => (
                                  <td key={d} className={`px-1 py-2 w-8 text-center ${isSat(d) ? "border-l border-white/30" : ""} ${isSun(d) ? "border-r border-white/30" : ""}`}>{fmtHrs((sede.day_totals || {})[String(d)] || 0)}</td>
                                ))}
                                <td className="px-2 py-2 text-center min-w-[80px]" data-testid={`rendic-ce-sede-total-${sede.site_id}`}>{fmtHrs(sede.tot_min) || "0"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  {sedi.map((sede) => (
                    <div key={sede.site_id} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale {sede.site_name}</p>
                      <p className="text-sm font-bold text-slate-900" data-testid={`rendic-ce-summary-sede-${sede.site_id}`}>{fmtHrs(sede.tot_min) || "0"}</p>
                    </div>
                  ))}
                  <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale generale Centro Estivo</p>
                    <p className="text-sm font-bold text-primary" data-testid="rendic-ce-summary-total">{fmtHrs(report.total_min) || "0"}</p>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}

      {isSpazioCompiti && (() => {
        const nDays = report.days || [];
        const sedi = report.sedi || [];
        const hasData = sedi.length > 0;
        const dayTh = () => nDays.map((d) => (
          <th key={d} className={`px-1 py-2 w-8 ${isWeekend(d) ? "bg-[#2a6076]" : ""} ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{d}</th>
        ));
        return (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-3 text-sm">
              <span className="text-slate-600">Sedi: <b className="text-slate-900" data-testid="rendic-sc-n-sedi">{report.summary?.n_sedi || 0}</b></span>
              <span className="text-slate-600">Operatori: <b className="text-slate-900">{report.summary?.n_operatori || 0}</b></span>
              <span className="text-slate-600">Ore erogate: <b className="text-slate-900" data-testid="rendic-sc-total">{fmtHrs(report.total_min) || "0"}</b></span>
              <span data-testid="rendic-sc-quad" className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${report.quadratura_ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {report.quadratura_ok ? <><Check className="h-3.5 w-3.5" /> Quadratura OK</> : <><AlertTriangle className="h-3.5 w-3.5" /> Verifica rendicontazione · {report.pending_count} segnalazion{report.pending_count === 1 ? "e" : "i"}</>}
              </span>
            </div>

            {!hasData ? (
              <p className="text-sm text-slate-500" data-testid="rendic-sc-empty">Nessuna prestazione {report.service_label || "Spazio Compiti"} registrata in questo mese.</p>
            ) : (
              <>
                {sedi.map((sede) => {
                  const sPending = (sede.signals || []).filter((s) => s.status === "pending");
                  const monteTxt = sede.monte_ore == null ? "—" : `${sede.monte_ore} h/sett`;
                  return (
                    <div key={sede.site_id} className="mb-8 rounded-2xl border border-slate-200 overflow-hidden" data-testid={`rendic-sc-sede-${sede.site_id}`}>
                      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-900 text-white">
                        <h4 className="font-heading text-sm font-bold" data-testid={`rendic-sc-sede-name-${sede.site_id}`}>Sede {sede.site_name}</h4>
                        <div className="flex items-center gap-3 text-xs">
                          <span>Monte ore: <b>{monteTxt}</b></span>
                          <span>Ore Sede: <b>{fmtHrs(sede.tot_min) || "0"}</b></span>
                          <span className={`inline-flex items-center gap-1 font-semibold rounded-full px-2 py-0.5 ${sede.quadratura_ok ? "bg-emerald-500/20 text-emerald-200" : "bg-amber-500/20 text-amber-200"}`}>
                            {sede.quadratura_ok ? <><Check className="h-3 w-3" /> OK</> : <><AlertTriangle className="h-3 w-3" /> {sede.pending_count}</>}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        {sPending.length > 0 && (
                          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden" data-testid={`rendic-sc-signals-${sede.site_id}`}>
                            <div className="bg-amber-100/70 text-amber-800 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Segnalazioni da verificare</div>
                            <div className="divide-y divide-amber-100">
                              {sPending.map((s) => (
                                <div key={s.key} data-testid={`rendic-sc-signal-${s.type}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                                  <div className="min-w-0">
                                    {s.user_name && <p className="text-sm font-semibold text-slate-800">{s.user_name}</p>}
                                    <p className="text-xs text-slate-600">{s.message}</p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {s.work_entries && s.work_entries.length > 0 && (
                                      <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`rendic-sc-signal-correct-${s.type}`} onClick={() => correctSignal(s)}><Pencil className="h-3.5 w-3.5 mr-1" /> Correggi</Button>
                                    )}
                                    <Button size="sm" className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`rendic-sc-signal-validate-${s.type}`} onClick={() => validateSignal(s)}><Check className="h-3.5 w-3.5 mr-1" /> Convalida</Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="overflow-x-auto border border-slate-200 rounded-lg">
                          <table className="text-xs border-collapse">
                            <thead>
                              <tr className="bg-[#31708A] text-white">
                                <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[220px] z-10">Operatore</th>
                                {dayTh()}
                                <th className="px-2 py-2 min-w-[80px]">Tot. operatore</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(sede.operators || []).map((op, oi) => (
                                <tr key={op.worker_id} className={`${oi % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]"} border-t border-slate-100`}>
                                  <td className={`sticky left-0 px-2 py-1.5 font-semibold text-slate-900 ${oi % 2 === 0 ? "bg-white" : "bg-[#F7FAFB]"}`}>{op.worker_name}</td>
                                  {nDays.map((d) => {
                                    const mn = op.days[String(d)] || 0;
                                    return (
                                      <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                                        <button type="button"
                                          data-testid={`rendic-sc-cell-${sede.site_id}-${op.worker_id}-${d}`}
                                          onClick={() => openCell({ sc: true, site_id: sede.site_id, site_name: sede.site_name }, op, d, mn)}
                                          className="w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 hover:bg-primary/10 cursor-pointer">
                                          {mn ? fmtHrs(mn) : <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" />}
                                        </button>
                                      </td>
                                    );
                                  })}
                                  <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(op.tot_min) || "0"}</td>
                                </tr>
                              ))}
                              <tr className="bg-primary text-white font-bold">
                                <td className="sticky left-0 bg-primary px-2 py-2 text-right">Totale Sede {sede.site_name}</td>
                                {nDays.map((d) => (
                                  <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-white/30" : ""} ${isSun(d) ? "border-r border-white/30" : ""}`}>{fmtHrs((sede.day_totals || {})[String(d)] || 0)}</td>
                                ))}
                                <td className="px-2 py-2 text-center" data-testid={`rendic-sc-sede-total-${sede.site_id}`}>{fmtHrs(sede.tot_min) || "0"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  {sedi.map((sede) => (
                    <div key={sede.site_id} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale {sede.site_name}</p>
                      <p className="text-sm font-bold text-slate-900" data-testid={`rendic-sc-summary-sede-${sede.site_id}`}>{fmtHrs(sede.tot_min) || "0"}</p>
                    </div>
                  ))}
                  <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale generale {report.service_label || "Spazio Compiti"}</p>
                    <p className="text-sm font-bold text-primary" data-testid="rendic-sc-summary-total">{fmtHrs(report.total_min) || "0"}</p>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}



      {!isBluebox && !isNido && !isCasaRiposo && !isCentroEstivo && !isSpazioCompiti && (cats.length === 0 ? (
        <p className="text-sm text-slate-500">Nessun utente configurato per questo appalto/servizio.</p>
      ) : (
        <>
          {!isPrepost && !isUserSvc && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="rendic-cat-nav">
            {cats.map((c) => (
              <button key={c.key} type="button" data-testid={`rendic-cat-${c.key}`}
                onClick={() => setActiveCat(c.key)}
                className={`text-xs rounded-full px-3.5 py-1.5 border transition-colors ${activeCat === c.key ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300 hover:border-primary/50"}`}>
                {c.label}
              </button>
            ))}
          </div>
          )}

          {cat && (
            <>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-3 text-sm">
                <span className="text-slate-600">{isPrepost ? "Sedi" : "Utenti"}: <b className="text-slate-900" data-testid="rendic-n-users">{isPrepost ? cat.n_sedi : cat.n_users}</b></span>
                <span className="text-slate-600">{isPrepost ? "Operatori" : "Educatori"}: <b className="text-slate-900">{cat.n_educatori}</b></span>
                <span className="text-slate-600">Ore erogate: <b className="text-slate-900" data-testid="rendic-total">{fmtHrs(cat.total_min) || "0"}</b></span>
                <span data-testid="rendic-quad" className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${cat.quadratura_ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {cat.quadratura_ok ? <><Check className="h-3.5 w-3.5" /> Quadratura OK</> : <><AlertTriangle className="h-3.5 w-3.5" /> Verifica rendicontazione · {cat.pending_count} segnalazion{cat.pending_count === 1 ? "e" : "i"}</>}
                </span>
              </div>

              {cat.signals.filter((s) => s.status === "pending").length > 0 && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden" data-testid="rendic-signals">
                  <div className="bg-amber-100/70 text-amber-800 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Segnalazioni da verificare
                  </div>
                  <div className="divide-y divide-amber-100">
                    {cat.signals.filter((s) => s.status === "pending").map((s) => (
                      <div key={s.key} data-testid={`rendic-signal-${s.type}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <div className="min-w-0">
                          {s.user_name && <p className="text-sm font-semibold text-slate-800">{s.user_name}</p>}
                          <p className="text-xs text-slate-600">{s.message}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {s.work_entries && s.work_entries.length > 0 && (
                            <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`rendic-signal-correct-${s.type}`} onClick={() => correctSignal(s)}>
                              <Pencil className="h-3.5 w-3.5 mr-1" /> Correggi
                            </Button>
                          )}
                          <Button size="sm" className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`rendic-signal-validate-${s.type}`} onClick={() => validateSignal(s)}>
                            <Check className="h-3.5 w-3.5 mr-1" /> Convalida
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cat.institutes && (
                <div className="mb-4 rounded-lg border border-slate-200 overflow-hidden max-w-md" data-testid="rendic-institutes">
                  <div className="bg-[#31708A] text-white text-xs font-semibold px-3 py-1.5">Riepilogo Plesso Istituto</div>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-slate-200 bg-slate-50 text-[11px] text-slate-500 uppercase">
                        <td className="px-3 py-1">Plesso Istituto</td><td className="px-3 py-1 text-right">Ore erogate</td>
                      </tr>
                      {cat.institutes.map((i) => (
                        <tr key={i.label} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 text-slate-700">{i.label}</td>
                          <td className="px-3 py-1.5 text-right font-medium text-slate-900">{fmtHrs(i.tot_min) || "0"}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-slate-200 bg-slate-50">
                        <td className="px-3 py-1.5 font-bold text-slate-900 text-right">Totale Comunale</td>
                        <td className="px-3 py-1.5 text-right font-bold text-slate-900">{fmtHrs(cat.total_min) || "0"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="rendic-matrix">
                <table className="text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#31708A] text-white">
                      <th className="sticky left-0 bg-[#31708A] px-2 py-2 text-left min-w-[90px] z-10">{isPrepost ? "Servizio" : "Utente"}</th>
                      <th className={`px-2 py-2 ${isPrepost ? "text-left min-w-[140px]" : "min-w-[64px]"}`}>{isPrepost ? "Sede operativa" : "Monte ore"}</th>
                      <th className={`px-2 py-2 ${isPrepost ? "text-left min-w-[110px]" : "min-w-[70px]"}`}>{isPrepost ? "Ruolo" : "Tot. utente"}</th>
                      {!isUserSvc && (
                        <th className={`px-2 py-2 ${isPrepost ? "min-w-[64px]" : "text-left min-w-[130px]"}`}>{isPrepost ? "Monte ore/gg" : "Istituto / Scuola"}</th>
                      )}
                      <th className="px-2 py-2 text-left min-w-[130px]">{isPrepost ? "Operatore" : "Educatore"}</th>
                      {days.map((d) => (
                        <th key={d} className={`px-1 py-2 w-8 ${isWeekend(d) ? "bg-[#2a6076]" : ""} ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{d}</th>
                      ))}
                      <th className="px-2 py-2 min-w-[70px]">{isPrepost ? "Tot. operatore" : "Tot. educatore"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isPrepost && prepostGroups.map((g, gi) => {
                      const n = g.rows.length;
                      return g.rows.map(({ b, ed }, ri) => {
                        const rowBg = b.role_group === "sorveglianza" ? "bg-[#EAF3F9]" : "bg-white";
                        return (
                          <tr key={g.gk + "-" + (ed.worker_id || ri)} className={`${rowBg} ${ri === 0 && gi > 0 ? "border-t-2 border-slate-300" : "border-t border-slate-100"}`}>
                            {ri === 0 && (
                              <td rowSpan={n} className={`sticky left-0 px-2 py-1.5 font-bold text-slate-900 align-top ${gi > 0 ? "border-t-2 border-slate-300" : ""} ${rowBg}`} data-testid={`rendic-pp-servizio-${g.gk}`}>{g.tipo}</td>
                            )}
                            {ri === 0 && (
                              <td rowSpan={n} className={`px-2 py-1.5 font-semibold text-slate-900 align-top ${gi > 0 ? "border-t-2 border-slate-300" : ""}`}>
                                <span className="inline-flex items-center gap-1">{g.pending && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" title="Segnalazione da verificare" />}{g.sede}</span>
                              </td>
                            )}
                            <td className="px-2 py-1.5 text-slate-700">{b.ruolo}</td>
                            <td className="px-2 py-1.5 text-center text-slate-700">{fmtNum(b.monte_ore)}</td>
                            <td className="px-2 py-1.5 text-slate-700">{ed.worker_name}</td>
                            {days.map((d) => {
                              const mn = ed.days[String(d)] || 0;
                              const clickable = !!ed.worker_id;
                              return (
                                <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                                  <button type="button" disabled={!clickable}
                                    data-testid={`rendic-cell-${b.user_id}-${ed.worker_id || "none"}-${d}`}
                                    onClick={() => openCell(b, ed, d, mn)}
                                    className={`w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 ${clickable ? "hover:bg-primary/10 cursor-pointer" : "cursor-default"}`}>
                                    {mn ? fmtHrs(mn) : (clickable ? <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" /> : "")}
                                  </button>
                                </td>
                              );
                            })}
                            <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(ed.tot_min) || "0"}</td>
                          </tr>
                        );
                      }).concat([
                        <tr key={g.gk + "-tot"} className="bg-[#20506A] text-white font-semibold" data-testid={`rendic-pp-subtotal-${g.gk}`}>
                          <td className="sticky left-0 bg-[#20506A] px-2 py-1.5 text-right" colSpan={5}>Totale {g.tipo} · {g.sede}</td>
                          {days.map((d) => (
                            <td key={d} className={`px-1 py-1.5 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{g.daySub[String(d)] ? fmtHrs(g.daySub[String(d)]) : ""}</td>
                          ))}
                          <td className="px-2 py-1.5 text-center">{fmtHrs(g.tot) || "0"}</td>
                        </tr>
                      ]);
                    })}
                    {!isPrepost && cat.users.map((u, ui) => (
                      u.educatori.map((ed, ei) => (
                        <tr key={u.user_id + "-" + (ed.worker_id || ei)}
                          className={`${ui % 2 === 0 ? "bg-white" : "bg-[#EAF3F9]"} ${ei === 0 ? "border-t-2 border-slate-300" : "border-t border-slate-100"}`}>
                          {ei === 0 && (
                            <td rowSpan={u.educatori.length} className={`sticky left-0 px-2 py-1.5 font-semibold text-slate-900 align-top border-t-2 border-slate-300 ${ui % 2 === 0 ? "bg-white" : "bg-[#EAF3F9]"}`}>
                              <span className="inline-flex items-center gap-1">{pendingUserIds.has(u.user_id) && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" title="Segnalazione da verificare" />}{u.user_name}</span>
                            </td>
                          )}
                          {ei === 0 && (
                            <td rowSpan={u.educatori.length} className="px-2 py-1.5 text-center text-slate-700 align-top">{fmtNum(u.monte_ore)}</td>
                          )}
                          {ei === 0 && (
                            <td rowSpan={u.educatori.length} className="px-2 py-1.5 text-center font-semibold text-slate-900 align-top">{fmtHrs(u.tot_user_min) || "0"}</td>
                          )}
                          {ei === 0 && !isUserSvc && (
                            <td rowSpan={u.educatori.length} className="px-2 py-1.5 text-slate-700 align-top">{u.scuola}{u.plesso ? ` · ${u.plesso}` : ""}</td>
                          )}
                          <td className="px-2 py-1.5 text-slate-700">{ed.worker_name}</td>
                          {days.map((d) => {
                            const mn = ed.days[String(d)] || 0;
                            const clickable = !!ed.worker_id;
                            return (
                              <td key={d} className={`p-0 text-center ${wkCell(d)}`}>
                                <button type="button" disabled={!clickable}
                                  data-testid={`rendic-cell-${u.user_id}-${ed.worker_id || "none"}-${d}`}
                                  onClick={() => openCell(u, ed, d, mn)}
                                  className={`w-full h-full px-1 py-1.5 flex items-center justify-center text-slate-700 ${clickable ? "hover:bg-primary/10 cursor-pointer" : "cursor-default"}`}>
                                  {mn ? fmtHrs(mn) : (clickable ? <span className="inline-block w-0.5 h-0.5 rounded-full bg-slate-300/70" /> : "")}
                                </button>
                              </td>
                            );
                          })}
                          <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{fmtHrs(ed.tot_min) || "0"}</td>
                        </tr>
                      ))
                    ))}
                    <tr className="bg-[#31708A] text-white font-semibold">
                      <td className="sticky left-0 bg-[#31708A] px-2 py-2 text-right" colSpan={isUserSvc ? 4 : 5}>Totale giorno</td>
                      {days.map((d) => (
                        <td key={d} className={`px-1 py-2 text-center ${isSat(d) ? "border-l border-[#4a8098]" : ""} ${isSun(d) ? "border-r border-[#4a8098]" : ""}`}>{fmtHrs(cat.day_totals[String(d)] || 0)}</td>
                      ))}
                      <td className="px-2 py-2 text-center bg-[#20506A]" data-testid="rendic-cat-total">{fmtHrs(cat.total_min) || "0"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ))}

      {/* Rettifica (riusa POST /work-entries/{id}/correct del Consuntivo) */}
      <Dialog open={edit != null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="rendic-edit-dialog">
          <DialogHeader><DialogTitle>Rettifica prestazione</DialogTitle>
            <DialogDescription>{edit?.user_name} · {edit?.worker_name} · giorno {edit?.day}</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1"><Label className="text-xs text-slate-500">Inizio</Label><TimePicker value={edit.start} onChange={(v) => setEdit({ ...edit, start: v })} /></div>
                <div className="flex-1"><Label className="text-xs text-slate-500">Fine</Label><TimePicker value={edit.end} onChange={(v) => setEdit({ ...edit, end: v })} /></div>
              </div>
              <EntryRoleSiteCorrection entry={edit.entry} corr={edit.corr} onChange={(c) => setEdit((e) => ({ ...e, corr: c }))} />
              <div><Label className="text-xs text-slate-500">Motivazione (facoltativa)</Label>
                <Textarea data-testid="rendic-edit-reason" value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} rows={2} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={deletePrestazione} data-testid="rendic-edit-delete"
              className="text-destructive border-destructive/30 hover:bg-destructive/5 mr-auto">Elimina prestazione</Button>
            <Button variant="ghost" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveCorrection} disabled={correctionIncomplete(edit)} data-testid="rendic-edit-save">Salva rettifica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inserimento (riusa POST /office/work-entries with_anomalies del Consuntivo) */}
      <Dialog open={insert != null} onOpenChange={(o) => !o && setInsert(null)}>
        <DialogContent data-testid="rendic-insert-dialog">
          <DialogHeader><DialogTitle>Inserisci prestazione</DialogTitle>
            <DialogDescription>{insert?.user_name || insert?.entity_label} · {insert?.worker_name} · giorno {insert?.day}{insert?.casa_riposo ? ` · Casa di riposo${insert?.role ? " · " + insert.role : ""}` : insert?.nido ? ` · Nido${insert?.role ? " · " + insert.role : ""}` : insert?.ce ? ` · Centro Estivo${insert?.role ? " · " + insert.role : ""}` : insert?.sc ? ` · Spazio Compiti${insert?.role ? " · " + insert.role : ""}` : insert?.bluebox ? " · Blue Box" : insert?.prepost ? " · PRE/Post" : ` · ${report?.service_label || "Assistenza scolastica"}`}</DialogDescription>
          </DialogHeader>
          {insert && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Verranno generate 2 anomalie indipendenti (apertura + chiusura) secondo il normale workflow Ufficio; nessuna timbratura reale.</p>
              <div className="flex gap-3">
                <div className="flex-1"><Label className="text-xs text-slate-500">Inizio</Label><TimePicker value={insert.start} onChange={(v) => setInsert({ ...insert, start: v })} /></div>
                <div className="flex-1"><Label className="text-xs text-slate-500">Fine</Label><TimePicker value={insert.end} onChange={(v) => setInsert({ ...insert, end: v })} /></div>
              </div>
              <div><Label className="text-xs text-slate-500">Motivazione</Label>
                <Textarea data-testid="rendic-insert-reason" value={insert.reason} onChange={(e) => setInsert({ ...insert, reason: e.target.value })} rows={2} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInsert(null)}>Annulla</Button>
            <Button onClick={saveInsert} data-testid="rendic-insert-save">Inserisci</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selettore prestazione (più work_entries nella stessa cella) */}
      <Dialog open={pick != null} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent data-testid="rendic-pick-dialog">
          <DialogHeader><DialogTitle>Scegli la prestazione da rettificare</DialogTitle>
            <DialogDescription>{pick?.user_name} · {pick?.worker_name} · giorno {pick?.day}</DialogDescription>
          </DialogHeader>
          {pick && (
            <div className="rounded-xl border border-slate-200 divide-y">
              {pick.list.map((e) => (
                <button key={e.id} type="button" data-testid={`rendic-pick-${e.id}`}
                  onClick={() => { setEdit({ entry: e, start: e.start_time, end: e.end_time || "", reason: "", user_name: pick.user_name, worker_name: pick.worker_name, day: parseInt((e.work_date || "").slice(8, 10)) || pick.day }); setPick(null); }}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between">
                  <span className="text-sm text-slate-700">{pick.showDate && e.work_date ? `${e.work_date.slice(8, 10)}/${e.work_date.slice(5, 7)} · ` : ""}{e.start_time} – {e.end_time || "—"}</span>
                  <span className="text-xs text-slate-400">{fmtHrs(e.duration_minutes) || "0"} h</span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
